<template>
  <div class="medical-dashboard container-fluid py-3">
    <!-- Dedicated Doctor Dashboard View -->
    <DoctorDashboard v-if="isDoctor"></DoctorDashboard>

    <!-- Admin / Hospital Management View -->
    <div v-else class="dashboard-body">
      <UsersTable v-if="activeTab === 'add-user' || activeTab === 'users'"></UsersTable>
      <RoleTable v-else-if="activeTab === 'roles'"></RoleTable>
      <Tokens v-else-if="activeTab === 'tokens'"></Tokens>
      <Counter v-else-if="activeTab === 'counter'"></Counter>
      <Services v-else-if="activeTab === 'services' || activeTab === 'services-prefix'"></Services>

      <div v-else>
        <!-- Standard Page Header (for Admin & SuperAdmin) -->
        <div class="md-card md-header p-3 mb-3 d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
          <div>
            <h4 class="mb-1 font-weight-bold text-dark">{{ resolvedHospitalName }}</h4>
            <p class="text-sm text-secondary mb-0">
              Queue control and waiting-hall screens · {{ todayLabel }}
            </p>
          </div>

          <div class="d-flex flex-wrap align-items-center gap-2">
            <button
              type="button"
              class="btn btn-outline-teal btn-sm font-weight-bold mb-0 text-nowrap d-flex align-items-center gap-1"
              @click="loadDoctorSession(false)"
              :disabled="isFetchingSession"
              title="Refresh dashboard"
            >
              <i class="fas fa-sync-alt" :class="{ 'fa-spin': isFetchingSession }"></i>
              <span>Refresh</span>
            </button>

            <!-- For Receptionist: Add Patient (Token) button -->
            <button
              v-if="isReceptionist"
              type="button"
              class="btn btn-teal btn-sm text-white font-weight-bold mb-0 text-nowrap d-flex align-items-center gap-1 shadow-sm"
              @click="openCreateTokenModal"
              title="Add a new patient token"
            >
              <i class="fas fa-user-plus me-1"></i> Add Patient (Token)
            </button>

            <!-- For Non-Receptionist (Admin/SuperAdmin): Add doctor button -->
            <button
              v-else
              type="button"
              class="btn btn-teal btn-sm text-white font-weight-bold mb-0 text-nowrap"
              @click="navigateToAddDoctor"
            >
              <i class="fas fa-plus me-1"></i> Add doctor
            </button>

            <button
              type="button"
              class="btn btn-sm mb-0 text-nowrap font-weight-bold d-flex align-items-center gap-2 emergency-btn"
              :class="emergencyTokens.length ? 'btn-danger text-white' : 'btn-outline-danger'"
              @click="scrollToEmergency"
              title="Go to emergency broadcast"
            >
              <i class="fas fa-ambulance"></i>
              <span>Emergency</span>
              <span class="badge bg-white text-danger">{{ emergencyTokens.length }}</span>
            </button>
          </div>
        </div>

        <!-- Active emergency broadcast banner -->
        <div v-if="activeEmergency" class="alert alert-danger shadow-sm border-0 d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 text-white p-3 rounded-4 animate-pulse">
          <div class="d-flex align-items-center gap-3">
            <i class="fas fa-exclamation-triangle fa-2x text-white"></i>
            <div>
              <h6 class="mb-0 font-weight-bold text-white">Emergency broadcast is live: {{ activeEmergency.title }}</h6>
              <small class="text-white-75">{{ activeEmergency.description }}</small>
            </div>
          </div>
          <button class="btn btn-light btn-sm text-danger font-weight-bold mb-0" @click="dismissEmergency">
            Stop broadcast
          </button>
        </div>

        <!-- ==================== ADMIN DASHBOARD ==================== -->
          <!-- 100% Full Width KPI Cards Row across Top -->
          <div class="row g-3 mb-3">
            <div v-for="kpi in kpiCards" :key="kpi.key" class="col-12 col-sm-6 col-xl-3">
              <div class="md-card kpi-card h-100 text-center p-3">
                <div class="kpi-icon mx-auto mb-2" :class="kpi.tone">
                  <i :class="kpi.icon"></i>
                </div>
                <p class="kpi-label mb-1">{{ kpi.label }}</p>
                <h3 class="kpi-value mb-1">
                  {{ kpi.value }}<span v-if="kpi.total !== undefined" class="kpi-total"> / {{ kpi.total }}</span>
                </h3>
                <small class="kpi-sub" :class="kpi.subClass">{{ kpi.sub }}</small>
              </div>
            </div>
          </div>

          <!-- ASSIGNED DOCTORS BY DEPARTMENT SECTION (Full Width Bar Chart matching mockup) -->
          <div class="card border-0 shadow-sm rounded-4 mb-4 bg-white p-4">
            <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
              <div class="d-flex align-items-center gap-2">
                <span class="text-xs font-weight-bold text-dark text-uppercase tracking-wider d-flex align-items-center">
                  <i class="fas fa-clinic-medical text-teal me-2"></i>ASSIGNED DOCTORS BY DEPARTMENT
                </span>
                <span v-if="selectedDepartmentName" class="badge bg-teal-soft text-teal border ms-2 text-xs">
                  Active: <strong>{{ selectedDepartmentName }}</strong>
                  <button type="button" class="btn-close btn-close-xs ms-1 p-0 align-middle" @click="selectedDepartmentName = null" title="Show all"></button>
                </span>
              </div>

              <div class="d-flex align-items-center gap-2">
                <span class="badge bg-white text-secondary border text-xxs font-weight-bold px-2 py-1 text-uppercase">
                  SCALE: 0 - {{ departmentBarData[0]?.maxRecords || 0 }} DOCS
                </span>
                <button
                  v-if="departmentSummaryList.length > 0"
                  type="button"
                  class="btn btn-outline-teal btn-xs mb-0 rounded-3 px-2 py-1 font-weight-bold"
                  @click="showAllDepartments = !showAllDepartments"
                >
                  {{ showAllDepartments ? 'Hide Cards' : 'Department Cards' }}
                </button>
              </div>
            </div>

            <!-- Bar Chart Display matching user design (Full Width, Left-aligned scale & Y-Axis) -->
            <div class="dept-bar-chart-card p-0" style="background: #ffffff; border-radius: 16px; position: relative;">
              <div class="dept-bars-wrapper position-relative" style="height: 165px; position: relative; overflow: hidden; padding: 10px 20px 0 50px;">
                <!-- Left Y-Axis Gridlines with labels on the left -->
                <div class="dept-bar-gridline top-line" style="position: absolute; left: 50px; right: 20px; top: 24px; border-top: 1px solid #e2e8f0; pointer-events: none; z-index: 0;">
                  <span class="gridline-label" style="position: absolute; left: -42px; top: -8px; width: 34px; text-align: right; font-size: 11px; font-weight: 600; color: #94a3b8;">{{ departmentBarData[0]?.maxRecords || 0 }}</span>
                </div>
                <div class="dept-bar-gridline mid-line" style="position: absolute; left: 50px; right: 20px; top: 68px; border-top: 1px dashed #e2e8f0; pointer-events: none; z-index: 0;">
                  <span class="gridline-label" style="position: absolute; left: -42px; top: -8px; width: 34px; text-align: right; font-size: 11px; font-weight: 600; color: #94a3b8;">{{ Math.round((departmentBarData[0]?.maxRecords || 0) / 2) }}</span>
                </div>
                <div class="dept-bar-gridline base-line" style="position: absolute; left: 50px; right: 20px; bottom: 32px; border-top: 1px solid #cbd5e1; pointer-events: none; z-index: 0;">
                  <span class="gridline-label" style="position: absolute; left: -42px; top: -8px; width: 34px; text-align: right; font-size: 11px; font-weight: 600; color: #94a3b8;">0</span>
                </div>

                <!-- Bars Row Centered in Card -->
                <div class="d-flex align-items-end justify-content-center h-100 position-relative z-index-1" style="display: flex; align-items: flex-end; justify-content: center; height: 100%; position: relative; z-index: 1; padding-bottom: 32px;">
                  <div
                    v-for="bar in departmentBarData"
                    :key="bar.name"
                    class="dept-bar-col"
                    :class="{ 'dept-bar-col-active': selectedDepartmentName === bar.name }"
                    @click="toggleDepartment(bar.name)"
                    :title="`${bar.name}: ${bar.records} doctors (${bar.waitingCount} waiting)`"
                    style="min-width: 80px; max-width: 140px; margin: 0 24px; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; cursor: pointer;"
                  >
                    <!-- Value above the bar -->
                    <span class="dept-bar-value mb-1 font-weight-bold" :style="{ color: bar.color || '#0d9488', fontSize: '12px', lineHeight: '1', whiteSpace: 'nowrap', marginBottom: '6px' }">
                      {{ bar.records }} docs
                    </span>

                    <!-- Scaled bar track (Guaranteed 80px height, never collapses!) -->
                    <div class="dept-bar-track" style="width: 52px; height: 80px; background-color: #f1f5f9; border-radius: 8px 8px 3px 3px; display: flex; align-items: flex-end; overflow: hidden; position: relative; flex-shrink: 0; box-shadow: inset 0 1px 2px rgba(0,0,0,0.04);">
                      <div
                        class="dept-bar-fill"
                        :style="{
                          width: '100%',
                          height: (bar.records > 0 ? Math.max(14, bar.heightPct) : 4) + '%',
                          backgroundColor: bar.color || '#0d9488',
                          borderRadius: '8px 8px 0 0',
                          transition: 'height 0.4s ease, background-color 0.2s ease',
                          display: 'block'
                        }"
                      ></div>
                    </div>

                    <!-- Department label below bar -->
                    <span class="dept-bar-name mt-2" :title="bar.name" style="font-size: 12px; max-width: 120px; text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; line-height: 1.3; color: #0d9488; font-weight: 600; margin-top: 6px; display: block;">
                      {{ bar.name }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="text-center mt-2 mb-1">
                <small class="text-xs text-muted d-block">
                  Click bar to view doctors belonging to that department
                </small>
              </div>
            </div>

            <!-- Optional Collapsible Department Summary Cards (Shown when 'Department Cards' is toggled) -->
            <div v-if="showAllDepartments" class="mt-4 pt-3 border-top">
              <div class="row g-3">
                <div
                  v-for="(dept, i) in visibleDepartmentsList"
                  :key="dept.name"
                  :class="visibleDepartmentsList.length === 1 ? 'col-12 col-md-8 col-lg-6' : 'col-12 col-md-6'"
                >
                  <div
                    class="department-card h-100 cursor-pointer shadow-xs transition-all"
                    :class="{ 'department-card-active border-teal bg-teal-soft-25 shadow-sm': selectedDepartmentName === dept.name }"
                    @click="toggleDepartment(dept.name)"
                  >
                    <div class="department-accent" :style="{ backgroundColor: departmentPiePalette[i % departmentPiePalette.length] }"></div>
                    <div class="ps-2 flex-grow-1">
                      <div class="d-flex align-items-center justify-content-between mb-1">
                        <h6 class="mb-0 font-weight-bold text-dark text-truncate">{{ dept.name }}</h6>
                        <span class="badge bg-light text-dark border text-xxs font-weight-bold px-2 py-0">
                          {{ dept.totalDoctors !== undefined ? dept.totalDoctors : (dept.doctors ? dept.doctors.size : 0) }} docs
                        </span>
                      </div>
                      <div class="text-xs text-muted mb-2 text-truncate">{{ dept.dutySubtitle }}</div>

                      <div class="dept-progress-track mb-2">
                        <div
                          class="dept-progress-bar"
                          :class="'bar-' + dept.accent"
                          :style="{ width: Math.min(100, Math.max(8, dept.percent)) + '%' }"
                        ></div>
                      </div>

                      <div class="d-flex align-items-center justify-content-between text-xs">
                        <span :class="dept.isNoDoctor ? 'text-danger font-weight-bold' : 'text-dark font-weight-bold'">
                          <i class="fas fa-ticket-alt text-teal me-1"></i>{{ dept.waitingCount }} waiting
                        </span>
                        <span :class="dept.isNoDoctor ? 'text-danger' : 'text-muted'" class="text-xxs">
                          <i class="far fa-clock me-1"></i>{{ dept.waitTime }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="visibleDepartmentsList.length === 0" class="col-12 text-center py-4 text-muted">
                  <i class="fas fa-clinic-medical fa-2x mb-2 text-secondary opacity-4"></i>
                  <p class="mb-0 text-sm">No departments registered yet.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Two-column grid below (8 / 4) -->
          <div class="row g-3">

            <!-- ============ LEFT COLUMN ============ -->
            <div :class="(isReceptionist && tokenBoardFullWidth) ? 'col-12 min-w-0' : 'col-12 col-xl-8 min-w-0'" style="min-width: 0; max-width: 100%;">

              <!-- Doctors in Department Table (shown ONLY when a department is clicked, styled matching mockup) -->
              <div v-if="selectedDepartmentName" class="card border-0 shadow-sm rounded-4 mb-4 bg-white p-4">
                <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4">
                  <div class="d-flex align-items-center gap-2">
                    <h5 class="mb-0 font-weight-bold text-dark">Doctors</h5>
                    <span class="badge bg-teal-soft text-teal text-xs font-weight-bold px-2 py-1">
                      {{ selectedDepartmentName }}
                    </span>
                  </div>

                  <div class="d-flex align-items-center gap-2">
                    <!-- Status Filter Pills matching screenshot -->
                    <div class="doctor-filter-pills d-flex align-items-center">
                      <button
                        v-for="opt in adminStatusFilterOptions"
                        :key="opt.key"
                        type="button"
                        class="btn-doctor-pill"
                        :class="{ active: adminDoctorStatusFilter === opt.key }"
                        @click="adminDoctorStatusFilter = opt.key"
                      >
                        {{ opt.label }}
                      </button>
                    </div>

                    <!-- Close button -->
                    <button
                      type="button"
                      class="btn btn-outline-secondary btn-sm mb-0 rounded-circle d-flex align-items-center justify-content-center p-0"
                      @click="selectedDepartmentName = null"
                      title="Close"
                      style="width: 28px; height: 28px;"
                    >
                      <i class="fas fa-times text-xs"></i>
                    </button>
                  </div>
                </div>

                <div class="table-responsive">
                  <table class="table md-table align-items-center mb-0">
                    <thead>
                      <tr>
                        <th class="text-secondary text-xs font-weight-bold ps-2 border-top-0">Doctor</th>
                        <th class="text-secondary text-xs font-weight-bold border-top-0">Department</th>
                        <th class="text-secondary text-xs font-weight-bold border-top-0">Counter</th>
                        <th class="text-secondary text-xs font-weight-bold border-top-0">Token Number</th>
                        <th class="text-secondary text-xs font-weight-bold border-top-0">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="item in filteredDepartmentDoctors"
                        :key="item.id"
                        @click="selectDoctorForTokenFlow(item)"
                        class="cursor-pointer doctor-row-hover"
                        :class="{ 'table-active-row': String(tokenFlowDoctorId) === String(item.id || item.user_id) }"
                        title="Click to view hourly token flow for this doctor"
                      >
                        <td class="ps-2 py-3 border-bottom-subtle">
                          <span class="font-weight-bold text-dark text-sm">{{ item.doctor_name || item.name }}</span>
                        </td>
                        <td class="py-3 border-bottom-subtle">
                          <span class="text-sm text-secondary">{{ item.speciality || item.department || selectedDepartmentName }}</span>
                        </td>
                        <td class="py-3 border-bottom-subtle">
                          <span
                            v-if="!getDoctorCounterName(item) || getDoctorCounterName(item) === '--'"
                            class="badge-no-room"
                          >
                            No counter
                          </span>
                          <span v-else class="text-sm text-dark font-weight-500">
                            {{ getDoctorCounterName(item) }}
                          </span>
                        </td>
                        <td class="py-3 border-bottom-subtle">
                          <span
                            v-if="getDoctorToken(item) && getDoctorToken(item) !== '--'"
                            class="token-chip font-weight-bold"
                          >
                            {{ getDoctorToken(item) }}
                          </span>
                          <span v-else class="text-muted text-sm font-weight-bold">--</span>
                        </td>
                        <td class="py-3 border-bottom-subtle">
                          <span 
                            v-if="item.is_on_break || String(item.status || item.doctor_status).toLowerCase().includes('break')" 
                            class="badge bg-warning-soft text-warning text-xxs font-weight-bold"
                          >
                            <i class="fas fa-coffee me-1"></i> On Break
                            <span v-if="item.break_start && item.break_end">
                              ({{ item.break_start }} - {{ item.break_end }})
                            </span>
                          </span>
                          <span v-else class="badge-status-pill" :class="getAdminDoctorStatusBadgeClass(item.status)">
                            {{ item.status || 'Available' }}
                          </span>
                          <small 
                            v-if="item.break_end && (item.is_on_break || String(item.status || item.doctor_status).toLowerCase().includes('break'))" 
                            class="text-muted d-block font-size-11 mt-1"
                          >
                            <i class="far fa-clock text-warning me-1"></i> Resumes at {{ item.break_end }}
                          </small>
                        </td>
                      </tr>
                      <tr v-if="filteredDepartmentDoctors.length === 0">
                        <td colspan="5" class="text-center py-4 text-muted">
                          <i class="fas fa-user-md fa-2x mb-2 text-secondary opacity-4"></i>
                          <p class="text-sm mb-0">No doctors found for {{ selectedDepartmentName }}<span v-if="adminDoctorStatusFilter !== 'all'"> with status "{{ adminDoctorStatusFilter }}"</span>.</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Interactive counter drilldown / Patient Tokens List -->
              <div v-if="!isDoctor || isAdminOrSuperAdmin || isReceptionist" class="md-card p-3 mb-3" style="max-width: 100%; overflow: hidden;">
                <div class="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <h6 class="mb-0 font-weight-bold text-dark">
                      <i class="fas fa-ticket-alt text-teal me-2"></i>{{ isReceptionist ? 'Patient Tokens List' : 'Counter Activity Drilldown' }}
                    </h6>
                    <small class="text-muted text-xs">
                      {{ isReceptionist ? 'Today\'s registered and active patient tokens' : (drilldownSelectedCounter ? `Drilldown for ${drilldownSelectedCounter.name}` : 'Click any counter below or on the chart to inspect today’s tokens') }}
                    </small>
                  </div>
                  <div class="d-flex align-items-center gap-2">
                    <button
                      v-if="drilldownSelectedCounter"
                      class="btn btn-outline-teal btn-sm mb-0 px-2 py-1 font-weight-bold"
                      @click="resetDrilldown"
                    >
                      <i class="fas fa-arrow-left me-1"></i>All counters
                    </button>
                    <button
                      class="btn btn-link text-secondary p-0 mb-0"
                      @click="isDrilldownCollapsed = !isDrilldownCollapsed"
                      :title="isDrilldownCollapsed ? 'Expand' : 'Collapse'"
                    >
                      <i :class="isDrilldownCollapsed ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"></i>
                    </button>
                  </div>
                </div>

                <div v-show="!isDrilldownCollapsed">
                  <!-- Header Controls: Counter Filter & Search -->
                  <div class="row g-2 align-items-center mb-3 pb-2 border-bottom">
                    <div :class="isReceptionist ? 'col-12 col-md-5' : 'col-12 col-md-7'" class="d-flex align-items-center gap-2">
                      <span class="text-xs fw-bold text-secondary text-uppercase text-nowrap">Filter Counter:</span>
                      <select
                        class="form-select form-select-sm"
                        :value="drilldownSelectedCounter ? drilldownSelectedCounter.id : ''"
                        @change="(e) => {
                          const val = e.target.value;
                          if (!val) resetDrilldown();
                          else {
                            const found = counters.find(c => String(c.id) === String(val));
                            if (found) selectDrilldown(found);
                          }
                        }"
                      >
                        <option value="">All Counters ({{ counters.length }})</option>
                        <option v-for="c in counters" :key="c.id" :value="c.id">
                          {{ c.name }} ({{ (drilldownCounterTokenMap.get(String(c.id)) || []).length }} tokens)
                        </option>
                      </select>
                    </div>

                    <!-- Search Patient/Token (Visible ONLY for Receptionist) -->
                    <div v-if="isReceptionist" class="col-12 col-md-4">
                      <div class="input-group input-group-sm">
                        <span class="input-group-text bg-white"><i class="fas fa-search text-xs text-secondary"></i></span>
                        <input
                          v-model="drilldownSearchQuery"
                          type="text"
                          class="form-control"
                          placeholder="Search patient, token, phone..."
                          @input="drilldownCurrentPage = 1"
                        />
                        <button
                          v-if="drilldownSearchQuery"
                          class="btn btn-outline-secondary mb-0"
                          type="button"
                          @click="drilldownSearchQuery = ''; drilldownCurrentPage = 1;"
                        >
                          <i class="fas fa-times"></i>
                        </button>
                      </div>
                    </div>

                    <div :class="isReceptionist ? 'col-12 col-md-3' : 'col-12 col-md-5'" class="text-md-end d-flex align-items-center justify-content-md-end gap-1 flex-wrap">
                      <span class="badge bg-teal-soft text-teal font-weight-bold text-xxs">
                        <i class="fas fa-ticket-alt me-1"></i>{{ drilldownFilteredTokens.length }} Tokens
                      </span>
                      <span class="badge bg-light text-dark font-weight-bold text-xxs border">
                        <i class="fas fa-male text-primary me-1"></i>{{ drilldownGenderStats.male }}M / <i class="fas fa-female text-danger me-1"></i>{{ drilldownGenderStats.female }}F
                      </span>
                    </div>
                  </div>

                  <!-- Main Content: Diagram for Superadmin/Tenant Admin OR Token List for Receptionist -->
                  <div class="row g-3 align-items-start">
                    <!-- Left Column: Counter / Status Donut Chart (Visible ONLY for Superadmin / Tenant Admin / Non-Receptionist) -->
                    <div v-if="!isReceptionist" class="col-12">
                      <!-- Selected counter donut -->
                      <div v-if="drilldownSelectedCounter" class="p-3 rounded-3 bg-light border">
                        <div class="row g-3 align-items-center">
                          <div class="col-12 col-md-4 text-center">
                            <div class="donut-chart-container position-relative mx-auto mb-2" style="max-width: 160px;">
                              <svg class="donut-svg" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="38" fill="transparent" stroke="#f1f5f9" stroke-width="14"></circle>
                                <circle
                                  v-for="(slice, i) in drilldownStatusSlices"
                                  :key="i"
                                  class="donut-segment"
                                  cx="50"
                                  cy="50"
                                  r="38"
                                  fill="transparent"
                                  :stroke="slice.color"
                                  stroke-width="14"
                                  :stroke-dasharray="`${slice.dashLength} ${100 - slice.dashLength}`"
                                  :stroke-dashoffset="slice.dashOffset"
                                ></circle>
                              </svg>
                              <div class="donut-center-label text-center">
                                <small class="text-secondary text-xxs d-block text-truncate px-1" style="max-width: 100px;">{{ drilldownSelectedCounter.name }}</small>
                                <h3 class="font-weight-bolder mb-0 text-dark">{{ drilldownCounterTokens.length }}</h3>
                                <small class="text-secondary text-xxs">tokens</small>
                              </div>
                            </div>

                            <button
                              class="btn btn-outline-teal btn-xs py-1 px-3 font-weight-bold"
                              @click="resetDrilldown"
                            >
                              <i class="fas fa-arrow-left me-1"></i> View All Counters
                            </button>
                          </div>

                          <div class="col-12 col-md-8">
                            <h6 class="text-xs font-weight-bolder text-uppercase text-secondary mb-3">
                              Status Breakdown for {{ drilldownSelectedCounter.name }}
                            </h6>
                            <div class="row g-2">
                              <div v-for="legend in drilldownStatusLegend" :key="legend.key" class="col-6 col-sm-3">
                                <div class="p-2 rounded-3 bg-white border text-center shadow-xs">
                                  <div class="d-flex align-items-center justify-content-center gap-1 mb-1">
                                    <span class="legend-color-dot" :class="legend.dot"></span>
                                    <span class="text-xxs font-weight-bold text-secondary">{{ legend.label }}</span>
                                  </div>
                                  <h4 class="font-weight-bolder mb-0 text-dark">{{ drilldownStatusCounts[legend.key] }}</h4>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- All counters overview donut -->
                      <div v-else class="p-3 rounded-3 bg-light border">
                        <div class="row g-3 align-items-center">
                          <div class="col-12 col-md-4 text-center border-end-md">
                            <div class="donut-chart-container position-relative mx-auto mb-2" style="max-width: 160px;">
                              <svg class="donut-svg" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="38" fill="transparent" stroke="#f1f5f9" stroke-width="14"></circle>
                                <circle
                                  v-for="(slice, i) in drilldownCounterSlices"
                                  :key="i"
                                  class="donut-segment cursor-pointer"
                                  cx="50"
                                  cy="50"
                                  r="38"
                                  fill="transparent"
                                  :stroke="slice.color"
                                  stroke-width="14"
                                  :stroke-dasharray="`${slice.dashLength} ${100 - slice.dashLength}`"
                                  :stroke-dashoffset="slice.dashOffset"
                                  @click="slice.counter ? selectDrilldown(slice.counter) : null"
                                ></circle>
                              </svg>
                              <div class="donut-center-label text-center">
                                <small class="text-secondary text-xxs d-block">Active</small>
                                <h3 class="font-weight-bolder mb-0 text-dark">{{ counters.length }}</h3>
                                <small class="text-secondary text-xxs">counters</small>
                              </div>
                            </div>
                            <small class="text-muted text-xxs d-block">Click any segment or counter on right to inspect</small>
                          </div>

                          <div class="col-12 col-md-8">
                            <h6 class="text-xs font-weight-bolder text-uppercase text-secondary mb-2">
                              Counters Token Distribution
                            </h6>
                            <div class="row g-2" style="max-height: 250px; overflow-y: auto;">
                              <div
                                v-for="item in drilldownLegendItems"
                                :key="item.counter.id"
                                class="col-12 col-sm-6"
                              >
                                <div
                                  class="d-flex align-items-center justify-content-between p-2 rounded-2 bg-white border hover-bg-light cursor-pointer shadow-xs"
                                  @click="selectDrilldown(item.counter)"
                                >
                                  <div class="d-flex align-items-center gap-2 text-truncate">
                                    <span class="legend-color-dot" :style="{ backgroundColor: item.color }"></span>
                                    <span class="text-xs font-weight-bold text-dark text-truncate">{{ item.counter.name }}</span>
                                  </div>
                                  <span class="badge bg-light text-dark border font-weight-bold text-xs">{{ item.count }} tokens</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Right Column: Token List / Kanban Drag & Drop Board (Visible ONLY for Receptionist) -->
                    <div v-if="isReceptionist" class="col-12">
                      <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                        <div class="d-flex align-items-center gap-2">
                          <h6 class="text-xs font-weight-bolder text-uppercase text-secondary mb-0">
                            <i class="fas fa-ticket-alt text-teal me-1"></i>
                            {{ drilldownSelectedCounter ? `${drilldownSelectedCounter.name} Tokens` : 'All Counters Token List' }}
                          </h6>
                          <span v-if="receptionistTokenView === 'list'" class="text-xxs text-muted">
                            (Page {{ drilldownCurrentPage }} of {{ drilldownTotalPages }})
                          </span>
                        </div>

                        <!-- View Switcher & Drag-and-drop indicator -->
                        <div class="d-flex align-items-center gap-2">
                          <span class="text-xxs text-secondary d-none d-sm-inline">
                            <i class="fas fa-hand-pointer text-teal me-1"></i>Drag tokens to reassign
                          </span>
                          <div class="btn-group btn-group-sm bg-white border rounded-3 p-1 shadow-xs">
                            <button
                              type="button"
                              class="btn btn-xs py-1 px-2 mb-0 rounded-2"
                              :class="receptionistTokenView === 'list' ? 'btn-teal text-white font-weight-bold shadow-xs' : 'btn-outline-secondary border-0'"
                              @click="receptionistTokenView = 'list'"
                              title="Table List view (6 per page)"
                            >
                              <i class="fas fa-list me-1"></i> Table View
                            </button>
                            <button
                              type="button"
                              class="btn btn-xs py-1 px-2 mb-0 rounded-2"
                              :class="receptionistTokenView === 'board' ? 'btn-teal text-white font-weight-bold shadow-xs' : 'btn-outline-secondary border-0'"
                              @click="receptionistTokenView = 'board'"
                              title="Kanban Drag & Drop Board"
                            >
                              <i class="fas fa-columns me-1"></i> Drag & Drop Board
                            </button>
                            <button
                              type="button"
                              class="btn btn-xs py-1 px-2 mb-0 rounded-2"
                              :class="tokenBoardFullWidth ? 'btn-teal text-white font-weight-bold shadow-xs' : 'btn-outline-secondary border-0'"
                              @click="tokenBoardFullWidth = !tokenBoardFullWidth"
                              :title="tokenBoardFullWidth ? 'Standard view' : 'Expand full width'"
                            >
                              <i :class="tokenBoardFullWidth ? 'fas fa-compress-arrows-alt' : 'fas fa-expand-arrows-alt'" class="me-1"></i>
                              {{ tokenBoardFullWidth ? 'Standard' : 'Full Width' }}
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- Quick Drop Zones Bar (Drag any token below onto a counter to reassign immediately) -->
                      <div v-if="receptionistTokenView === 'list'" class="quick-drop-bar p-2 mb-2 bg-light border rounded-3 d-flex align-items-center gap-2 overflow-x-auto">
                        <span class="text-xxs fw-bold text-uppercase text-secondary text-nowrap d-flex align-items-center gap-1">
                          <i class="fas fa-arrows-alt text-teal"></i> Drop Target:
                        </span>

                        <div
                          class="counter-drop-chip px-2 py-1 rounded-2 text-xxs font-weight-bold border cursor-pointer text-nowrap shadow-xs"
                          :class="{ 'drop-active pulse-border': dragOverColumnId === 'unassigned', 'bg-white text-secondary': dragOverColumnId !== 'unassigned' }"
                          @dragover.prevent="onDragOver('unassigned')"
                          @dragleave="onDragLeave"
                          @drop="onDropToken('unassigned')"
                          title="Drop here to move token to unassigned queue"
                        >
                          <i class="fas fa-inbox me-1 text-secondary"></i> Unassigned ({{ getTokensForCounter('unassigned').length }})
                        </div>

                        <div
                          v-for="c in counters"
                          :key="c.id"
                          class="counter-drop-chip px-2 py-1 rounded-2 text-xxs font-weight-bold border cursor-pointer text-nowrap shadow-xs"
                          :class="{ 'drop-active pulse-border': String(dragOverColumnId) === String(c.id), 'bg-white text-dark': String(dragOverColumnId) !== String(c.id) }"
                          @dragover.prevent="onDragOver(c.id)"
                          @dragleave="onDragLeave"
                          @drop="onDropToken(c)"
                          :title="`Drop here to transfer token to ${c.name}`"
                        >
                          <i class="fas fa-door-open text-teal me-1"></i> {{ c.name }} ({{ getTokensForCounter(c).length }})
                        </div>
                      </div>

                      <!-- 1. TABLE VIEW (6 records per page, Scrollable, Draggable Rows) -->
                      <div v-if="receptionistTokenView === 'list'">
                        <!-- Scrollable Table Container -->
                        <div class="table-responsive border rounded-3 bg-white shadow-xs" style="max-height: 380px; overflow-y: auto;">
                          <table class="table md-table align-items-center mb-0">
                            <thead class="bg-light sticky-top" style="z-index: 2;">
                              <tr>
                                <th class="text-uppercase text-secondary text-xxs font-weight-bolder ps-3 py-2">Token #</th>
                                <th class="text-uppercase text-secondary text-xxs font-weight-bolder py-2">Patient Details</th>
                                <th class="text-uppercase text-secondary text-xxs font-weight-bolder py-2">Service</th>
                                <th class="text-uppercase text-secondary text-xxs font-weight-bolder py-2">Counter / Doctor</th>
                                <th class="text-uppercase text-secondary text-xxs font-weight-bolder text-center py-2">Time</th>
                                <th class="text-uppercase text-secondary text-xxs font-weight-bolder text-center py-2">Status</th>
                                <th class="text-uppercase text-secondary text-xxs font-weight-bolder text-center pe-3 py-2">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="t in paginatedDrilldownTokens"
                                :key="t.id"
                                class="border-bottom hover-bg-light draggable-row"
                                :draggable="canDragToken(t)"
                                @dragstart="onDragStart($event, t)"
                              >
                                <!-- Token Number + Drag Handle + Type Badge -->
                                <td class="ps-3 py-2">
                                  <div class="d-flex align-items-center gap-2">
                                    <span v-if="canDragToken(t)" class="text-secondary opacity-6 cursor-grab" title="Drag to transfer counter">
                                      <i class="fas fa-grip-vertical text-xs"></i>
                                    </span>
                                    <div class="d-flex flex-column align-items-start">
                                      <span class="badge bg-dark text-white font-weight-bold text-xs px-2 py-1">
                                        {{ t.token_number }}
                                      </span>
                                      <small
                                        class="badge text-xxs px-1 py-0 mt-1"
                                        :class="{
                                          'bg-danger text-white': t.is_emergency || t.booking_type === 'Emergency',
                                          'bg-warning text-dark': t.booking_type === 'VIP',
                                          'bg-primary text-white': t.booking_type === 'Appointment',
                                          'bg-secondary text-white': !t.booking_type || t.booking_type === 'Walk-in'
                                        }"
                                      >
                                        {{ t.booking_type || 'Walk-in' }}
                                      </small>
                                    </div>
                                  </div>
                                </td>

                                <!-- Patient Details (Name, Age, Gender, Phone) -->
                                <td class="py-2">
                                  <p class="text-xs font-weight-bold text-dark mb-0 text-truncate" style="max-width: 170px;">
                                    {{ t.token_name || 'Walk-in Patient' }}
                                  </p>
                                  <div class="text-xxs text-secondary d-flex align-items-center gap-1 flex-wrap">
                                    <span class="badge bg-light text-dark px-1 py-0 border">
                                      <i :class="String(t.gender || t.sex || t.patient_gender).toLowerCase() === 'female' ? 'fas fa-female text-danger' : 'fas fa-male text-primary'" class="me-1"></i>
                                      {{ t.gender || t.sex || t.patient_gender || 'Not specified' }}
                                    </span>
                                    <span v-if="t.age">· {{ t.age }} yrs</span>
                                    <span v-if="t.phone_number || t.phone">· <i class="fas fa-phone-alt text-xxs text-muted"></i> {{ t.phone_number || t.phone }}</span>
                                  </div>
                                </td>

                                <!-- Service Name -->
                                <td class="py-2">
                                  <span class="text-xs font-weight-semibold text-dark d-block text-truncate" style="max-width: 130px;">
                                    {{ t.service?.name || t.service_name || '-' }}
                                  </span>
                                  <small v-if="t.service?.prefix" class="text-xxs text-muted">Prefix: {{ t.service.prefix }}</small>
                                </td>

                                <!-- Counter & Attendant -->
                                <td class="py-2">
                                  <span class="badge bg-light text-dark border text-xxs d-inline-block text-truncate mb-1" style="max-width: 140px;">
                                    <i class="fas fa-door-closed me-1 text-teal"></i>{{ t.counter?.name || t.counter_name || 'Unassigned' }}
                                  </span>
                                  <small v-if="t.token_attended && t.token_attended !== 'No'" class="text-xxs text-secondary d-block text-truncate" style="max-width: 140px;">
                                    <i class="fas fa-user-md me-1 text-muted"></i>{{ t.token_attended }}
                                  </small>
                                </td>

                                <!-- Time -->
                                <td class="py-2 text-center text-nowrap">
                                  <span class="text-xs font-weight-bold text-dark d-block">{{ formatTime(t.booking_time || t.createdAt || t.created_at) }}</span>
                                  <small v-if="t.booking_date" class="text-xxs text-muted">{{ t.booking_date.substring(0, 10) }}</small>
                                </td>

                                <!-- Status Pill -->
                                <td class="py-2 text-center text-nowrap">
                                  <div class="d-flex align-items-center justify-content-center gap-1 flex-wrap">
                                    <span
                                      class="badge text-xxs font-weight-bold px-2 py-1"
                                      :class="{
                                        'bg-success-soft text-success': t.status === 'completed',
                                        'bg-primary-soft text-primary': t.status === 'calling',
                                        'bg-info-soft text-info': ['serving', 'processing'].includes(t.status),
                                        'bg-warning-soft text-warning': ['pending', 'waiting'].includes(t.status),
                                        'bg-secondary-soft text-secondary': t.status === 'skipped',
                                        'bg-danger-soft text-danger': t.status === 'rejected'
                                      }"
                                    >
                                      {{ t.status }}
                                    </span>
                                    <span 
                                      v-if="t.counter_on_break || isTokenDoctorOnBreak(t)" 
                                      class="badge bg-warning-soft text-warning text-xxs ms-1"
                                      :title="'Doctor on break until ' + (t.break_resumes_at || t.break_end || getTokenBreakResumesAt(t))"
                                    >
                                      <i class="fas fa-pause-circle me-1"></i> Break (Resumes {{ t.break_resumes_at || t.break_end || getTokenBreakResumesAt(t) }})
                                    </span>
                                  </div>
                                </td>

                                <!-- Edit Action Button -->
                                <td class="pe-3 py-2 text-center text-nowrap">
                                  <button
                                    type="button"
                                    class="btn btn-outline-teal btn-xs py-1 px-2 mb-0 font-weight-bold d-inline-flex align-items-center gap-1 shadow-xs"
                                    @click="openEditTokenModal(t)"
                                    title="Edit token details"
                                  >
                                    <i class="fas fa-edit"></i>
                                    <span>Edit</span>
                                  </button>
                                </td>
                              </tr>

                              <!-- Empty State -->
                              <tr v-if="paginatedDrilldownTokens.length === 0">
                                <td colspan="7" class="text-center py-4 text-muted">
                                  <i class="fas fa-inbox fa-2x mb-2 text-secondary opacity-4 d-block"></i>
                                  <p class="text-sm font-weight-bold mb-1">
                                    {{ drilldownSearchQuery ? 'No matching tokens found.' : (drilldownSelectedCounter ? `No tokens for ${drilldownSelectedCounter.name} today.` : 'No tokens registered today.') }}
                                  </p>
                                  <small class="text-muted">Use the "Add Patient (Token)" button above to create a new token.</small>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        <!-- Pagination Footer (Exactly 6 per page) -->
                        <div v-if="drilldownFilteredTokens.length > 0" class="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-2 pt-2 border-top">
                          <small class="text-xxs text-secondary">
                            Showing <strong>{{ (drilldownCurrentPage - 1) * drilldownPageSize + 1 }}</strong> to
                            <strong>{{ Math.min(drilldownCurrentPage * drilldownPageSize, drilldownFilteredTokens.length) }}</strong> of
                            <strong>{{ drilldownFilteredTokens.length }}</strong> tokens
                          </small>

                          <div class="d-flex align-items-center gap-1">
                            <button
                              type="button"
                              class="btn btn-outline-teal btn-xs py-1 px-2 mb-0"
                              :disabled="drilldownCurrentPage <= 1"
                              @click="drilldownCurrentPage--"
                              title="Previous Page"
                            >
                              <i class="fas fa-chevron-left me-1"></i> Prev
                            </button>

                            <button
                              v-for="p in drilldownTotalPages"
                              :key="p"
                              type="button"
                              class="btn btn-xs py-1 px-2 mb-0"
                              :class="drilldownCurrentPage === p ? 'btn-teal text-white font-weight-bold' : 'btn-outline-secondary'"
                              @click="drilldownCurrentPage = p"
                            >
                              {{ p }}
                            </button>

                            <button
                              type="button"
                              class="btn btn-outline-teal btn-xs py-1 px-2 mb-0"
                              :disabled="drilldownCurrentPage >= drilldownTotalPages"
                              @click="drilldownCurrentPage++"
                              title="Next Page"
                            >
                              Next <i class="fas fa-chevron-right ms-1"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- 2. KANBAN BOARD VIEW (Interactive Drag & Drop Columns across Counters) -->
                      <div v-else-if="receptionistTokenView === 'board'" class="w-100" style="max-width: 100%; overflow: hidden;">
                        <!-- Scroll Navigation Controls -->
                        <div class="d-flex flex-wrap align-items-center justify-content-between p-2 mb-2 bg-light rounded-3 border">
                          <small class="text-xxs text-secondary d-flex align-items-center gap-1">
                            <i class="fas fa-info-circle text-teal"></i>
                            Showing counters with active tokens first. Scroll horizontally to view all {{ boardCounters.length }} counters.
                          </small>
                          <div class="d-flex align-items-center gap-1">
                            <button
                              type="button"
                              class="btn btn-outline-teal btn-xs py-1 px-2 mb-0 shadow-xs"
                              @click="$refs.boardWrapper && $refs.boardWrapper.scrollBy({ left: -320, behavior: 'smooth' })"
                              title="Scroll left"
                            >
                              <i class="fas fa-chevron-left me-1"></i> Left
                            </button>
                            <button
                              type="button"
                              class="btn btn-outline-teal btn-xs py-1 px-2 mb-0 shadow-xs"
                              @click="$refs.boardWrapper && $refs.boardWrapper.scrollBy({ left: 320, behavior: 'smooth' })"
                              title="Scroll right"
                            >
                              Right <i class="fas fa-chevron-right ms-1"></i>
                            </button>
                          </div>
                        </div>

                        <div ref="boardWrapper" class="counter-board-wrapper d-flex gap-3 pb-3 overflow-x-auto w-100" style="min-height: 420px; max-width: 100%; width: 100%;">
                          <!-- Unassigned Queue Column -->
                          <div
                            class="counter-column flex-shrink-0"
                            :class="{ 'drop-active': dragOverColumnId === 'unassigned' }"
                            style="width: 280px; background: #f8fafc; border-radius: 12px; border: 2px dashed #cbd5e1;"
                            @dragover.prevent="onDragOver('unassigned')"
                            @dragleave="onDragLeave"
                            @drop="onDropToken('unassigned')"
                          >
                            <div class="p-2 px-3 bg-secondary text-white rounded-top d-flex justify-content-between align-items-center">
                              <div>
                                <h6 class="text-xs font-weight-bold mb-0 text-white"><i class="fas fa-inbox me-1"></i>Unassigned Queue</h6>
                                <small class="text-xxs opacity-75">General waiting pool</small>
                              </div>
                              <span class="badge bg-white text-dark text-xs">{{ getTokensForCounter('unassigned').length }}</span>
                            </div>

                            <div class="p-2 d-flex flex-column gap-2" style="max-height: 380px; overflow-y: auto;">
                              <div
                                v-for="tok in getTokensForCounter('unassigned')"
                                :key="tok.id"
                                class="token-drag-card p-2 bg-white rounded-3 border shadow-xs"
                                :draggable="canDragToken(tok)"
                                @dragstart="onDragStart($event, tok)"
                              >
                                <div class="d-flex justify-content-between align-items-center mb-1">
                                  <span class="badge bg-dark text-white text-xxs font-weight-bold">{{ tok.token_number }}</span>
                                  <span class="badge bg-warning-soft text-warning text-xxs">{{ tok.status }}</span>
                                </div>
                                <p class="text-xs font-weight-bold text-dark mb-0 text-truncate">{{ tok.token_name || 'Walk-in Patient' }}</p>
                                <div class="d-flex justify-content-between align-items-center text-xxs text-secondary mt-1">
                                  <span>{{ tok.service?.name || tok.service_name || 'General' }}</span>
                                  <i class="fas fa-grip-vertical text-muted"></i>
                                </div>
                              </div>
                              <div v-if="getTokensForCounter('unassigned').length === 0" class="text-center py-4 text-muted text-xxs fst-italic">
                                No unassigned tokens
                              </div>
                            </div>
                          </div>

                          <!-- Dynamic Counter Columns -->
                          <div
                            v-for="counter in boardCounters"
                            :key="counter.id"
                            class="counter-column flex-shrink-0"
                            :class="{ 'drop-active': String(dragOverColumnId) === String(counter.id) }"
                            style="width: 280px; background: #f8fafc; border-radius: 12px; border: 2px dashed #cbd5e1;"
                            @dragover.prevent="onDragOver(counter.id)"
                            @dragleave="onDragLeave"
                            @drop="onDropToken(counter)"
                          >
                          <div class="p-2 px-3 bg-teal text-white rounded-top d-flex justify-content-between align-items-center">
                            <div>
                              <h6 class="text-xs font-weight-bold mb-0 text-white text-truncate" style="max-width: 170px;">
                                <i class="fas fa-door-open me-1"></i>{{ counter.name }}
                              </h6>
                              <small class="text-xxs opacity-75 text-truncate d-block" style="max-width: 170px;">
                                {{ counter.user?.name || counter.attendant_name || 'Assigned Staff' }}
                              </small>
                            </div>
                            <span class="badge bg-white text-teal text-xs font-weight-bold">{{ getTokensForCounter(counter).length }}</span>
                          </div>

                          <div class="p-2 d-flex flex-column gap-2" style="max-height: 380px; overflow-y: auto;">
                            <div
                              v-for="tok in getTokensForCounter(counter)"
                              :key="tok.id"
                              class="token-drag-card p-2 bg-white rounded-3 border shadow-xs"
                              :draggable="canDragToken(tok)"
                              @dragstart="onDragStart($event, tok)"
                            >
                              <div class="d-flex justify-content-between align-items-center mb-1">
                                <span class="badge bg-teal-soft text-teal text-xxs font-weight-bold">{{ tok.token_number }}</span>
                                <span
                                  class="badge text-xxs font-weight-bold"
                                  :class="tok.status === 'completed' ? 'bg-success-soft text-success' : 'bg-primary-soft text-primary'"
                                >
                                  {{ tok.status }}
                                </span>
                              </div>
                              <p class="text-xs font-weight-bold text-dark mb-0 text-truncate">{{ tok.token_name || 'Walk-in Patient' }}</p>
                              <div class="d-flex justify-content-between align-items-center text-xxs text-secondary mt-1">
                                <span>{{ tok.service?.name || tok.service_name || 'General' }}</span>
                                <i class="fas fa-grip-vertical text-muted"></i>
                              </div>
                            </div>
                            <div v-if="getTokensForCounter(counter).length === 0" class="text-center py-4 text-muted text-xxs fst-italic">
                              No tokens in this counter
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

              <!-- Display screens (fills the left column space and balances the layout) -->
              <div class="md-card p-3 mb-3">
                <div class="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <h6 class="mb-0 font-weight-bold text-dark">
                      <i class="fas fa-desktop text-teal me-2"></i>Display screens
                    </h6>
                    <small class="text-muted text-xs">Waiting halls and lobbies</small>
                  </div>
                  <span class="badge bg-teal-soft text-teal">{{ onlineDisplaysCount }} / {{ displays.length }} online</span>
                </div>

                <div class="row g-2">
                  <div v-for="screen in displays" :key="screen.id" class="col-12 col-md-6 col-lg-4">
                    <div class="screen-item p-2 border rounded-3 h-100 d-flex align-items-center gap-2">
                      <div class="screen-status-icon" :class="screen.online ? 'bg-success-soft text-success' : 'bg-danger-soft text-danger'">
                        <i class="fas fa-tv"></i>
                      </div>
                      <div class="flex-grow-1 min-w-0">
                        <h6 class="mb-0 text-sm font-weight-bold text-dark text-truncate">{{ screen.name }}</h6>
                        <small class="text-muted text-xs d-block text-truncate">{{ screen.location }} · {{ screen.playlist }}</small>
                      </div>
                      <span :class="screen.online ? 'badge bg-success-soft text-success' : 'badge bg-danger-soft text-danger'" class="text-xs">
                        {{ screen.online ? 'Online' : 'Offline' }}
                      </span>
                    </div>
                  </div>
                  <div v-if="displays.length === 0" class="col-12 text-center py-4 text-muted">
                    <i class="fas fa-tv fa-2x mb-2 text-secondary opacity-5"></i>
                    <p class="text-sm mb-0">No display devices found.</p>
                  </div>
                </div>
              </div>

            </div>

            <!-- ============ RIGHT COLUMN ============ -->
            <div v-if="!(isReceptionist && tokenBoardFullWidth)" class="col-12 col-xl-4">

              <!-- Token Records / Doctor Token Flow Card (Directly aligned with Doctors table) -->
              <div class="md-card p-3 mb-3 token-flow-card">
                <!-- Header with Title, Timing Option, and Clear Doctor -->
                <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                  <div class="d-flex align-items-center gap-2">
                    <h6 class="mb-0 font-weight-bold text-dark text-nowrap">
                      <i class="fas fa-chart-line text-teal me-1"></i>
                      <span v-if="selectedDoctorName">{{ selectedDoctorName }} Token Records</span>
                      <span v-else>Token Records</span>
                    </h6>
                    <span v-if="selectedDoctorName" class="badge bg-teal-soft text-teal text-xxs font-weight-bold">
                      Doctor Records
                    </span>
                  </div>
                  <div class="d-flex align-items-center gap-1 flex-wrap">
                    <button
                      v-if="selectedDoctorName"
                      type="button"
                      class="btn btn-outline-secondary btn-xs mb-0 py-1 px-2 font-weight-bold text-xxs"
                      @click="clearSelectedDoctor"
                      title="Clear doctor filter and view all tokens"
                    >
                      All Doctors <i class="fas fa-times ms-1"></i>
                    </button>
                    <!-- Booking Date Filter Dropdown -->
                    <select
                      v-model="tokenFlowDateFilter"
                      @change="onTokenFlowDateFilterChange"
                      class="form-select form-select-sm token-flow-timing-select"
                      title="Filter by booking date"
                      style="min-width: 95px;"
                    >
                      <option value="today">Today</option>
                      <option value="yesterday">Yesterday</option>
                      <option value="custom">Pick Date...</option>
                      <option value="all">All Dates</option>
                    </select>

                    <!-- Custom Date Picker Input -->
                    <input
                      v-if="tokenFlowDateFilter === 'custom'"
                      v-model="tokenFlowCustomDate"
                      type="date"
                      class="form-control form-control-sm token-flow-timing-select px-2"
                      style="max-width: 125px; font-size: 11px; height: 28px;"
                      @change="onTokenFlowDateFilterChange"
                    />

                    <!-- Timing Option Dropdown -->
                    <select
                      v-model="tokenFlowHour"
                      class="form-select form-select-sm token-flow-timing-select"
                      title="Filter by timing / hour"
                      style="min-width: 95px;"
                    >
                      <option value="all">All Hours</option>
                      <option
                        v-for="slot in tokenFlowHourOptions"
                        :key="slot.hour"
                        :value="slot.hour"
                      >
                        {{ slot.timeRange }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Doctor Info Banner (Shown when a doctor is clicked) -->
                <div v-if="selectedDoctorName" class="d-flex align-items-center justify-content-between p-2 mb-3 rounded-3 bg-light border">
                  <div class="d-flex align-items-center gap-2 min-w-0">
                    <div class="avatar avatar-xs bg-teal text-white rounded-circle d-flex align-items-center justify-content-center font-weight-bold text-xxs" style="width: 28px; height: 28px;">
                      {{ getInitials(selectedDoctorName) }}
                    </div>
                    <div class="min-w-0">
                      <div class="d-flex align-items-center gap-1 flex-wrap">
                        <strong class="text-xs text-dark text-truncate">{{ selectedDoctorName }}</strong>
                        <span v-if="selectedDoctorCounterName && selectedDoctorCounterName !== '--'" class="badge bg-white text-secondary border text-xxs font-weight-bold">
                          <i class="fas fa-desktop text-teal me-1"></i>{{ selectedDoctorCounterName }}
                        </span>
                      </div>
                      <small class="text-muted text-xxs d-block text-truncate">
                        {{ selectedDoctorDepartment || 'Medical Department' }}
                      </small>
                    </div>
                  </div>
                  <div class="text-end">
                    <span 
                      v-if="isDoctorBreak(selectedDoctorObject || selectedDoctorStatus)" 
                      class="badge bg-warning-soft text-warning text-xxs font-weight-bold"
                    >
                      <i class="fas fa-coffee me-1"></i> On Break
                      <span v-if="selectedDoctorBreakStart && selectedDoctorBreakEnd">
                        ({{ selectedDoctorBreakStart }} - {{ selectedDoctorBreakEnd }})
                      </span>
                    </span>
                    <span v-else class="badge text-xxs font-weight-bold" :class="getAdminDoctorStatusBadgeClass(selectedDoctorStatus)">
                      {{ selectedDoctorStatus || 'Available' }}
                    </span>
                    <small 
                      v-if="selectedDoctorBreakEnd && isDoctorBreak(selectedDoctorObject || selectedDoctorStatus)" 
                      class="text-muted d-block font-size-11 mt-1"
                    >
                      <i class="far fa-clock text-warning me-1"></i> Resumes at {{ selectedDoctorBreakEnd }}
                    </small>
                  </div>
                </div>

                <!-- Department and Doctor Filters Row (shown ONLY when no doctor is specifically selected) -->
                <div v-else class="row g-2 mb-3">
                  <div class="col-6">
                    <label class="form-label text-xxs text-secondary text-uppercase font-weight-bold mb-1">
                      Department
                    </label>
                    <select
                      v-model="tokenFlowDepartment"
                      @change="onTokenFlowDeptChange"
                      class="form-select form-select-sm token-flow-select"
                    >
                      <option value="all">All Departments</option>
                      <option
                        v-for="dept in departmentSummaryList"
                        :key="dept.name"
                        :value="dept.name"
                      >
                        {{ dept.name }}
                      </option>
                    </select>
                  </div>
                  <div class="col-6">
                    <label class="form-label text-xxs text-secondary text-uppercase font-weight-bold mb-1">
                      Doctor
                    </label>
                    <select
                      v-model="tokenFlowDoctorId"
                      @change="tokenFlowHour = 'all'"
                      class="form-select form-select-sm token-flow-select"
                    >
                      <option value="all">All Doctors</option>
                      <option
                        v-for="doc in tokenFlowDoctorOptions"
                        :key="doc.id"
                        :value="doc.id"
                      >
                        {{ doc.name }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Active Hour Indicator Banner (appears only when an hour bar is clicked) -->
                <div v-if="tokenFlowHour !== 'all'" class="d-flex align-items-center justify-content-between px-2 py-1 mb-2 rounded-2 text-xs" style="background-color: #f0f7ff; border: 1px solid #d0e3ff;">
                  <span class="text-primary font-weight-bold d-flex align-items-center gap-1">
                    <i class="far fa-clock"></i>
                    <span>{{ selectedHourTimeRange }}</span>
                    <small class="text-secondary fw-normal">({{ selectedHourTokens.length }} token{{ selectedHourTokens.length === 1 ? '' : 's' }})</small>
                  </span>
                  <button
                    type="button"
                    class="btn btn-link text-primary text-xxs p-0 mb-0 font-weight-bold text-decoration-none"
                    @click="tokenFlowHour = 'all'"
                    title="Clear hourly filter and show full day"
                  >
                    Show Full Day <i class="fas fa-times ms-1"></i>
                  </button>
                </div>

                <!-- 4 Status Summary Pills matching screenshot -->
                <div class="token-flow-pills-row d-flex align-items-center gap-2 mb-3">
                  <div class="token-flow-pill flex-fill" :class="{ 'token-flow-pill-active': tokenFlowHour !== 'all' }">
                    <div class="token-flow-pill-val">{{ tokenFlowStats.issued }}</div>
                    <div class="token-flow-pill-lbl">Issued</div>
                  </div>
                  <div class="token-flow-pill flex-fill">
                    <div class="token-flow-pill-val text-success">{{ tokenFlowStats.served }}</div>
                    <div class="token-flow-pill-lbl">Served</div>
                  </div>
                  <div class="token-flow-pill flex-fill">
                    <div class="token-flow-pill-val text-secondary">{{ tokenFlowStats.skipped }}</div>
                    <div class="token-flow-pill-lbl">Skipped</div>
                  </div>
                  <div class="token-flow-pill flex-fill">
                    <div class="token-flow-pill-val text-warning">{{ tokenFlowStats.waiting }}</div>
                    <div class="token-flow-pill-lbl">Waiting</div>
                  </div>
                </div>

                <!-- Hourly Bar Chart matching screenshot (Click bar to select hour) -->
                <div class="token-flow-chart-wrap mb-2">
                  <div class="token-flow-bars d-flex align-items-end justify-content-between">
                    <div
                      v-for="bar in tokenFlowHourlyData"
                      :key="bar.hour"
                      class="token-flow-bar-col d-flex flex-column align-items-center cursor-pointer"
                      :class="{ 'bar-col-selected': String(tokenFlowHour) === String(bar.hour) }"
                      :title="`Click to filter: ${bar.timeRange} (${bar.count} token${bar.count === 1 ? '' : 's'})`"
                      @click="toggleTokenFlowHour(bar.hour)"
                    >
                      <!-- Count above bar -->
                      <span
                        class="token-flow-bar-val mb-1"
                        :class="{ 'font-weight-bold text-dark': bar.isPeak, 'text-primary font-weight-bolder': String(tokenFlowHour) === String(bar.hour) }"
                      >
                        {{ bar.count }}
                      </span>

                      <!-- Bar track and fill -->
                      <div class="token-flow-bar-track">
                        <div
                          class="token-flow-bar"
                          :class="{ 'bar-peak': bar.isPeak, 'bar-active': String(tokenFlowHour) === String(bar.hour) }"
                          :style="{ height: `${bar.heightPct}%` }"
                        ></div>
                      </div>

                      <!-- Hour label below bar -->
                      <span
                        class="token-flow-bar-hour mt-1"
                        :class="{ 'font-weight-bold text-dark': bar.isPeak, 'text-primary font-weight-bolder': String(tokenFlowHour) === String(bar.hour) }"
                      >
                        {{ bar.label }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Busiest time indicator & Patient toggle -->
                <div class="token-flow-footer pt-1 d-flex align-items-center justify-content-between flex-wrap gap-1">
                  <span class="text-secondary text-xs font-weight-500">
                    Busiest time:
                    <strong class="text-dark ms-1">{{ tokenFlowBusiestTime }}</strong>
                  </span>
                  <button
                    type="button"
                    class="btn btn-link text-teal text-xs p-0 mb-0 font-weight-bold text-decoration-none"
                    @click="showHourlyDetails = !showHourlyDetails"
                  >
                    <i :class="showHourlyDetails ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="me-1"></i>
                    {{ showHourlyDetails ? 'Hide Tokens' : `Show Tokens (${displayedTokenFlowTokens.length})` }}
                  </button>
                </div>

                <!-- Token Records Section (Directly shows the token records) -->
                <div v-if="showHourlyDetails || selectedDoctorName" class="hourly-patient-drawer mt-2 pt-2 border-top">
                  <div class="d-flex align-items-center justify-content-between mb-2">
                    <small class="text-xxs text-uppercase text-secondary font-weight-bold">
                      <i class="fas fa-ticket-alt text-teal me-1"></i>
                      {{ selectedDoctorName ? `${selectedDoctorName}'s Tokens` : 'Tokens' }}
                      <span v-if="tokenFlowHour !== 'all'">({{ selectedHourTimeRange }})</span>
                    </small>
                    <div class="d-flex align-items-center gap-1">
                      <span class="badge bg-white text-secondary border text-xxs font-weight-bold">
                        <i class="far fa-calendar-alt text-teal me-1"></i>{{ tokenFlowDateLabel }}
                      </span>
                      <span class="badge bg-teal-soft text-teal text-xxs font-weight-bold">
                        {{ displayedTokenFlowTokens.length }} total
                      </span>
                    </div>
                  </div>

                  <div style="max-height: 250px; overflow-y: auto;" class="pe-1">
                    <div
                      v-for="tok in displayedTokenFlowTokens"
                      :key="tok.id"
                      class="p-2 mb-2 rounded bg-light border-start-3"
                      :style="{ borderLeft: `3px solid ${getHourlyTokenBorderColor(tok.status)}` }"
                    >
                      <div class="d-flex align-items-center justify-content-between mb-1">
                        <div class="d-flex align-items-center gap-1 flex-wrap">
                          <span class="font-weight-bold text-dark text-xs badge bg-white border text-dark">
                            {{ tok.token_number }}
                          </span>
                          <span class="text-xs text-dark font-weight-bold text-truncate" style="max-width: 130px;">
                            {{ tok.token_name || 'Walk-in' }}
                          </span>
                          <!-- Token List Break Badge (Item 4) -->
                          <span 
                            v-if="tok.counter_on_break || isTokenDoctorOnBreak(tok)" 
                            class="badge bg-warning-soft text-warning text-xxs ms-1"
                            :title="'Doctor on break until ' + (tok.break_resumes_at || tok.break_end || getTokenBreakResumesAt(tok))"
                          >
                            <i class="fas fa-pause-circle me-1"></i> Break (Resumes {{ tok.break_resumes_at || tok.break_end || getTokenBreakResumesAt(tok) }})
                          </span>
                        </div>
                        <span class="badge text-xxs font-weight-bold" :class="getTokenStatusBadgeClass(tok.status)">
                          {{ tok.status || 'pending' }}
                        </span>
                      </div>

                      <!-- Counter and Service Information Row -->
                      <div class="d-flex align-items-center justify-content-between flex-wrap gap-1 text-xxs mt-1 pt-1 border-top border-light">
                        <div class="d-flex align-items-center gap-1">
                          <span class="badge bg-white text-secondary border font-weight-bold">
                            <i class="fas fa-desktop text-teal me-1"></i>{{ getCounterNameForToken(tok) }}
                          </span>
                          <span class="text-secondary text-truncate" style="max-width: 110px;">
                            {{ tok.service?.name || tok.department || 'General OPD' }}
                          </span>
                        </div>

                        <span class="text-muted font-weight-bold">
                          <span v-if="extractTokenBookingDate(tok)" class="me-2"><i class="far fa-calendar-alt text-teal me-1"></i>{{ extractTokenBookingDate(tok) }}</span>
                          <i class="far fa-clock me-1"></i>{{ formatTime(tok.createdAt || tok.created_at || tok.date || tok.token_time) }}
                        </span>
                      </div>
                    </div>

                    <div v-if="displayedTokenFlowTokens.length === 0" class="text-center py-3 text-muted text-xs">
                      <i class="fas fa-ticket-alt text-secondary opacity-4 fa-2x mb-2 d-block"></i>
                      <p class="mb-1 font-weight-bold text-dark">
                        {{ selectedDoctorName ? `No token records currently assigned to ${selectedDoctorName}.` : 'No tokens found.' }}
                      </p>
                      <small v-if="selectedDoctorCounterName && selectedDoctorCounterName !== '--'" class="text-muted d-block mb-2">
                        Assigned Counter: <strong>{{ selectedDoctorCounterName }}</strong> · Status: {{ selectedDoctorStatus }}
                      </small>
                      <button
                        type="button"
                        class="btn btn-xs btn-outline-teal mt-1 py-1 px-2"
                        @click="$router.push('/tokens')"
                      >
                        <i class="fas fa-plus me-1"></i>New Token
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Emergency broadcast (Positioned below Token Records card) -->
              <div ref="emergencyPanel" class="md-card p-3 mb-3">
                <div class="mb-3 d-flex align-items-center justify-content-between">
                  <div>
                    <h6 class="mb-0 font-weight-bold text-dark">
                      <i class="fas fa-bullhorn text-danger me-2"></i>Emergency broadcast
                    </h6>
                    <small class="text-muted text-xs">Alert all waiting-hall screens instantly</small>
                  </div>
                  <span v-if="emergencyTokens.length" class="badge bg-danger-soft text-danger animate-pulse">
                    {{ emergencyTokens.length }} Active Alert(s)
                  </span>
                </div>

                <div v-if="emergencyTokens.length" class="emergency-list d-flex flex-column gap-2 mb-3">
                  <button
                    v-for="em in emergencyTokens"
                    :key="em.id"
                    type="button"
                    class="btn btn-outline-danger d-flex align-items-center justify-content-between p-2 rounded-3 mb-0 text-start w-100 emergency-btn"
                    @click="broadcastEmergency(em)"
                  >
                    <div class="text-start">
                      <span class="font-weight-bold text-sm text-danger">{{ em.token_number }}</span>
                      <small class="text-muted text-xs d-block">{{ em.token_name || 'Emergency patient' }}</small>
                    </div>
                    <i class="fas fa-ambulance text-danger"></i>
                  </button>
                </div>
                <div v-else class="text-center py-3 text-muted">
                  <small>No active emergency tokens.</small>
                </div>
              </div>

              <!-- Booked and walk-in Card (Total average per day) -->
              <div class="md-card p-3 mb-3 booked-walkin-card">
                <div class="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <h6 class="mb-0 font-weight-bold text-dark">Booked and walk-in</h6>
                    <small class="text-muted text-xs">Total average per day</small>
                  </div>
                  <span class="badge bg-light text-secondary text-xs px-2 py-1 font-weight-bold">
                    {{ bookingTypesTotalCount }} total today
                  </span>
                </div>

                <!-- Multi-segment Progress Bar -->
                <div class="booked-walkin-progress mb-3">
                  <div
                    v-for="opt in bookingTypeOptionsList"
                    :key="opt.key"
                    :style="{ width: opt.pct + '%', backgroundColor: opt.color }"
                    :title="`${opt.label}: ${opt.count} (${opt.pct}%)`"
                    class="booking-type-progress-seg"
                  ></div>
                </div>

                <!-- Exactly the 6 requested options -->
                <div class="booked-walkin-rows d-flex flex-column">
                  <div
                    v-for="(opt, idx) in bookingTypeOptionsList"
                    :key="opt.key"
                    class="booked-walkin-row d-flex align-items-center justify-content-between py-2"
                    :class="{ 'border-bottom': idx < bookingTypeOptionsList.length - 1 }"
                  >
                    <div class="d-flex align-items-center gap-2">
                      <span class="booking-type-dot" :style="{ backgroundColor: opt.color }"></span>
                      <span class="text-sm text-secondary font-weight-500">{{ opt.label }}</span>
                    </div>
                    <div class="d-flex align-items-center gap-2">
                      <span class="text-xs text-muted" v-if="opt.count > 0">({{ opt.count }})</span>
                      <span class="text-sm font-weight-bold text-dark">{{ opt.pct }}%</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>

          </div>

      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { io } from "socket.io-client";
import Swal from "sweetalert2";
import DoctorDashboard from "@/views/DoctorDashboard.vue";
import UsersTable from "./components/UsersTable.vue";
import RoleTable from "./components/RoleTable.vue";
import Tokens from "./components/Tokens.vue";
import Counter from "./components/Counter.vue";
import Services from "./components/Services.vue";

/* ------------------------------------------------------------------ */
/* Shared helpers (previously copy-pasted across the component)        */
/* ------------------------------------------------------------------ */
const SERVING_STATUSES = ["serving", "processing"];
const QUEUE_NOW_STATUSES = ["serving", "processing", "calling"];
const QUEUE_NEXT_STATUSES = ["pending", "waiting"];
const DOCTOR_KEYWORDS = ["doctor", "physician", "consultant", "specialist", "surgeon"];
const NON_DOCTOR_ROLE_KEYWORDS = ["tokenuser", "tokenmanager", "tokenmanger", "operator", "teller", "superadmin", "supervisor", "tenant", "reception", "nurse"];

const lower = (v) => String(v || "").toLowerCase();
const stripDr = (v) => lower(v).replace(/^dr\.?\s*/i, "").trim();
const cleanRoleKey = (v) => lower(v).replace(/[^a-z0-9]/gi, "");
const hasAny = (text, words) => words.some((w) => text.includes(w));

const getAuthToken = () =>
  localStorage.getItem("token") || localStorage.getItem("auth_token") || localStorage.getItem("admin_token");
const getApiBase = () => (process.env.VUE_APP_API_BASE_URL || "/api").replace(/\/+$/, "");
const getStoredName = () => localStorage.getItem("userName") || localStorage.getItem("name") || "";
const getStoredEmail = () => localStorage.getItem("adminEmail") || localStorage.getItem("email") || "";

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}") || {};
  } catch (e) {
    return {};
  }
};
const readAllShifts = () => {
  try {
    return JSON.parse(localStorage.getItem("hospitalDoctorShifts") || "{}") || {};
  } catch (e) {
    return {};
  }
};
const writeLocalStatus = (status) => {
  localStorage.setItem("availability_status", status);
  localStorage.setItem("doctor_status", status);
};

const getInitials = (name) =>
  String(name || "")
    .replace(/^dr\.?\s*/i, "")
    .trim()
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase() || "DR";

const formatDoctorName = (rawName) => {
  const raw = String(rawName || "");
  return raw.startsWith("Dr.") ? raw : `Dr. ${raw.replace(/^dr\.?\s*/i, "").trim()}`;
};

const shiftLabel = (s) =>
  s && s.start_time && s.end_time ? `${s.start_time} - ${s.end_time}` : (s && s.shift) || "09:00 - 17:00";

const isEmergencyToken = (t) =>
  !!t &&
  lower(t.status) !== "completed" &&
  (t.is_emergency === true || t.is_emergency === 1 || t.is_emergency === "true" || lower(t.is_emergency) === "yes");

const roleNameOfUser = (u) =>
  lower(u?.role_name || u?.role_slug || (u?.role && (u.role.name || u.role.slug)) || u?.role || "");

const detectIsDoctor = () => {
  const r = cleanRoleKey(localStorage.getItem("userRole") || localStorage.getItem("role") || "");
  if (r.includes("doctor") || r === "physician") return true;
  const uRole = roleNameOfUser(readStoredUser());
  return uRole.includes("doctor") || uRole.includes("physician");
};

/** Pull an array out of the many response shapes this API returns. */
const extractItems = (resVal) => {
  if (!resVal) return [];
  const val = resVal.value !== undefined ? resVal.value : resVal;
  const body = val && val.data !== undefined ? val.data : val;
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== "object") return [];
  const keys = ["data", "result", "tokens", "rows", "users", "candidates", "attendants", "roles", "counters", "branches", "services"];
  for (const k of keys) if (Array.isArray(body[k])) return body[k];
  if (body.data && typeof body.data === "object") {
    for (const k of keys) if (Array.isArray(body.data[k])) return body.data[k];
  }
  return [];
};

/** Find the token currently being served for a user / counter / attendant name. */
const findServingToken = (tokens, userId, counterId, cleanName) => {
  const cn = lower(cleanName);
  return tokens.find((tok) => {
    if (!tok) return false;
    const st = lower(tok.status);
    if (["completed", "done", "expired", "cancelled"].includes(st)) return false;
    const tu = String(tok.user_id || "");
    const tc = String(tok.counter_id || "");
    const ta = stripDr(tok.token_attended);
    return (
      (userId && tu === String(userId)) ||
      (counterId && tc === String(counterId)) ||
      (cn && ta && ta !== "no" && (ta.includes(cn) || cn.includes(ta)))
    );
  });
};

export default {
  name: "MedicalDashboard",
  components: {
    DoctorDashboard,
    UsersTable,
    RoleTable,
    Tokens,
    Counter,
    Services,
  },
  beforeMount() {
    this.$store.state.showSidenav = true;
    this.$store.state.showNavbar = true;
    this.$store.state.showFooter = true;

    const isAuth = localStorage.getItem("medical_authenticated");
    const role = lower(localStorage.getItem("userRole"));

    if (isAuth !== "true" && !getAuthToken() && role !== "doctor") {
      this.$router.push("/sign-in");
    }
  },
  mounted() {
    try {
      const user = readStoredUser();
      const realComp = user.company_name || user.tenant_name || user.hospital_name || user.companyShortName || "";
      if (realComp) {
        this.tenantName = realComp;
        localStorage.setItem("tenant_name", realComp);
        localStorage.setItem("company_name", realComp);
      } else {
        const storedComp = localStorage.getItem("company_name") || localStorage.getItem("tenant_name") || "";
        this.tenantName =
          storedComp && !lower(storedComp).includes("metrotrust")
            ? storedComp
            : user.name && !lower(user.name).includes("admin") && !lower(user.name).includes("dr.")
              ? user.name
              : "Star Hospital";
      }

      if (this.isDoctor && user.role && typeof user.role === "object") {
        let rolePerms = user.role.permissions || user.role.modules || [];
        if (typeof rolePerms === "string") {
          try { rolePerms = JSON.parse(rolePerms); } catch (e) { rolePerms = [rolePerms]; }
        }
        if (Array.isArray(rolePerms) && rolePerms.length > 0) {
          const cleanPerms = rolePerms.map((p) => (typeof p === "object" && p !== null ? p.code || p.name || p.module_name || p.id : p));
          localStorage.setItem("allowedModules", JSON.stringify(cleanPerms));
          localStorage.setItem("userModules", JSON.stringify(rolePerms));
        }
      }
    } catch (e) {
      console.warn("Could not parse tenant user in mounted:", e);
    }

    // loadDoctorSession already fetches displays and tokens
    if (typeof window !== "undefined" && window.speechSynthesis && window.speechSynthesis.cancel) {
      window.speechSynthesis.cancel();
    }
    this.loadDoctorSession();

    try {
      const rawBase = process.env.VUE_APP_API_BASE_URL || window.location.origin;
      const parsedUrl = new URL(rawBase.startsWith("http") ? rawBase : window.location.origin);
      const socketUrl = process.env.VUE_APP_SOCKET_URL || `${parsedUrl.protocol}//${parsedUrl.host}`;

      this.socket = io(socketUrl, {
        auth: { token: getAuthToken() },
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 2000,
      });

      const handleLiveStatusSync = (payload) => {
        if (payload && (payload.status || payload.availability_status)) {
          // The window listener below applies the change (and refreshes tokens);
          // other components listening for this event get it too.
          window.dispatchEvent(new CustomEvent("doctor-status-synced", { detail: payload }));
        } else {
          this.fetchEmergencyTokens();
        }
      };

      const handleLiveShiftSync = (payload) => {
        if (payload && payload.is_shift_update) this.loadDoctorSession(true);
        else this.fetchEmergencyTokens();
      };

      this.socket.on("doctor_status_updated", handleLiveStatusSync);
      this.socket.on("doctor_availability_changed", handleLiveStatusSync);
      this.socket.on("counter_status_updated", handleLiveStatusSync);
      this.socket.on("doctor_shift_updated", handleLiveShiftSync);
      this.socket.on("token_updated", () => this.fetchEmergencyTokens());
      this.socket.on("token_created", () => this.fetchEmergencyTokens());
    } catch (sockInitErr) {
      console.warn("Could not initialize socket in MedicalDashboard:", sockInitErr);
    }

    this.timerInterval = setInterval(() => {
      this.currentTime = new Date();
    }, 1000);

    window.addEventListener("storage", this.handleStorageEvent);
    window.addEventListener("doctor-availability-changed", this.handleDoctorAvailabilityEvent);
    window.addEventListener("doctor-status-synced", this.handleDoctorStatusSynced);

    this.onTokenSyncHandler = () => {
      this.loadDoctorSession(true);
      this.fetchEmergencyTokens();
    };
    window.addEventListener("token-created", this.onTokenSyncHandler);
    window.addEventListener("tokens-updated", this.onTokenSyncHandler);
  },
  beforeUnmount() {
    if (this.timerInterval) clearInterval(this.timerInterval);
    if (this.socket) this.socket.disconnect();

    if (this.onTokenSyncHandler) {
      window.removeEventListener("token-created", this.onTokenSyncHandler);
      window.removeEventListener("tokens-updated", this.onTokenSyncHandler);
    }

    window.removeEventListener("storage", this.handleStorageEvent);
    window.removeEventListener("doctor-availability-changed", this.handleDoctorAvailabilityEvent);
    window.removeEventListener("doctor-status-synced", this.handleDoctorStatusSynced);
  },
  data() {
    const u = readStoredUser();
    const resolvedTenantName =
      u.company_name || u.tenant_name || u.hospital_name || u.companyShortName ||
      localStorage.getItem("company_name") || localStorage.getItem("tenant_name") || localStorage.getItem("companyName") || "";

    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const localTodayIso = `${y}-${m}-${day}`;

    return {
      tenantName: resolvedTenantName,
      activeTab: "dashboard",
      doctorFilter: "",
      activeEmergency: null,
      activeDoctorSession: null,
      currentTime: new Date(),
      timerInterval: null,
      socket: null,
      isFetchingSession: false,
      tokensRequest: null,
      cachedRoles: null,
      cachedBranches: null,
      doctors: [],
      displays: [],
      emergencyTokens: [],
      counters: [],
      branches: [],
      services: [],
      todayTokens: [],

      // Interactive counter drilldown
      drilldownSelectedCounter: null,
      isDrilldownCollapsed: false,
      drilldownCurrentPage: 1,
      drilldownPageSize: 6,
      drilldownSearchQuery: "",
      receptionistTokenView: "list",
      tokenBoardFullWidth: false,
      draggedToken: null,
      dragOverColumnId: null,
      drilldownColorPalette: ["#6ee7b7", "#9ca3af", "#f59e0b", "#3b82f6", "#ec4899", "#8b5cf6", "#14b8a6", "#f97316"],
      drilldownStatusLegend: [
        { key: "completed", label: "Completed", dot: "bg-success" },
        { key: "pending", label: "Pending", dot: "bg-warning" },
        { key: "skipped", label: "Skipped", dot: "bg-secondary" },
        { key: "rejected", label: "Rejected", dot: "bg-danger" },
      ],

      // Admin Dashboard state
      selectedDepartmentName: null,
      showAllDepartments: false,
      showAllDevices: false,
      adminDoctorStatusFilter: "all",
      adminStatusFilterOptions: [
        { key: "all", label: "All" },
        { key: "available", label: "Available" },
        { key: "busy", label: "Busy" },
        { key: "break", label: "Break" },
        { key: "offline", label: "Offline" },
      ],
      adminActiveTab: "overview",

      // Token flow analytics state
      tokenFlowDepartment: "all",
      tokenFlowDoctorId: "all",
      tokenFlowHour: "all",
      showHourlyDetails: false,
      tokenFlowDateFilter: "today",
      tokenFlowCustomDate: localTodayIso,
      tokenFlowDate: localTodayIso,
      historicalTokensCache: {},
      isFetchingHistoricalTokens: false,
      allTokensCache: [],
      selectedDoctorForTokens: null,
    };
  },
  computed: {
    todayLabel() {
      return this.currentTime.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    },
    visibleTokens() {
      return this.todayTokens.filter((t) => !this.isOtherTenant(t));
    },
    totalTokensCount() {
      return this.visibleTokens.length;
    },
    todayGenderStats() {
      const list = this.visibleTokens || [];
      let male = 0;
      let female = 0;
      let other = 0;
      list.forEach((t) => {
        const g = String(t.gender || t.sex || t.patient_gender || "").toLowerCase().trim();
        if (g === "male" || g === "m") male++;
        else if (g === "female" || g === "f") female++;
        else if (g) other++;
      });
      return { total: list.length, male, female, other };
    },
    activeTokensCount() {
      return this.visibleTokens.filter((t) => !["completed", "rejected"].includes(lower(t.status))).length;
    },
    completedTokensCount() {
      return this.visibleTokens.filter((t) => lower(t.status) === "completed").length;
    },
    skippedTokensCount() {
      return this.visibleTokens.filter(
        (t) =>
          lower(t.status) === "skipped" ||
          t.is_skipped === true ||
          t.is_skipped === 1 ||
          String(t.is_skipped || "").toLowerCase() === "true" ||
          (Array.isArray(this.localSkippedTokenIds) && this.localSkippedTokenIds.includes(String(t.id)))
      ).length;
    },
    totalCountersCount() {
      return this.counters.length;
    },
    activeCountersCount() {
      return this.counters.filter((c) => lower(c.status) === "active" || c.status === 1).length;
    },
    onlineDisplaysCount() {
      return this.displays.filter((d) => d.online).length;
    },
    kpiCards() {
      const online = this.onlineDisplaysCount;
      const totalScreens = this.displays.length;
      const waiting = this.waitingTokensCount;
      return [
        {
          key: "tokens",
          label: "Total Patients",
          value: this.totalTokensCount,
          icon: "fas fa-users fa-lg",
          tone: "tone-teal",
          sub: `${this.todayGenderStats.male} Male · ${this.todayGenderStats.female} Female`,
          subClass: "text-success",
        },
        {
          key: "counters",
          label: "OPD counters",
          value: this.totalCountersCount,
          icon: "fas fa-door-open fa-lg",
          tone: "tone-blue",
          sub: `${this.activeCountersCount} rooms active`,
          subClass: "text-success",
        },
        {
          key: "displays",
          label: "Display screens",
          value: online,
          total: totalScreens,
          icon: "fas fa-tv fa-lg",
          tone: "tone-amber",
          sub: totalScreens > 0 ? `${online} connected` : "No screens online",
          subClass: online > 0 ? "text-success" : "text-danger",
        },
        {
          key: "waiting",
          label: "Waiting Patients",
          value: waiting,
          icon: "fas fa-user-clock fa-lg",
          tone: "tone-pink",
          sub: waiting > 0 ? `${waiting} ${waiting === 1 ? 'patient' : 'patients'} waiting` : "No patients waiting",
          subClass: waiting > 0 ? "text-pink" : "text-muted",
        },
      ];
    },
    currentTenantId() {
      const u = readStoredUser();
      const raw =
        u.tenant_id ??
        u.tenantId ??
        u.tenant?.id ??
        u.Tenant?.id ??
        localStorage.getItem("tenant_id") ??
        localStorage.getItem("tenantId") ??
        null;
      if (raw === null || raw === undefined) return null;
      const s = String(raw).trim();
      if (!s || s === "null" || s === "undefined" || s === "0" || s === "NaN") return null;
      return s;
    },
    bookingTypesTotalCount() {
      return (this.visibleTokens || []).length;
    },
    bookingTypeOptionsList() {
      const tokens = this.visibleTokens || [];
      const total = tokens.length;

      const counts = {
        "Walk-in": 0,
        "Appointment": 0,
        "Online": 0,
        "Emergency": 0,
        "VIP": 0,
        "Teleconsultation": 0,
      };

      tokens.forEach((t) => {
        const bt = String(t.booking_type || t.type || "").trim().toLowerCase();
        if (bt.includes("appointment")) {
          counts["Appointment"]++;
        } else if (bt.includes("online")) {
          counts["Online"]++;
        } else if (bt.includes("teleconsult")) {
          counts["Teleconsultation"]++;
        } else if (bt.includes("vip")) {
          counts["VIP"]++;
        } else if (bt.includes("emerg") || t.is_emergency) {
          counts["Emergency"]++;
        } else {
          counts["Walk-in"]++;
        }
      });

      const colors = {
        "Walk-in": "#0d9488",
        "Appointment": "#2563eb",
        "Online": "#8b5cf6",
        "Emergency": "#ef4444",
        "VIP": "#f59e0b",
        "Teleconsultation": "#06b6d4",
      };

      const keys = ["Walk-in", "Appointment", "Online", "Emergency", "VIP", "Teleconsultation"];

      if (total > 0) {
        return keys.map((key) => {
          const count = counts[key] || 0;
          const pct = Math.round((count / total) * 100);
          return {
            key,
            label: key,
            count,
            pct,
            color: colors[key],
          };
        });
      }

      // Default baseline when demo / empty
      const demoPcts = {
        "Walk-in": 39,
        "Appointment": 35,
        "Online": 14,
        "Emergency": 4,
        "VIP": 4,
        "Teleconsultation": 4,
      };

      return keys.map((key) => ({
        key,
        label: key,
        count: 0,
        pct: demoPcts[key],
        color: colors[key],
      }));
    },
    adminKpiCards() {
      const onlineScreens = this.adminOnlineDevicesCount;
      const totalScreens = this.adminDisplayDevices.length;
      const activeDocs = this.activeDoctorsCount;
      const totalDocs = this.allAdminDoctors.length;
      const totalPatients = this.totalTokensCount;
      const activePatients = this.activeTokensCount;
      const completedPatients = this.completedTokensCount;

      return [
        {
          key: "patients",
          label: "Total Patients",
          value: totalPatients,
          icon: "fas fa-users fa-lg",
          tone: "tone-teal",
          sub: `${this.todayGenderStats.male} Male · ${this.todayGenderStats.female} Female · ${activePatients} active · ${completedPatients} done`,
          subClass: "text-teal",
        },
        {
          key: "departments",
          label: "Total Departments",
          value: this.departmentSummaryList.length,
          icon: "fas fa-clinic-medical fa-lg",
          tone: "tone-blue",
          sub: `${this.activeDepartmentsCount} active on duty`,
          subClass: "text-success",
        },
        {
          key: "devices",
          label: "Display screens",
          value: onlineScreens,
          total: totalScreens,
          icon: "fas fa-tv fa-lg",
          tone: "tone-amber",
          sub: totalScreens > 0 ? `${onlineScreens} of ${totalScreens} online` : "No screens online",
          subClass: onlineScreens > 0 && onlineScreens === totalScreens ? "text-success" : "text-warning",
        },
        {
          key: "doctors",
          label: "Doctors on duty",
          value: activeDocs,
          total: totalDocs,
          icon: "fas fa-user-md fa-lg",
          tone: "tone-teal",
          sub: `${activeDocs} of ${totalDocs} on duty now`,
          subClass: "text-success",
        },
      ];
    },
    departmentSummaryList() {
      const deptMap = new Map();

      const getOrCreateDept = (rawName) => {
        const name = String(rawName || "").trim();
        if (!name || name === "--" || lower(name) === "undefined" || lower(name) === "null") return null;
        const key = name.toLowerCase();
        if (!deptMap.has(key)) {
          deptMap.set(key, {
            name,
            doctors: new Set(),
            onDutyDoctors: new Set(),
            waitingTokens: 0,
            servingTokens: 0,
            completedTokens: 0,
            shifts: [],
          });
        }
        return deptMap.get(key);
      };

      const findExistingDept = (rawName) => {
        if (!rawName) return null;
        const key = String(rawName).trim().toLowerCase();
        if (!key) return null;
        if (deptMap.has(key)) return deptMap.get(key);
        for (const [k, dept] of deptMap.entries()) {
          if (k === key || k.includes(key) || key.includes(k)) {
            return dept;
          }
        }
        return null;
      };

      // 1. Strictly register ONLY this tenant admin's actual Departments (from Branches / Departments module)
      const tenantBranches = (this.branches || []).filter((b) => !this.isOtherTenant(b));
      tenantBranches.forEach((b) => {
        const name = (b.branch_name || b.department_name || b.name || b.title || "").trim();
        if (name && name !== "--") {
          getOrCreateDept(name);
        }
      });

      // 2. Fallback: if no branches registered yet, check tenant's doctors for genuine department names (excluding counters & rooms)
      if (deptMap.size === 0) {
        this.allAdminDoctors.forEach((doc) => {
          const dept = (doc.department || doc.speciality || "").trim();
          if (dept && dept !== "--" && !lower(dept).includes("counter") && !lower(dept).includes("room")) {
            getOrCreateDept(dept);
          }
        });
      }

      if (deptMap.size === 0) {
        return [];
      }

      // 3. Group tenant's doctors into the registered departments
      this.allAdminDoctors.forEach((doc) => {
        for (const dept of deptMap.values()) {
          if (this.isDoctorInDepartment(doc.raw || doc, dept.name)) {
            const docKey = String(doc.id || doc.name);
            dept.doctors.add(docKey);
            const st = String(doc.status || "").toLowerCase();
            if (st.includes("available") || st.includes("busy") || st.includes("break")) {
              dept.onDutyDoctors.add(docKey);
            }
            const shiftStr = doc.raw?.shift || doc.shift;
            if (shiftStr && shiftStr !== "--") {
              dept.shifts.push(shiftStr);
            }
          }
        }
      });

      // 3b. Also ensure doctors assigned in Department module (branch.doctor_ids / doctors) are included directly
      tenantBranches.forEach((b) => {
        const bName = (b.branch_name || b.department_name || b.name || b.title || "").trim();
        const dept = deptMap.get(bName.toLowerCase());
        if (!dept) return;
        const bDocIds = this.getBranchDoctorIds(b);
        bDocIds.forEach((id) => {
          dept.doctors.add(String(id));
          const doc = this.allAdminDoctors.find((d) => String(d.id || d.user_id) === String(id));
          if (doc) {
            const st = String(doc.status || "").toLowerCase();
            if (st.includes("available") || st.includes("busy") || st.includes("break")) {
              dept.onDutyDoctors.add(String(id));
            }
          }
        });
      });

      // 4. Match tenant's today tokens to the registered departments (never create new departments from tokens or counters)
      const counterBranchMap = new Map();
      (this.counters || []).forEach((c) => {
        if (this.isOtherTenant(c)) return;
        const bName = c.branch?.branch_name || c.branch_name || c.speciality || c.department || "";
        if (bName) counterBranchMap.set(String(c.id), bName);
        if (c.name && bName) counterBranchMap.set(lower(c.name), bName);
      });

      (this.todayTokens || []).forEach((t) => {
        if (this.isOtherTenant(t)) return;

        let targetDeptName = t.department || t.speciality || t.branch_name || t.service?.branch_name || "";
        if (!targetDeptName && t.counter_id && counterBranchMap.has(String(t.counter_id))) {
          targetDeptName = counterBranchMap.get(String(t.counter_id));
        }
        if (!targetDeptName && t.counter_name && counterBranchMap.has(lower(t.counter_name))) {
          targetDeptName = counterBranchMap.get(lower(t.counter_name));
        }

        let bucket = findExistingDept(targetDeptName);
        if (!bucket && deptMap.size === 1) {
          bucket = Array.from(deptMap.values())[0];
        }
        if (!bucket) return;

        const st = String(t.status || "").toLowerCase();
        if (QUEUE_NEXT_STATUSES.includes(st)) {
          bucket.waitingTokens++;
        } else if (QUEUE_NOW_STATUSES.includes(st)) {
          bucket.servingTokens++;
        } else if (st === "completed" || st === "done") {
          bucket.completedTokens++;
        }
      });

      const palette = ["teal", "amber", "teal", "red", "amber", "teal"];
      let idx = 0;

      return Array.from(deptMap.values()).map((dept) => {
        const totalDocs = dept.doctors.size;
        const onDuty = dept.onDutyDoctors.size;
        const pct = totalDocs > 0 ? Math.round((onDuty / totalDocs) * 100) : 0;
        const waiting = dept.waitingTokens;

        let waitTime = "";
        let isNoDoctor = false;

        if (totalDocs > 0 && onDuty === 0 && waiting > 0) {
          isNoDoctor = true;
          waitTime = "no doctor";
        } else if (waiting === 0) {
          waitTime = "wait 0 min";
        } else {
          const waitMins = Math.max(3, waiting * 5);
          waitTime = `wait ${waitMins} min`;
        }

        let accent = "teal";
        if (isNoDoctor) {
          accent = "red";
        } else if (pct < 100 || onDuty === 0) {
          accent = "amber";
        } else {
          accent = palette[idx % palette.length];
        }
        idx++;

        let dutySubtitle = `${onDuty} of ${totalDocs} doctors on duty`;
        if (dept.shifts.length > 0 && onDuty === 1 && totalDocs === 1) {
          const shiftEnd = dept.shifts[0].split("-")[1]?.trim() || dept.shifts[0];
          dutySubtitle = `1 of 1 · ends at ${shiftEnd}`;
        }

        return {
          name: dept.name,
          doctorsOnDuty: onDuty,
          totalDoctors: totalDocs,
          percent: pct,
          dutySubtitle,
          waitingCount: waiting,
          waitTime,
          isNoDoctor,
          accent,
        };
      });
    },
    visibleDepartmentsList() {
      if (this.showAllDepartments) {
        return this.departmentSummaryList;
      }
      return this.departmentSummaryList.slice(0, 6);
    },
    activeDepartmentsCount() {
      return this.departmentSummaryList.filter((d) => d.doctorsOnDuty > 0).length;
    },
    activeDepartmentName() {
      return this.selectedDepartmentName || "";
    },
    departmentDoctors() {
      if (!this.selectedDepartmentName || !Array.isArray(this.doctors)) return [];
      const tenantDoctors = this.doctors.filter((d) => this.isDoctorUser(d) && !this.isOtherTenant(d));
      return tenantDoctors.filter((doc) => this.isDoctorInDepartment(doc, this.selectedDepartmentName));
    },
    filteredDepartmentDoctors() {
      const list = this.departmentDoctors;
      const filter = String(this.adminDoctorStatusFilter || "all").toLowerCase();
      let res = list;
      if (filter !== "all") {
        res = res.filter((doc) => {
          const s = String(doc.status || "").toLowerCase();
          if (filter === "available") return s.includes("available");
          if (filter === "busy") return s.includes("busy");
          if (filter === "break") return s.includes("break");
          if (filter === "offline") return s.includes("offline");
          return true;
        });
      }
      const q = (this.doctorFilter || "").toLowerCase().trim();
      if (!q) return res;
      return res.filter((doc) => {
        const name = String(doc.doctor_name || doc.name || "").toLowerCase();
        const spec = String(doc.speciality || doc.department || "").toLowerCase();
        const room = String(doc.room || doc.counter_name || "").toLowerCase();
        return name.includes(q) || spec.includes(q) || room.includes(q);
      });
    },
    allAdminDoctors() {
      if (!Array.isArray(this.doctors)) return [];

      const tenantDoctors = this.doctors.filter((d) => this.isDoctorUser(d) && !this.isOtherTenant(d));

      return tenantDoctors.map((doc, idx) => {
        const rawName = doc.doctor_name || doc.name || `Doctor ${idx + 1}`;
        const name = formatDoctorName(rawName);

        // Find which registered department this doctor actually belongs to:
        let matchedDeptName = "";
        const docId = String(doc.id || doc.user_id || "");

        // 1. Check explicit branch assignments (doctor_ids / doctors / user_id)
        if (docId) {
          const assignedBranch = (this.branches || []).find((b) => {
            if (this.isOtherTenant(b)) return false;
            return this.getBranchDoctorIds(b).includes(docId);
          });
          if (assignedBranch) {
            matchedDeptName = assignedBranch.branch_name || assignedBranch.department_name || assignedBranch.name || "";
          }
        }

        // 2. Check branch_id / dept_id
        if (!matchedDeptName && (doc.branch_id || doc.dept_id)) {
          const bId = String(doc.branch_id || doc.dept_id);
          const assignedBranch = (this.branches || []).find((b) => String(b.id) === bId);
          if (assignedBranch) {
            matchedDeptName = assignedBranch.branch_name || assignedBranch.department_name || assignedBranch.name || "";
          }
        }

        // 3. Check speciality / department string
        if (!matchedDeptName) {
          const rawDept = String(doc.speciality || doc.department || doc.department_name || doc.raw?.speciality || doc.raw?.department || "").trim();
          if (rawDept && rawDept !== "--") {
            const matchedB = (this.branches || []).find((b) => {
              const bName = String(b.branch_name || b.name || "").trim().toLowerCase();
              return bName && (bName === rawDept.toLowerCase() || bName.includes(rawDept.toLowerCase()) || rawDept.toLowerCase().includes(bName));
            });
            matchedDeptName = matchedB ? (matchedB.branch_name || matchedB.name) : rawDept;
          }
        }

        // 4. Fallback: if only 1 branch exists, use that branch; otherwise 'General OPD'
        if (!matchedDeptName) {
          if (this.branches && this.branches.length === 1) {
            matchedDeptName = this.branches[0].branch_name || this.branches[0].name || "Medical department";
          } else {
            matchedDeptName = "General OPD";
          }
        }

        const room = doc.room || doc.counter_name || (doc.counter ? doc.counter.name : "");
        const hasNoRoom = !room || room === "No room" || room === "--";
        const status = doc.availability_status || doc.status || "Available";

        return {
          id: doc.id || doc.user_id || `tenant-doc-${idx}`,
          name,
          department: matchedDeptName,
          room: hasNoRoom ? "No room" : room,
          hasNoRoom,
          status,
          queue: this.getDoctorQueueCount(doc),
          utilization: this.getDoctorUtilization(doc),
          raw: doc,
        };
      });
    },
    filteredAdminDoctors() {
      const filter = String(this.adminDoctorStatusFilter || "all").toLowerCase();
      if (filter === "all") return this.allAdminDoctors;
      return this.allAdminDoctors.filter((doc) => {
        const s = String(doc.status || "").toLowerCase();
        if (filter === "available") return s.includes("available");
        if (filter === "busy") return s.includes("busy");
        if (filter === "break") return s.includes("break");
        if (filter === "offline") return s.includes("offline");
        return true;
      });
    },
    activeDoctorsCount() {
      return this.allAdminDoctors.filter((d) => {
        const s = String(d.status || "").toLowerCase();
        return s.includes("available") || s.includes("busy");
      }).length;
    },
    adminDisplayDevices() {
      if (!Array.isArray(this.displays)) return [];
      return this.displays
        .filter((d) => !this.isOtherTenant(d))
        .map((d, idx) => {
          const isOnline = Boolean(d.online || lower(d.status) === "online");
          const hasRoom = Boolean(d.location && d.location !== "Unassigned" && d.location !== "--");
          let statusText = isOnline ? "Online" : "Offline";
          if (!hasRoom && isOnline) {
            statusText = "Unassigned";
          }
          const isProblem = !isOnline || statusText === "Unassigned";

          return {
            id: d.id || idx + 1,
            name: d.name || `Display TV #${d.id || idx + 1}`,
            location: !isOnline
              ? (d.location ? `${d.location} · Offline` : "Offline")
              : (d.location || "Not assigned to a room"),
            status: statusText,
            online: isOnline,
            isProblem,
          };
        });
    },
    adminProblemDevices() {
      return this.adminDisplayDevices.filter((d) => d.isProblem);
    },
    visibleAdminDevices() {
      if (this.showAllDevices) {
        return this.adminDisplayDevices;
      }
      return this.adminProblemDevices;
    },
    adminOnlineDevicesCount() {
      return this.adminDisplayDevices.filter((d) => d.online).length;
    },
    deviceOnlinePercent() {
      const total = this.adminDisplayDevices.length;
      if (!total) return 0;
      return Math.round((this.adminOnlineDevicesCount / total) * 100);
    },
    waitingTokensList() {
      return this.visibleTokens.filter((t) => QUEUE_NEXT_STATUSES.includes(lower(t.status)));
    },
    waitingTokensCount() {
      return this.waitingTokensList.length;
    },
    hasEmergencyTokens() {
      return this.visibleTokens.some((t) =>
        (t.is_emergency === true || t.is_emergency === 1 || t.is_emergency === "true" || String(t.is_emergency || "").toLowerCase() === "yes") &&
        ["pending", "waiting"].includes(String(t.status || "").toLowerCase())
      );
    },
    emergencyPendingTokens() {
      return this.visibleTokens.filter((t) =>
        (t.is_emergency === true || t.is_emergency === 1 || t.is_emergency === "true" || String(t.is_emergency || "").toLowerCase() === "yes") &&
        ["pending", "waiting"].includes(String(t.status || "").toLowerCase())
      );
    },
    resolvedHospitalName() {
      const user = readStoredUser();
      if (user.company_name) return user.company_name;
      if (user.tenant_name) return user.tenant_name;
      if (user.hospital_name) return user.hospital_name;
      if (user.companyShortName) return user.companyShortName;
      if (user.name && !lower(user.name).includes("admin") && !lower(user.name).includes("dr.")) return user.name;

      const c = localStorage.getItem("company_name") || localStorage.getItem("tenant_name");
      if (c && !lower(c).includes("metrotrust")) return c;
      return this.tenantName && !lower(this.tenantName).includes("metrotrust") ? this.tenantName : "Star Hospital";
    },
    currentUserRole() {
      const u = readStoredUser();
      const rolesArray = Array.isArray(u.roles) ? u.roles.map((x) => x.name || x.slug).join(" ") : "";
      return cleanRoleKey(
        localStorage.getItem("userRole") ||
        localStorage.getItem("role") ||
        localStorage.getItem("role_name") ||
        u.role_name ||
        u.role_slug ||
        rolesArray ||
        (u.role && (u.role.name || u.role.slug)) ||
        u.role ||
        ""
      );
    },
    isReceptionist() {
      const r = this.currentUserRole;
      return (
        r.includes("receptionist") ||
        r.includes("reception") ||
        r.includes("tokenmanager") ||
        r.includes("tokenmanger") ||
        r.includes("desk") ||
        r.includes("frontoffice") ||
        r.includes("frontdesk") ||
        r.includes("registration") ||
        r.includes("respt") ||
        r.includes("nurse")
      );
    },
    isDoctor() {
      return detectIsDoctor();
    },
    isAdminOrSuperAdmin() {
      if (this.isDoctor) return false;
      const r = this.currentUserRole;
      const userType = Number(readStoredUser().type || 0);
      return (
        userType === 1 ||
        hasAny(r, ["superadmin", "admin", "tenant", "manager", "receptionist", "reception", "nurse", "tokenmanager", "tokenmanger"]) ||
        r === "1"
      );
    },
    boardCounters() {
      const list = [...(this.counters || [])];
      return list.sort((a, b) => {
        const countA = this.getTokensForCounter(a).length;
        const countB = this.getTokensForCounter(b).length;
        return countB - countA;
      });
    },
    filteredDoctors() {
      const list = this.doctors.filter((d) => this.isDoctorUser(d));
      if (this.doctorFilter) {
        const q = lower(this.doctorFilter);
        return list.filter(
          (d) => lower(d.name).includes(q) || lower(d.speciality).includes(q) || (d.room && lower(d.room).includes(q))
        );
      }
      return list;
    },
    drilldownCounterTokenMap() {
      const map = new Map();
      (this.counters || []).forEach((c) => {
        const key = String(c.id);
        map.set(key, []);
      });

      const allToks = this.todayTokens && this.todayTokens.length ? this.todayTokens : (this.visibleTokens || []);

      allToks.forEach((tok) => {
        const tokCounterId = tok.counter_id !== undefined && tok.counter_id !== null ? String(tok.counter_id) : (tok.counter && tok.counter.id ? String(tok.counter.id) : null);
        const tokCounterName = lower(tok.counter_name || (tok.counter ? tok.counter.name : "")).trim();
        const tokAtt = lower(tok.token_attended || "").trim();
        const visitedList = Array.isArray(tok.visited_counters) ? tok.visited_counters : [];

        (this.counters || []).forEach((c) => {
          const cKey = String(c.id);
          const cName = lower(c.name || "").trim();
          const cAtt = lower((c.user && c.user.name) || c.attendant_name || "").trim();

          let isMatch = false;
          if (tokCounterId && tokCounterId === cKey) {
            isMatch = true;
          } else if (cName && tokCounterName && (cName === tokCounterName || cName.includes(tokCounterName) || tokCounterName.includes(cName))) {
            isMatch = true;
          } else if (cAtt && tokAtt && tokAtt !== "no" && (cAtt.includes(tokAtt) || tokAtt.includes(cAtt))) {
            isMatch = true;
          } else if (visitedList.some((v) => String(v.counter_id) === cKey || lower(v.counter_name || "").trim() === cName)) {
            isMatch = true;
          }

          if (isMatch) {
            const list = map.get(cKey) || [];
            if (!list.some((x) => String(x.id) === String(tok.id))) {
              list.push(tok);
              map.set(cKey, list);
            }
          }
        });
      });
      return map;
    },
    drilldownLegendItems() {
      return this.counters.map((c, idx) => ({
        counter: c,
        count: (this.drilldownCounterTokenMap.get(String(c.id)) || []).length,
        color: this.drilldownColorPalette[idx % this.drilldownColorPalette.length],
      }));
    },
    drilldownCounterSlices() {
      const items = this.drilldownLegendItems;
      const totalTokens = items.reduce((sum, item) => sum + item.count, 0);

      if (totalTokens === 0) {
        const slicePct = 100 / Math.max(1, items.length);
        let offset = 25;
        return items.map((item) => {
          const res = {
            counter: item.counter,
            count: 0,
            color: "#e2e8f0",
            dashLength: Math.max(0.5, slicePct - 1),
            dashOffset: -offset,
          };
          offset += slicePct;
          return res;
        });
      }

      let accumulatedPct = 0;
      return items.map((item) => {
        const pct = (item.count / totalTokens) * 100;
        const slice = {
          counter: item.counter,
          count: item.count,
          color: item.color,
          dashLength: pct > 0 ? Math.max(0.5, pct - 0.5) : 0,
          dashOffset: -(accumulatedPct - 25),
        };
        accumulatedPct += pct;
        return slice;
      });
    },
    drilldownCounterTokens() {
      if (!this.drilldownSelectedCounter) {
        return this.todayTokens && this.todayTokens.length ? this.todayTokens : (this.visibleTokens || []);
      }
      return this.drilldownCounterTokenMap.get(String(this.drilldownSelectedCounter.id)) || [];
    },
    drilldownFilteredTokens() {
      let list = this.drilldownCounterTokens;
      if (this.drilldownSearchQuery) {
        const q = lower(this.drilldownSearchQuery).trim();
        list = list.filter((t) => {
          const num = lower(t.token_number);
          const name = lower(t.token_name);
          const phone = lower(t.phone_number || t.phone);
          const serv = lower(t.service?.name || t.service_name);
          const status = lower(t.status);
          const doc = lower(t.token_attended || t.doctor_name);
          return num.includes(q) || name.includes(q) || phone.includes(q) || serv.includes(q) || status.includes(q) || doc.includes(q);
        });
      }
      return list;
    },
    drilldownTotalPages() {
      return Math.max(1, Math.ceil(this.drilldownFilteredTokens.length / this.drilldownPageSize));
    },
    paginatedDrilldownTokens() {
      const start = (this.drilldownCurrentPage - 1) * this.drilldownPageSize;
      return this.drilldownFilteredTokens.slice(start, start + this.drilldownPageSize);
    },
    drilldownGenderStats() {
      const toks = this.drilldownFilteredTokens;
      let male = 0;
      let female = 0;
      let other = 0;
      toks.forEach((t) => {
        const g = String(t.gender || t.sex || t.patient_gender || t.user_gender || "").toLowerCase().trim();
        if (g === "male" || g === "m") male++;
        else if (g === "female" || g === "f") female++;
        else if (g) other++;
      });
      return { total: toks.length, male, female, other };
    },
    drilldownStatusCounts() {
      const toks = this.drilldownCounterTokens;
      const count = (list) => toks.filter((t) => list.includes(lower(t.status))).length;
      return {
        completed: count(["completed"]),
        pending: count(["pending", "waiting", "calling", "serving", "processing"]),
        skipped: count(["skipped"]),
        rejected: count(["rejected"]),
      };
    },
    drilldownStatusSlices() {
      const sc = this.drilldownStatusCounts;
      const total = sc.completed + sc.pending + sc.skipped + sc.rejected;

      const slicesConfig = [
        { name: "Completed", count: sc.completed, color: "#22c55e" },
        { name: "Pending", count: sc.pending, color: "#f59e0b" },
        { name: "Skipped", count: sc.skipped, color: "#94a3b8" },
        { name: "Rejected", count: sc.rejected, color: "#ef4444" },
      ];

      if (total === 0) {
        return [{ name: "Empty", count: 0, color: "#cbd5e1", dashLength: 100, dashOffset: 25 }];
      }

      let accumulatedPct = 0;
      return slicesConfig.map((scItem) => {
        const pct = (scItem.count / total) * 100;
        const res = {
          ...scItem,
          dashLength: pct > 0 ? Math.max(0.5, pct - 0.5) : 0,
          dashOffset: -(accumulatedPct - 25),
        };
        accumulatedPct += pct;
        return res;
      });
    },
    todayDateIso() {
      const d = new Date();
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    yesterdayDateIso() {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    resolvedTokenFlowDate() {
      if (this.tokenFlowDateFilter === "today") return this.todayDateIso;
      if (this.tokenFlowDateFilter === "yesterday") return this.yesterdayDateIso;
      if (this.tokenFlowDateFilter === "custom") return this.tokenFlowCustomDate || this.todayDateIso;
      if (this.tokenFlowDateFilter === "all") return "all";
      return this.todayDateIso;
    },
    tokenFlowDateLabel() {
      if (this.tokenFlowDateFilter === "today") return "Today";
      if (this.tokenFlowDateFilter === "yesterday") return "Yesterday";
      if (this.tokenFlowDateFilter === "custom") return this.tokenFlowCustomDate || "Custom";
      if (this.tokenFlowDateFilter === "all") return "All Dates";
      return "Today";
    },
    isTokenFlowToday() {
      return this.resolvedTokenFlowDate === this.todayDateIso;
    },
    formattedTokenFlowDate() {
      if (!this.tokenFlowDate) return "";
      try {
        const parts = this.tokenFlowDate.split("-");
        if (parts.length === 3) {
          const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
          return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
        }
      } catch (e) {
        // fallback
      }
      return this.tokenFlowDate;
    },
    tokenFlowDoctorOptions() {
      if (!Array.isArray(this.allAdminDoctors)) return [];
      if (!this.tokenFlowDepartment || this.tokenFlowDepartment === "all") {
        return this.allAdminDoctors;
      }
      return this.allAdminDoctors.filter((doc) => {
        return this.isDoctorInDepartment(doc.raw || doc, this.tokenFlowDepartment);
      });
    },
    selectedDoctorObject() {
      if (!this.tokenFlowDoctorId || this.tokenFlowDoctorId === "all") return null;
      const docId = String(this.tokenFlowDoctorId);
      return (
        (this.filteredDepartmentDoctors || []).find((d) => String(d.id) === docId || String(d.user_id) === docId) ||
        (this.allAdminDoctors || []).find((d) => String(d.id) === docId || String(d.user_id) === docId || String(d.raw?.id) === docId || String(d.raw?.user_id) === docId) ||
        (this.doctors || []).find((d) => String(d.id) === docId || String(d.user_id) === docId) ||
        this.selectedDoctorForTokens
      );
    },
    selectedDoctorName() {
      const doc = this.selectedDoctorObject;
      return doc ? (doc.doctor_name || doc.name) : "";
    },
    selectedDoctorCounterName() {
      const doc = this.selectedDoctorObject;
      if (!doc) return "";
      return this.getDoctorCounterName(doc.raw || doc);
    },
    selectedDoctorDepartment() {
      const doc = this.selectedDoctorObject;
      if (!doc) return "";
      return this.getDoctorDepartmentDisplay(doc.raw || doc);
    },
    selectedDoctorStatus() {
      const doc = this.selectedDoctorObject;
      if (!doc) return "Available";
      return doc.status || doc.availability_status || "Available";
    },
    selectedDoctorBreakStart() {
      const doc = this.selectedDoctorObject;
      return doc ? (doc.break_start || doc.breakStart || '13:00') : '';
    },
    selectedDoctorBreakEnd() {
      const doc = this.selectedDoctorObject;
      return doc ? (doc.break_end || doc.breakEnd || '14:00') : '';
    },
    displayedTokenFlowTokens() {
      if (this.tokenFlowHour !== "all" && this.tokenFlowHour !== null && this.tokenFlowHour !== undefined) {
        return this.selectedHourTokens;
      }
      return this.filteredTokenFlowTokens;
    },
    tokenFlowSourceTokens() {
      // Pool all available tokens so no doctor token is missed by date or cache boundaries
      const tokenMap = new Map();
      (this.todayTokens || []).forEach((t) => { if (t && t.id && !this.isOtherTenant(t)) tokenMap.set(String(t.id), t); });
      (this.visibleTokens || []).forEach((t) => { if (t && t.id && !this.isOtherTenant(t)) tokenMap.set(String(t.id), t); });
      (this.calendarFetchedTokens || []).forEach((t) => { if (t && t.id && !this.isOtherTenant(t)) tokenMap.set(String(t.id), t); });
      if (this.historicalTokensCache) {
        Object.values(this.historicalTokensCache).forEach((list) => {
          if (Array.isArray(list)) {
            list.forEach((t) => { if (t && t.id && !this.isOtherTenant(t)) tokenMap.set(String(t.id), t); });
          }
        });
      }
      if (Array.isArray(this.allTokensCache)) {
        this.allTokensCache.forEach((t) => { if (t && t.id && !this.isOtherTenant(t)) tokenMap.set(String(t.id), t); });
      }
      return Array.from(tokenMap.values());
    },
    departmentPiePalette() {
      return [
        "#0d9488",
        "#2563eb",
        "#f59e0b",
        "#8b5cf6",
        "#ec4899",
        "#10b981",
        "#06b6d4",
        "#f97316",
        "#6366f1",
        "#ef4444",
      ];
    },
    departmentBarData() {
      const list = this.departmentSummaryList || [];
      if (!list.length) return [];

      const rawBars = list.map((dept, idx) => {
        const docCount = Number(dept.totalDoctors !== undefined ? dept.totalDoctors : (dept.doctors ? dept.doctors.size : 0));
        const waitingCount = Number(dept.waitingCount || 0);
        return {
          name: dept.name,
          dept,
          records: docCount,
          waitingCount,
          docCount,
          color: this.departmentPiePalette[idx % this.departmentPiePalette.length],
        };
      });

      const maxRecords = Math.max(...rawBars.map((b) => b.records), 1);

      return rawBars.map((bar) => {
        const rawPct = (bar.records / maxRecords) * 100;
        const heightPct = Math.min(100, Math.max(10, Math.round(rawPct)));
        return {
          ...bar,
          maxRecords,
          heightPct,
        };
      });
    },
    departmentPieSlices() {
      const list = this.departmentSummaryList || [];
      if (!list.length) return [];

      const weights = list.map((d) => {
        const tokenCount = Number(d.waitingCount || d.waitingTokens || 0);
        const docCount = Number(d.totalDoctors !== undefined ? d.totalDoctors : (d.doctors ? d.doctors.size : 1));
        return {
          name: d.name,
          dept: d,
          tokenCount,
          docCount,
          weight: Math.max(1, tokenCount > 0 ? tokenCount : docCount),
        };
      });

      const totalWeight = weights.reduce((sum, item) => sum + item.weight, 0);
      let accumulatedPct = 0;

      return weights.map((item, idx) => {
        const pct = totalWeight > 0 ? (item.weight / totalWeight) * 100 : 100 / weights.length;
        const color = this.departmentPiePalette[idx % this.departmentPiePalette.length];
        const slice = {
          name: item.name,
          dept: item.dept,
          color,
          tokenCount: item.tokenCount,
          docCount: item.docCount,
          pct: Math.round(pct),
          dashLength: pct > 0 ? Math.max(0.5, pct - 0.5) : 0,
          dashOffset: -(accumulatedPct - 25),
        };
        accumulatedPct += pct;
        return slice;
      });
    },
    filteredTokenFlowTokens() {
      let list = this.tokenFlowSourceTokens;

      // Filter by booking date
      if (this.resolvedTokenFlowDate && this.resolvedTokenFlowDate !== "all") {
        const targetDate = this.resolvedTokenFlowDate;
        list = list.filter((t) => this.tokenMatchesBookingDate(t, targetDate));
      }

      // Resolve selected doctor object
      const selectedDoc = this.selectedDoctorObject || this.selectedDoctorForTokens;

      // Filter by doctor if selected
      if (selectedDoc) {
        list = list.filter((t) => this.isTokenMatchingDoctor(t, selectedDoc));
      } else {
        // Filter by department only if no specific doctor selected
        if (this.tokenFlowDepartment && this.tokenFlowDepartment !== "all") {
          list = list.filter((t) => this.isTokenMatchingDepartment(t, this.tokenFlowDepartment));
        }
        if (this.tokenFlowDoctorId && this.tokenFlowDoctorId !== "all") {
          const docId = String(this.tokenFlowDoctorId);
          const foundDoc =
            (this.allAdminDoctors || []).find((d) => String(d.id) === docId || String(d.user_id) === docId || String(d.raw?.id) === docId || String(d.raw?.user_id) === docId) ||
            (this.doctors || []).find((d) => String(d.id) === docId || String(d.user_id) === docId);
          if (foundDoc) {
            list = list.filter((t) => this.isTokenMatchingDoctor(t, foundDoc));
          }
        }
      }

      return list;
    },
    tokenFlowHourOptions() {
      return [
        { hour: 8, label: "8", timeRange: "8 to 9 AM" },
        { hour: 9, label: "9", timeRange: "9 to 10 AM" },
        { hour: 10, label: "10", timeRange: "10 to 11 AM" },
        { hour: 11, label: "11", timeRange: "11 AM to 12 PM" },
        { hour: 12, label: "12", timeRange: "12 to 1 PM" },
        { hour: 13, label: "1", timeRange: "1 to 2 PM" },
        { hour: 14, label: "2", timeRange: "2 to 3 PM" },
        { hour: 15, label: "3", timeRange: "3 to 4 PM" },
        { hour: 16, label: "4", timeRange: "4 to 5 PM" },
        { hour: 17, label: "5", timeRange: "5 to 6 PM" },
      ];
    },
    selectedHourTimeRange() {
      if (this.tokenFlowHour === "all") return "All Hours";
      const found = this.tokenFlowHourOptions.find((s) => s.hour === Number(this.tokenFlowHour));
      return found ? found.timeRange : `${this.tokenFlowHour}:00`;
    },
    selectedHourTokens() {
      if (this.tokenFlowHour === "all") return [];
      const targetHour = Number(this.tokenFlowHour);
      return this.filteredTokenFlowTokens.filter((t) => {
        const h = this.getTokenHour(t);
        return h !== null && h === targetHour;
      });
    },
    tokenFlowStats() {
      let list = this.filteredTokenFlowTokens;

      if (this.tokenFlowHour !== "all" && this.tokenFlowHour !== null && this.tokenFlowHour !== undefined) {
        const targetHour = Number(this.tokenFlowHour);
        list = list.filter((t) => {
          const h = this.getTokenHour(t);
          return h !== null && h === targetHour;
        });
      }

      const issued = list.length;
      let served = 0;
      let skipped = 0;
      let waiting = 0;

      list.forEach((t) => {
        const st = String(t.status || "").toLowerCase();
        if (st === "completed" || st === "done") {
          served++;
        } else if (
          st === "skipped" ||
          t.is_skipped === true ||
          t.is_skipped === 1 ||
          String(t.is_skipped || "").toLowerCase() === "true" ||
          (Array.isArray(this.localSkippedTokenIds) && this.localSkippedTokenIds.includes(String(t.id)))
        ) {
          skipped++;
        } else if (["pending", "waiting", "calling", "serving", "processing"].includes(st)) {
          waiting++;
        }
      });

      return { issued, served, skipped, waiting };
    },
    tokenFlowHourlyData() {
      const slots = this.tokenFlowHourOptions;
      const list = this.filteredTokenFlowTokens;
      const hourCounts = {};
      slots.forEach((s) => { hourCounts[s.hour] = 0; });

      list.forEach((t) => {
        const h = this.getTokenHour(t);
        if (h !== null && !isNaN(h)) {
          const mappedHour = h < 8 ? 8 : (h > 17 ? 17 : h);
          if (hourCounts[mappedHour] !== undefined) {
            hourCounts[mappedHour]++;
          }
        }
      });

      const counts = slots.map((s) => hourCounts[s.hour]);
      const maxCount = Math.max(0, ...counts);

      return slots.map((slot) => {
        const count = hourCounts[slot.hour] || 0;
        let heightPct = 0;
        if (maxCount > 0 && count > 0) {
          heightPct = Math.max(12, Math.round((count / maxCount) * 100));
        } else if (count === 0) {
          heightPct = 4;
        }
        return {
          ...slot,
          count,
          heightPct,
          isPeak: maxCount > 0 && count === maxCount,
          isSelected: this.tokenFlowHour !== "all" && Number(this.tokenFlowHour) === slot.hour,
        };
      });
    },
    tokenFlowBusiestTime() {
      const data = this.tokenFlowHourlyData;
      if (!data || data.length === 0) return "--";
      let peak = data[0];
      data.forEach((item) => {
        if (item.count > peak.count) peak = item;
      });
      if (peak.count === 0) return "No tokens in hours";
      return peak.timeRange;
    },
  },
  watch: {
    selectedDepartmentName(newVal) {
      if (newVal) {
        this.tokenFlowDepartment = newVal;
      } else {
        this.tokenFlowDepartment = "all";
      }
      this.tokenFlowDoctorId = "all";
      this.tokenFlowHour = "all";
    },
    tokenFlowDepartment() {
      this.tokenFlowHour = "all";
    },
    tokenFlowDoctorId() {
      this.tokenFlowHour = "all";
    },
    tokenFlowDate() {
      this.tokenFlowHour = "all";
    },
  },
  methods: {
    isOtherTenant(rec) {
      if (!rec) return false;
      const myTenantId = this.currentTenantId;
      if (!myTenantId) return false;
      const recTenantId =
        rec.tenant_id ??
        rec.tenantId ??
        rec.TenantId ??
        rec.tenant?.id ??
        rec.Tenant?.id ??
        (rec.user && (rec.user.tenant_id ?? rec.user.tenantId)) ??
        (rec.raw && (rec.raw.tenant_id ?? rec.raw.tenantId)) ??
        null;
      if (recTenantId === null || recTenantId === undefined) return false;
      const s = String(recTenantId).trim();
      if (!s || s === "null" || s === "undefined" || s === "0") return false;
      return s !== String(myTenantId);
    },

    /* ------------------------- Admin Dashboard Helpers ------------------------- */
    getDoctorQueueCount(doc) {
      if (!doc) return 0;
      const dept = lower(doc.department || doc.speciality || "");
      const room = lower(doc.room || doc.counter_name || "");
      const matched = this.todayTokens.filter((t) => {
        if (this.isOtherTenant(t)) return false;
        const st = lower(t.status || "");
        if (!QUEUE_NEXT_STATUSES.includes(st) && !QUEUE_NOW_STATUSES.includes(st)) return false;
        const tDept = lower(t.department || t.speciality || "");
        const tCounter = lower(t.counter_name || t.counter?.name || "");
        return (dept && tDept.includes(dept)) || (room && tCounter.includes(room));
      });
      return matched.length;
    },
    getDoctorUtilization(doc) {
      if (!doc) return 0;
      const s = lower(doc.status || "");
      if (s.includes("offline")) return 0;
      const queueCount = this.getDoctorQueueCount(doc);
      if (s.includes("busy")) {
        return Math.min(100, Math.max(70, 70 + queueCount * 5));
      }
      if (s.includes("available")) {
        return queueCount > 0 ? Math.min(100, Math.max(40, 30 + queueCount * 10)) : 25;
      }
      if (s.includes("break")) return 20;
      return 0;
    },
    getAdminDoctorStatusBadgeClass(status) {
      const s = String(status || "").toLowerCase();
      if (s.includes("available")) return "badge-status-available";
      if (s.includes("busy")) return "badge-status-busy";
      if (s.includes("break")) return "badge-status-break";
      if (s.includes("offline")) return "badge-status-offline";
      return "badge-status-available";
    },
    isDoctorBreak(docOrStatus) {
      if (!docOrStatus) return false;
      if (typeof docOrStatus === "string") {
        return docOrStatus.toLowerCase().includes("break");
      }
      return Boolean(
        docOrStatus.is_on_break ||
        String(docOrStatus.status || docOrStatus.doctor_status || docOrStatus.availability_status).toLowerCase().includes("break")
      );
    },
    isTokenDoctorOnBreak(tok) {
      if (!tok) return false;
      if (tok.counter_on_break || tok.is_on_break) return true;
      const counterId = tok.counter_id || tok.counter?.id;
      if (counterId && Array.isArray(this.counters)) {
        const matchedCounter = this.counters.find((c) => String(c.id) === String(counterId));
        if (
          matchedCounter &&
          (matchedCounter.is_on_break ||
            String(matchedCounter.availability_status || matchedCounter.status).toLowerCase().includes("break"))
        ) {
          return true;
        }
      }
      const docName = (tok.token_attended || tok.doctor_name || "").toLowerCase().replace(/^dr\.?\s*/i, "").trim();
      const allDocs = this.allAdminDoctors || this.doctors || [];
      if (docName && allDocs.length > 0) {
        const matchedDoc = allDocs.find((d) => {
          const name = (d.doctor_name || d.name || "").toLowerCase().replace(/^dr\.?\s*/i, "").trim();
          return name && (name.includes(docName) || docName.includes(name));
        });
        if (
          matchedDoc &&
          (matchedDoc.is_on_break ||
            String(matchedDoc.status || matchedDoc.doctor_status || matchedDoc.availability_status).toLowerCase().includes("break"))
        ) {
          return true;
        }
      }
      return false;
    },
    getTokenBreakResumesAt(tok) {
      if (!tok) return "14:00";
      if (tok.break_resumes_at) return tok.break_resumes_at;
      if (tok.break_end) return tok.break_end;
      const counterId = tok.counter_id || tok.counter?.id;
      if (counterId && Array.isArray(this.counters)) {
        const matchedCounter = this.counters.find((c) => String(c.id) === String(counterId));
        if (matchedCounter && (matchedCounter.break_resumes_at || matchedCounter.break_end)) {
          return matchedCounter.break_resumes_at || matchedCounter.break_end;
        }
      }
      const docName = (tok.token_attended || tok.doctor_name || "").toLowerCase().replace(/^dr\.?\s*/i, "").trim();
      const allDocs = this.allAdminDoctors || this.doctors || [];
      if (docName && allDocs.length > 0) {
        const matchedDoc = allDocs.find((d) => {
          const name = (d.doctor_name || d.name || "").toLowerCase().replace(/^dr\.?\s*/i, "").trim();
          return name && (name.includes(docName) || docName.includes(name));
        });
        if (matchedDoc && (matchedDoc.break_resumes_at || matchedDoc.break_end)) {
          return matchedDoc.break_resumes_at || matchedDoc.break_end;
        }
      }
      return "14:00";
    },
    getDoctorCounterName(item) {
      if (!item) return "--";
      if (item.counter_name && item.counter_name !== "--" && item.counter_name !== "No room") return item.counter_name;
      if (item.room && item.room !== "--" && item.room !== "No room") return item.room;
      if (item.counter && item.counter.name) return item.counter.name;
      const docId = String(item.id || item.user_id || item.raw?.id || item.raw?.user_id || "");
      if (docId && Array.isArray(this.counters)) {
        const matched = this.counters.find((c) => {
          if (!c || this.isOtherTenant(c)) return false;
          return String(c.user_id) === docId || (c.user && String(c.user.id) === docId);
        });
        if (matched && (matched.name || matched.counter_name)) return matched.name || matched.counter_name;
      }
      return "--";
    },
    getDoctorToken(item) {
      if (!item) return "--";
      const checkCandidate = (tokNum) => {
        if (!tokNum || tokNum === "--") return null;
        if (Array.isArray(this.todayTokens)) {
          const matched = this.todayTokens.find((t) => String(t.token_number || t.token) === String(tokNum));
          if (matched && ["completed", "done", "expired", "cancelled"].includes(String(matched.status || "").toLowerCase())) {
            return null;
          }
        }
        return tokNum;
      };

      const fromCurrent = checkCandidate(item.currentToken) || checkCandidate(item.current_token);
      if (fromCurrent) return fromCurrent;

      if (Array.isArray(this.todayTokens)) {
        // 1. Serving or Calling token first
        const active = this.todayTokens.find((t) => {
          if (!SERVING_STATUSES.includes(String(t.status || "").toLowerCase())) return false;
          return this.isTokenMatchingDoctor(t, item);
        });
        if (active && (active.token_number || active.token)) return active.token_number || active.token;

        // 2. Next waiting token
        const waiting = this.todayTokens.find((t) => {
          if (!QUEUE_NEXT_STATUSES.includes(String(t.status || "").toLowerCase())) return false;
          return this.isTokenMatchingDoctor(t, item);
        });
        if (waiting && (waiting.token_number || waiting.token)) return waiting.token_number || waiting.token;
      }
      return "--";
    },
    getDeviceBadgeClass(status) {
      const s = String(status || "").toLowerCase();
      if (s.includes("offline")) return "badge-device-offline";
      if (s.includes("unassigned")) return "badge-device-unassigned";
      return "badge-device-online";
    },

    /* ------------------------- small UI helpers ------------------------- */
    getCounterNameForToken(tok) {
      if (!tok) return "No counter";
      if (tok.counter && (tok.counter.name || tok.counter.counter_name)) {
        return tok.counter.name || tok.counter.counter_name;
      }
      if (tok.counter_name && tok.counter_name !== "--" && tok.counter_name !== "No room") {
        return tok.counter_name;
      }
      const cId = tok.counter_id ? String(tok.counter_id) : "";
      if (cId && Array.isArray(this.counters)) {
        const found = this.counters.find((c) => String(c.id) === cId);
        if (found && (found.name || found.counter_name)) {
          return found.name || found.counter_name;
        }
      }
      const att = stripDr(tok.token_attended);
      if (att && att !== "no" && Array.isArray(this.counters)) {
        const found = this.counters.find((c) => {
          const cAtt = stripDr(c.attendant_name || c.user?.name);
          return cAtt && (cAtt.includes(att) || att.includes(cAtt));
        });
        if (found && (found.name || found.counter_name)) {
          return found.name || found.counter_name;
        }
      }
      return "No counter";
    },
    tokenMatchesDate(tok, targetDateStr) {
      if (!tok || !targetDateStr) return false;
      const raw = tok.date || tok.booking_date || tok.bookingDate || tok.token_date || tok.createdAt || tok.created_at;
      if (!raw) return true;
      const s = String(raw).trim();
      const datePart = s.split("T")[0].split(" ")[0].trim();
      if (datePart === targetDateStr) return true;

      if (s.includes("-")) {
        const parts = s.split("-");
        if (parts.length === 3 && parts[2].length === 4) {
          const reordered = `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
          if (reordered === targetDateStr) return true;
        }
      }
      return false;
    },
    toggleDepartment(deptName) {
      if (this.selectedDepartmentName === deptName) {
        this.selectedDepartmentName = null;
        this.tokenFlowDepartment = "all";
      } else {
        this.selectedDepartmentName = deptName;
        this.tokenFlowDepartment = deptName;
      }
      this.tokenFlowDoctorId = "all";
    },
    selectDepartment(deptName) {
      this.selectedDepartmentName = deptName;
      this.tokenFlowDepartment = deptName || "all";
      this.tokenFlowDoctorId = "all";
    },
    onTokenFlowDeptChange() {
      this.tokenFlowDoctorId = "all";
      this.tokenFlowHour = "all";
      if (this.tokenFlowDepartment !== "all") {
        this.selectedDepartmentName = this.tokenFlowDepartment;
      }
    },
    extractDateString(val) {
      if (!val) return "";
      if (val instanceof Date) {
        if (isNaN(val.getTime())) return "";
        const y = val.getFullYear();
        const m = String(val.getMonth() + 1).padStart(2, "0");
        const d = String(val.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
      }
      if (typeof val === "number" || /^\d{10,13}$/.test(String(val).trim())) {
        const num = typeof val === "number" ? val : Number(val);
        const d = new Date(num > 1e11 ? num : num * 1000);
        if (!isNaN(d.getTime())) {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${y}-${m}-${day}`;
        }
      }
      const s = String(val).trim();
      const isoMatch = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
      if (isoMatch) {
        return `${isoMatch[1]}-${isoMatch[2].padStart(2, "0")}-${isoMatch[3].padStart(2, "0")}`;
      }
      const ddmmyyyy = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
      if (ddmmyyyy) {
        return `${ddmmyyyy[3]}-${ddmmyyyy[2].padStart(2, "0")}-${ddmmyyyy[1].padStart(2, "0")}`;
      }
      try {
        const d = new Date(s);
        if (!isNaN(d.getTime())) {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${y}-${m}-${day}`;
        }
      } catch (e) {
        // ignore
      }
      return "";
    },
    extractTokenBookingDate(tok) {
      if (!tok) return "";
      // 1. Explicit booking date
      const bookingRaw = tok.booking_date || tok.bookingDate;
      if (bookingRaw) {
        const d = this.extractDateString(bookingRaw);
        if (d) return d;
      }
      // 2. Token creation / date field
      const raw = tok.date || tok.token_date || tok.createdAt || tok.created_at;
      if (raw) {
        const d = this.extractDateString(raw);
        if (d) return d;
      }
      return "";
    },
    tokenMatchesBookingDate(tok, targetDate) {
      if (!tok) return false;
      if (!targetDate || targetDate === "all") return true;

      // 1. Strict match against token's actual booking date or creation date
      const tokDate = this.extractTokenBookingDate(tok);
      if (tokDate) {
        return tokDate === targetDate;
      }

      // 2. Fall back to tokenMatchesDate helper
      if (this.tokenMatchesDate(tok, targetDate)) {
        return true;
      }

      // 3. Only if token has no date information at all and target is today, allow if in active tokens
      if (targetDate === this.todayDateIso) {
        const hasAnyDate = tok.booking_date || tok.bookingDate || tok.date || tok.token_date || tok.createdAt || tok.created_at;
        if (!hasAnyDate) {
          return Array.isArray(this.todayTokens) && this.todayTokens.some((x) => String(x.id) === String(tok.id));
        }
      }

      return false;
    },
    async onTokenFlowDateFilterChange() {
      this.tokenFlowHour = "all";
      const targetDate = this.resolvedTokenFlowDate;
      if (this.tokenFlowDateFilter === "today") {
        await this.loadTodayTokens();
      } else if (targetDate && targetDate !== "all") {
        this.tokenFlowDate = targetDate;
        await this.fetchHistoricalTokens(targetDate);
      }
      this.fetchAllTokensIfNeeded();
    },
    async onTokenFlowDateChange() {
      this.tokenFlowHour = "all";
      this.showHourlyDetails = true; // Auto-open token details on date selection
      if (!this.tokenFlowDate) return;
      await this.fetchHistoricalTokens(this.tokenFlowDate);
    },
    getTokenHour(t) {
      if (!t) return null;
      const raw = t.booking_time || t.bookingTime || t.time || t.token_time || t.time_slot || t.createdAt || t.created_at || t.date;
      if (!raw) return null;
      const s = String(raw).trim();
      // Check 12-hour AM/PM e.g. "02:30 PM", "9:00 AM", "1:15pm"
      const ampmMatch = s.match(/(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm)/i);
      if (ampmMatch) {
        let h = parseInt(ampmMatch[1], 10);
        const isPm = ampmMatch[3].toLowerCase() === "pm";
        if (isPm && h < 12) h += 12;
        if (!isPm && h === 12) h = 0;
        return h;
      }
      // Check 24-hour time string e.g. "14:30" or "09:15"
      const timeMatch = s.match(/^(\d{1,2}):(\d{2})/);
      if (timeMatch) {
        const h = parseInt(timeMatch[1], 10);
        if (!isNaN(h) && h >= 0 && h <= 23) return h;
      }
      // Check ISO / Date parseable
      try {
        const d = new Date(s);
        if (!isNaN(d.getTime())) return d.getHours();
      } catch (e) {
        // ignore
      }
      return null;
    },
    getHourlyTokenCount(hour) {
      const list = this.filteredTokenFlowTokens;
      const targetHour = Number(hour);
      return list.filter((t) => {
        const h = this.getTokenHour(t);
        return h !== null && h === targetHour;
      }).length;
    },
    toggleTokenFlowHour(hour) {
      if (String(this.tokenFlowHour) === String(hour)) {
        this.tokenFlowHour = "all";
      } else {
        this.tokenFlowHour = hour;
      }
    },
    getTokenStatusBadgeClass(status) {
      const s = String(status || "").toLowerCase();
      if (s === "completed" || s === "done") return "bg-success-soft text-success";
      if (["pending", "waiting", "calling", "serving", "processing"].includes(s)) return "bg-warning-soft text-warning";
      if (s === "skipped") return "bg-secondary-soft text-secondary";
      if (s === "rejected") return "bg-danger-soft text-danger";
      return "bg-light text-secondary";
    },
    getHourlyTokenBorderColor(status) {
      const s = String(status || "").toLowerCase();
      if (s === "completed" || s === "done") return "#10b981";
      if (s === "serving" || s === "calling") return "#0284c7";
      if (s === "pending" || s === "waiting") return "#f59e0b";
      if (s === "skipped") return "#64748b";
      if (s === "rejected") return "#ef4444";
      return "#94a3b8";
    },
    formatTime(val) {
      if (!val) return "--";
      try {
        const d = new Date(val);
        if (isNaN(d.getTime())) return String(val);
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      } catch (e) {
        return String(val);
      }
    },
    async fetchHistoricalTokens(dateStr) {
      if (!dateStr) return;
      this.isFetchingHistoricalTokens = true;
      try {
        const token = getAuthToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const base = getApiBase();
        const apiUrl = base.endsWith("/api") ? base : `${base}/api`;

        const url = `${apiUrl}/v1/admin/tokens/list?date=${dateStr}&booking_date=${dateStr}&all=true`;
        const res = await axios.get(url, { headers });
        let items = extractItems(res);

        if (Array.isArray(items) && items.length > 0) {
          const dateMatched = items.filter((t) => this.tokenMatchesDate(t, dateStr));
          if (dateMatched.length > 0) {
            items = dateMatched;
          }
        }
        this.historicalTokensCache = { ...this.historicalTokensCache, [dateStr]: items };
      } catch (err) {
        console.warn("Could not fetch historical tokens:", dateStr, err);
        this.historicalTokensCache = { ...this.historicalTokensCache, [dateStr]: [] };
      } finally {
        this.isFetchingHistoricalTokens = false;
      }
    },
    getInitials(name) {
      if (!name) return "DR";
      const clean = stripDr(name).trim();
      const parts = clean.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return clean.slice(0, 2).toUpperCase() || "DR";
    },
    selectDoctorForTokenFlow(doc) {
      if (!doc) return;
      const id = doc.id || doc.user_id || doc.raw?.id || doc.raw?.user_id;
      if (id) {
        this.selectedDoctorForTokens = doc;
        this.tokenFlowDoctorId = id;
        this.tokenFlowDateFilter = "today";
        this.tokenFlowHour = "all";
        this.tokenFlowDepartment = "all";
        this.showHourlyDetails = true;
        this.loadTodayTokens();
        this.fetchAllTokensIfNeeded();
        const targetCard = document.querySelector(".token-flow-card");
        if (targetCard && targetCard.scrollIntoView) {
          targetCard.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }
    },
    clearSelectedDoctor() {
      this.tokenFlowDoctorId = "all";
      this.tokenFlowHour = "all";
      this.selectedDoctorForTokens = null;
    },
    async fetchAllTokensIfNeeded() {
      try {
        const token = getAuthToken();
        if (!token) return;
        const base = getApiBase();
        const apiUrl = base.endsWith("/api") ? base : `${base}/api`;
        const res = await axios.get(`${apiUrl}/v1/admin/tokens/list?all=true`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const items = extractItems(res);
        if (Array.isArray(items) && items.length > 0) {
          this.allTokensCache = items.filter((t) => !this.isOtherTenant(t));
        }
      } catch (e) {
        console.debug("fetchAllTokens notice:", e.message);
      }
    },
    isTokenMatchingDoctor(t, doc) {
      if (!t || !doc || this.isOtherTenant(t)) return false;
      const docId = String(doc.id || doc.user_id || doc.raw?.id || doc.raw?.user_id || "").trim();
      const rawDocId = String(doc.raw?.id || doc.raw?.user_id || "").trim();
      const tUid = String(t.user_id || "").trim();
      const tDid = String(t.doctor_id || t.token_user_id || "").trim();
      const tUserObjId = String(t.user?.id || "").trim();

      if (docId && (tUid === docId || tDid === docId || tUserObjId === docId)) return true;
      if (rawDocId && (tUid === rawDocId || tDid === rawDocId || tUserObjId === rawDocId)) return true;

      const docCleanName = stripDr(doc.doctor_name || doc.name || doc.raw?.name || "").toLowerCase().trim();
      if (docCleanName) {
        const docNameOnTok = stripDr(t.doctor_name || t.doctor || t.attendant_name || "").toLowerCase().trim();
        if (docNameOnTok && (docNameOnTok === docCleanName || docNameOnTok.includes(docCleanName) || docCleanName.includes(docNameOnTok))) return true;

        const tUserName = stripDr(t.user?.name || "").toLowerCase().trim();
        if (tUserName && (tUserName === docCleanName || tUserName.includes(docCleanName) || docCleanName.includes(tUserName))) return true;

        const tAtt = stripDr(t.token_attended || "").toLowerCase().trim();
        if (tAtt && tAtt !== "no" && (tAtt === docCleanName || tAtt.includes(docCleanName) || docCleanName.includes(tAtt))) return true;
      }

      // Check doctor's current token
      const docTokenNum = String(doc.currentToken || doc.current_token || doc.token_number || "").trim();
      const tNum = String(t.token_number || t.token || "").trim();
      if (docTokenNum && docTokenNum !== "--") {
        if (tNum && (tNum === docTokenNum || tNum.toLowerCase() === docTokenNum.toLowerCase())) {
          return true;
        }
      }

      // Check current token prefix match (e.g. "XRC-0002" -> "XRC-")
      if (docTokenNum && docTokenNum !== "--" && docTokenNum.includes("-")) {
        const docPrefix = docTokenNum.split("-")[0].trim().toUpperCase();
        if (docPrefix && tNum.toUpperCase().startsWith(`${docPrefix}-`)) {
          return true;
        }
      }

      const counterId = String(doc.counter_id || doc.raw?.counter_id || doc.raw?.counter?.id || "").trim();
      const tCid = String(t.counter_id !== undefined && t.counter_id !== null ? t.counter_id : (t.counter ? t.counter.id : "")).trim();
      if (counterId && tCid && counterId === tCid) return true;

      const docCounter = doc.room && doc.room !== "No room" && doc.room !== "--" ? lower(doc.room).replace(/^room\s*/i, "").trim() : "";
      if (docCounter) {
        const tRoom = lower(t.counter?.name || t.counter_name || t.room || "").replace(/^room\s*/i, "").trim();
        if (tRoom && (tRoom === docCounter || tRoom.includes(docCounter) || docCounter.includes(tRoom))) return true;
      }

      const docCname = lower(doc.counter_name || (doc.counter ? doc.counter.name : "") || "").trim();
      if (docCname && docCname !== "--" && docCname !== "no counter") {
        const tCname = lower(t.counter_name || (t.counter ? t.counter.name : "") || "").trim();
        if (tCname && (tCname === docCname || tCname.includes(docCname) || docCname.includes(tCname))) return true;
      }

      // Check resolved counter name from helper against token's resolved counter name
      const resolvedCounterName = lower(this.getDoctorCounterName(doc.raw || doc) || "").trim();
      if (resolvedCounterName && resolvedCounterName !== "--" && resolvedCounterName !== "no counter") {
        const tCname = lower(t.counter_name || (t.counter ? t.counter.name : "") || "").trim();
        if (tCname && (tCname === resolvedCounterName || tCname.includes(resolvedCounterName) || resolvedCounterName.includes(tCname))) return true;

        const tCounterResolved = lower(this.getCounterNameForToken(t) || "").trim();
        if (tCounterResolved && tCounterResolved !== "no counter" && (tCounterResolved === resolvedCounterName || tCounterResolved.includes(resolvedCounterName) || resolvedCounterName.includes(tCounterResolved))) {
          return true;
        }
      }

      // Check counters table if doctor is assigned to a counter where this token belongs
      if (Array.isArray(this.counters)) {
        const docCounters = this.counters.filter((c) => {
          if (!c || this.isOtherTenant(c)) return false;
          const cUid = String(c.user_id || c.user?.id || c.doctor_id || "");
          const cName = lower(c.name || c.counter_name || "").trim();
          return (
            (docId && cUid === docId) ||
            (rawDocId && cUid === rawDocId) ||
            (resolvedCounterName && cName && (cName === resolvedCounterName || cName.includes(resolvedCounterName) || resolvedCounterName.includes(cName))) ||
            (docCname && cName && (cName === docCname || cName.includes(docCname) || docCname.includes(cName)))
          );
        });

        const tCounterResolved = lower(this.getCounterNameForToken(t) || t.counter_name || (t.counter ? t.counter.name : "") || "").trim();
        for (const c of docCounters) {
          const cId = String(c.id || "");
          const cName = lower(c.name || c.counter_name || "").trim();
          if (cId && tCid && cId === tCid) return true;
          if (cName && tCounterResolved && tCounterResolved !== "no counter" && (tCounterResolved === cName || tCounterResolved.includes(cName) || cName.includes(tCounterResolved))) return true;
          const cPrefix = String(c.prefix || c.service?.prefix || "").trim().toUpperCase();
          if (cPrefix && tNum.toUpperCase().startsWith(`${cPrefix}-`)) return true;
        }
      }

      return false;
    },
    isTokenMatchingDepartment(t, targetDepartmentName) {
      if (!t || !targetDepartmentName || this.isOtherTenant(t)) return false;
      const target = String(targetDepartmentName).trim().toLowerCase();
      if (!target || target === "all" || target === "all departments") return true;

      // If hospital only has 1 department, all tenant tokens belong to it
      if ((this.departmentSummaryList || []).length <= 1) return true;

      const tDept = String(t.department || t.speciality || t.branch_name || t.service?.branch_name || "").trim().toLowerCase();
      if (tDept && (tDept === target || tDept.includes(target) || target.includes(tDept))) return true;

      // Check counter branch
      const tCid = String(t.counter_id !== undefined && t.counter_id !== null ? t.counter_id : (t.counter ? t.counter.id : "")).trim();
      const tCname = lower(t.counter_name || (t.counter ? t.counter.name : "")).trim();
      if (Array.isArray(this.counters)) {
        const c = this.counters.find((cnt) => (tCid && String(cnt.id) === tCid) || (tCname && lower(cnt.name).trim() === tCname));
        if (c) {
          const cDept = String(c.branch?.branch_name || c.branch_name || c.speciality || c.department || "").trim().toLowerCase();
          if (cDept && (cDept === target || cDept.includes(target) || target.includes(cDept))) return true;
        }
      }

      // Check if any doctor in this department matches this token
      const deptDocs = (this.allAdminDoctors || []).filter((d) => this.isDoctorInDepartment(d.raw || d, target));
      if (deptDocs.some((d) => this.isTokenMatchingDoctor(t, d))) return true;

      return false;
    },
    getBranchDoctorIds(branch) {
      if (!branch) return [];
      let ids = [];
      if (Array.isArray(branch.doctors) && branch.doctors.length > 0) {
        return branch.doctors.map((d) => String(d.id || d.user_id || d));
      }
      if (Array.isArray(branch.doctor_ids)) {
        ids = branch.doctor_ids.map(String);
      } else if (typeof branch.doctor_ids === "string") {
        try {
          const p = JSON.parse(branch.doctor_ids);
          if (Array.isArray(p)) ids = p.map(String);
          else ids = branch.doctor_ids.split(",").map((s) => s.trim());
        } catch (e) {
          ids = branch.doctor_ids.split(",").map((s) => s.trim());
        }
      }
      if (branch.user_id) {
        const uid = String(branch.user_id);
        if (!ids.includes(uid)) ids.push(uid);
      }
      if (branch.doctor_names && typeof branch.doctor_names === "string" && Array.isArray(this.doctors)) {
        const rawList = branch.doctor_names.split(",").map((s) => s.trim().toLowerCase().replace(/^dr\.?\s*/i, ""));
        this.doctors.forEach((d) => {
          const dName = String(d.doctor_name || d.name || "").trim().toLowerCase().replace(/^dr\.?\s*/i, "");
          if (dName && rawList.some((n) => n && (dName.includes(n) || n.includes(dName)))) {
            const did = String(d.id || d.user_id);
            if (did && !ids.includes(did)) ids.push(did);
          }
        });
      }
      if (branch.contact_person && typeof branch.contact_person === "string" && Array.isArray(this.doctors)) {
        const cp = branch.contact_person.trim().toLowerCase().replace(/^dr\.?\s*/i, "");
        this.doctors.forEach((d) => {
          const dName = String(d.doctor_name || d.name || "").trim().toLowerCase().replace(/^dr\.?\s*/i, "");
          if (dName && cp && (dName.includes(cp) || cp.includes(dName))) {
            const did = String(d.id || d.user_id);
            if (did && !ids.includes(did)) ids.push(did);
          }
        });
      }
      return ids.filter(Boolean);
    },
    isDoctorInDepartment(doc, targetDepartmentName) {
      if (!doc || !targetDepartmentName) return false;
      const target = String(targetDepartmentName).trim().toLowerCase();
      if (!target) return false;

      // Find the branch corresponding to targetDepartmentName
      const matchedBranch = (this.branches || []).find((b) => {
        if (!b || this.isOtherTenant(b)) return false;
        const bName = String(b.branch_name || b.department_name || b.name || b.title || "").trim().toLowerCase();
        return bName === target || bName.includes(target) || target.includes(bName);
      });

      const branchId = matchedBranch ? String(matchedBranch.id) : null;
      const docId = String(doc.id || doc.user_id || doc.raw?.id || doc.raw?.user_id || "").trim();

      // 0. Primary: Check if doctor is explicitly assigned to this department
      if (matchedBranch && docId) {
        const assignedDoctorIds = this.getBranchDoctorIds(matchedBranch);
        if (assignedDoctorIds.includes(docId)) return true;
      }

      // Check if doctor is explicitly assigned to ANOTHER department. If so, do not cross-match!
      if (docId) {
        const otherBranchWithDoctor = (this.branches || []).find((b) => {
          if (!b || this.isOtherTenant(b)) return false;
          const bName = String(b.branch_name || b.department_name || b.name || b.title || "").trim().toLowerCase();
          if (bName === target) return false;
          return this.getBranchDoctorIds(b).includes(docId);
        });
        if (otherBranchWithDoctor) return false;
      }

      // 1. Direct branch assignment: doctor assigned as department in-charge/contact in branches table
      const branchUserId = matchedBranch && matchedBranch.user_id ? String(matchedBranch.user_id) : null;
      if (branchUserId && docId && branchUserId === docId) return true;

      // 2. Doctor's branch_id or dept_id matches the department's branch id
      const docBranchId = String(doc.branch_id || doc.dept_id || doc.raw?.branch_id || doc.raw?.dept_id || doc.raw?.branch?.id || doc.branch?.id || "").trim();
      if (branchId && docBranchId && branchId === docBranchId) return true;

      // 3. Doctor's raw department / speciality / branch_name text matching
      const docDept = String(doc.speciality || doc.department || doc.department_name || doc.raw?.department || doc.raw?.speciality || doc.raw?.department_name || "").trim().toLowerCase();
      if (docDept && (docDept === target || docDept.includes(target) || target.includes(docDept))) return true;

      const docBranchName = String(doc.branch_name || doc.raw?.branch_name || doc.raw?.branch?.branch_name || "").trim().toLowerCase();
      if (docBranchName && (docBranchName === target || docBranchName.includes(target) || target.includes(docBranchName))) return true;

      // 4. Counter / Room association (if doctor's counter is linked to this branch)
      const docCounterId = String(doc.counter_id || doc.raw?.counter_id || "").trim();
      if (docCounterId) {
        const matchedCounter = (this.counters || []).find((c) => {
          if (!c || this.isOtherTenant(c)) return false;
          return String(c.id) === docCounterId;
        });
        if (matchedCounter) {
          if (branchId && matchedCounter.branch_id && String(matchedCounter.branch_id) === branchId) return true;
          const cBranchName = String(matchedCounter.branch?.branch_name || matchedCounter.branch_name || matchedCounter.speciality || matchedCounter.department || "").trim().toLowerCase();
          if (cBranchName && (cBranchName === target || cBranchName.includes(target) || target.includes(cBranchName))) return true;
        }
      }

      // 5. Contact person match
      if (matchedBranch && matchedBranch.contact_person) {
        const branchContact = String(matchedBranch.contact_person).trim().toLowerCase();
        const rawDocName = String(doc.doctor_name || doc.name || doc.raw?.name || "").trim().toLowerCase();
        const cleanDocName = rawDocName.replace(/^dr\.?\s*/i, "").trim();
        if (cleanDocName && (branchContact.includes(cleanDocName) || cleanDocName.includes(branchContact))) return true;
      }

      // 5b. Branch doctor_names match
      if (matchedBranch && matchedBranch.doctor_names) {
        const bDocNames = String(matchedBranch.doctor_names).trim().toLowerCase();
        const rawDocName = String(doc.doctor_name || doc.name || doc.raw?.name || "").trim().toLowerCase();
        const cleanDocName = rawDocName.replace(/^dr\.?\s*/i, "").trim();
        if (cleanDocName && (bDocNames.includes(cleanDocName) || cleanDocName.includes(bDocNames))) return true;
      }

      // 6. If only 1 department exists in the hospital, all doctors belong to it
      if ((this.departmentSummaryList || []).length === 1) return true;

      return false;
    },
    selectDrilldown(counter) {
      this.drilldownSelectedCounter = counter;
      this.drilldownCurrentPage = 1;
    },
    resetDrilldown() {
      this.drilldownSelectedCounter = null;
      this.drilldownCurrentPage = 1;
    },
    async openEditTokenModal(t) {
      if (!t) return;
      let serviceOpts = "";
      (this.services || []).forEach((s) => {
        const sel = String(s.id) === String(t.service_id) ? "selected" : "";
        serviceOpts += `<option value="${s.id}" ${sel}>${s.name} ${s.prefix ? `(${s.prefix})` : ""}</option>`;
      });

      const currentCounterId = t.counter_id !== undefined && t.counter_id !== null ? String(t.counter_id) : (t.counter && t.counter.id ? String(t.counter.id) : "");
      let counterOpts = `<option value="">Unassigned</option>`;
      (this.counters || []).forEach((c) => {
        if (this.isOtherTenant(c)) return;
        const sel = String(c.id) === String(currentCounterId) ? "selected" : "";
        counterOpts += `<option value="${c.id}" ${sel}>${c.name}</option>`;
      });

      const currentStatus = lower(t.status || "pending").trim();

      const { value: formValues } = await Swal.fire({
        title: `Edit Patient Token: ${t.token_number || ""}`,
        width: "700px",
        html: `
          <div class="text-start">
            <div class="row g-2 mb-2">
              <div class="col-6">
                <label class="form-label text-xs fw-bold text-secondary mb-1">Service *</label>
                <select id="swal-edit-service" class="form-select">${serviceOpts}</select>
              </div>
              <div class="col-6">
                <label class="form-label text-xs fw-bold text-secondary mb-1">Assign Counter</label>
                <select id="swal-edit-counter" class="form-select">${counterOpts}</select>
              </div>
            </div>

            <div class="row g-2 mb-2">
              <div class="col-6">
                <label class="form-label text-xs fw-bold text-secondary mb-1">Patient Name *</label>
                <input id="swal-edit-name" class="form-control" value="${t.token_name || ""}" placeholder="Full Name" />
              </div>
              <div class="col-6">
                <label class="form-label text-xs fw-bold text-secondary mb-1">Phone Number</label>
                <input id="swal-edit-phone" class="form-control" value="${t.phone_number || t.phone || ""}" placeholder="Phone Number" />
              </div>
            </div>

            <div class="row g-2 mb-2">
              <div class="col-4">
                <label class="form-label text-xs fw-bold text-secondary mb-1">Age</label>
                <input id="swal-edit-age" type="number" class="form-control" value="${t.age || ""}" placeholder="Age" />
              </div>
              <div class="col-4">
                <label class="form-label text-xs fw-bold text-secondary mb-1">Gender</label>
                <select id="swal-edit-gender" class="form-select">
                  <option value="" ${!t.gender && !t.sex && !t.patient_gender ? "selected" : ""}>Select Gender</option>
                  <option value="Male" ${String(t.gender || t.sex || t.patient_gender || '').toLowerCase() === "male" ? "selected" : ""}>Male</option>
                  <option value="Female" ${String(t.gender || t.sex || t.patient_gender || '').toLowerCase() === "female" ? "selected" : ""}>Female</option>
                  <option value="Other" ${String(t.gender || t.sex || t.patient_gender || '').toLowerCase() === "other" ? "selected" : ""}>Other</option>
                </select>
              </div>
              <div class="col-4">
                <label class="form-label text-xs fw-bold text-secondary mb-1">Status</label>
                <select id="swal-edit-status" class="form-select">
                  <option value="pending" ${currentStatus === "pending" || currentStatus === "waiting" ? "selected" : ""}>Waiting</option>
                  <option value="calling" ${currentStatus === "calling" ? "selected" : ""}>Calling</option>
                  <option value="serving" ${currentStatus === "serving" || currentStatus === "processing" ? "selected" : ""}>Serving</option>
                  <option value="completed" ${currentStatus === "completed" ? "selected" : ""}>Completed</option>
                  <option value="skipped" ${currentStatus === "skipped" ? "selected" : ""}>Skipped</option>
                  <option value="rejected" ${currentStatus === "rejected" ? "selected" : ""}>Rejected</option>
                </select>
              </div>
            </div>

            <div class="row g-2 mb-2">
              <div class="col-6">
                <label class="form-label text-xs fw-bold text-secondary mb-1">Booking Type</label>
                <select id="swal-edit-booking-type" class="form-select">
                  <option value="Walk-in" ${t.booking_type === "Walk-in" ? "selected" : ""}>Walk-in</option>
                  <option value="Appointment" ${t.booking_type === "Appointment" ? "selected" : ""}>Appointment</option>
                  <option value="Emergency" ${t.booking_type === "Emergency" || t.is_emergency ? "selected" : ""}>Emergency</option>
                  <option value="VIP" ${t.booking_type === "VIP" ? "selected" : ""}>VIP</option>
                </select>
              </div>
              <div class="col-6">
                <label class="form-label text-xs fw-bold text-secondary mb-1">Token Number</label>
                <input id="swal-edit-number" class="form-control" value="${t.token_number || ""}" />
              </div>
            </div>

            <label class="form-label text-xs fw-bold text-secondary mb-1">Chief Complaint / Description</label>
            <textarea id="swal-edit-notes" class="form-control" rows="2" placeholder="Symptoms, reason for visit...">${t.description || t.notes || ""}</textarea>
          </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonColor: "#0d9488",
        confirmButtonText: '<i class="fas fa-save me-1"></i> Update Token',
        preConfirm: () => {
          const nameEl = document.getElementById("swal-edit-name");
          if (!nameEl || !nameEl.value.trim()) {
            Swal.showValidationMessage("Please enter patient name.");
            return false;
          }
          const chosenCounter = document.getElementById("swal-edit-counter")?.value;
          const chosenGender = document.getElementById("swal-edit-gender")?.value || null;
          return {
            service_id: document.getElementById("swal-edit-service")?.value || t.service_id,
            counter_id: chosenCounter ? Number(chosenCounter) : null,
            name: nameEl.value.trim(),
            token_name: nameEl.value.trim(),
            phone: document.getElementById("swal-edit-phone")?.value || "",
            phone_number: document.getElementById("swal-edit-phone")?.value || "",
            age: document.getElementById("swal-edit-age")?.value ? Number(document.getElementById("swal-edit-age").value) : null,
            gender: chosenGender,
            sex: chosenGender,
            patient_gender: chosenGender,
            status: document.getElementById("swal-edit-status")?.value || t.status,
            booking_type: document.getElementById("swal-edit-booking-type")?.value || "Walk-in",
            token_number: document.getElementById("swal-edit-number")?.value || t.token_number,
            notes: document.getElementById("swal-edit-notes")?.value || "",
            description: document.getElementById("swal-edit-notes")?.value || "",
          };
        },
      });

      if (formValues) {
        try {
          const token = getAuthToken();
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          const base = getApiBase();
          const apiUrl = base.endsWith("/api") ? base : `${base}/api`;

          // Optimistically update local token object
          Object.assign(t, {
            token_name: formValues.token_name,
            name: formValues.name,
            phone: formValues.phone,
            phone_number: formValues.phone_number,
            age: formValues.age,
            gender: formValues.gender,
            sex: formValues.gender,
            patient_gender: formValues.gender,
            status: formValues.status,
            booking_type: formValues.booking_type,
            notes: formValues.notes,
            description: formValues.description,
            token_number: formValues.token_number,
            counter_id: formValues.counter_id,
          });

          // If counter changed, call transfer-counter to register attendant and logs
          if (formValues.counter_id && String(formValues.counter_id) !== String(currentCounterId)) {
            await axios
              .put(
                `${apiUrl}/v1/admin/tokens/transfer-counter`,
                {
                  tokenId: t.id,
                  counterId: Number(formValues.counter_id),
                  status: formValues.status,
                },
                { headers }
              )
              .catch(() => {});
          }

          // Multi-endpoint update fallback
          try {
            await axios.put(`${apiUrl}/v1/admin/tokens/update/${t.id}`, formValues, { headers });
          } catch (e1) {
            try {
              await axios.post(`${apiUrl}/v1/admin/tokens/update/${t.id}`, formValues, { headers });
            } catch (e2) {
              await axios.put(`${apiUrl}/v1/admin/tokens/${t.id}`, formValues, { headers });
            }
          }

          localStorage.setItem("tokens_last_updated", String(Date.now()));
          window.dispatchEvent(new CustomEvent("tokens-updated"));
          window.dispatchEvent(new CustomEvent("token-created", { detail: formValues }));

          Swal.fire({
            title: "Updated!",
            text: `Token ${formValues.token_number} updated successfully.`,
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });

          this.tokensRequest = null;
          await this.loadTodayTokens();
          this.loadDoctorSession(true);
        } catch (err) {
          console.error("Error updating token:", err);
          Swal.fire("Error", err.response?.data?.message || "Failed to update token", "error");
        }
      }
    },
    canDragToken(t) {
      if (!t) return false;
      const s = String(t.status || "").toLowerCase();
      return !["completed", "rejected"].includes(s);
    },
    onDragStart(e, token) {
      if (!this.canDragToken(token)) {
        e.preventDefault();
        return;
      }
      this.draggedToken = token;
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(token.id));
    },
    onDragOver(targetId) {
      if (!this.draggedToken) return;
      this.dragOverColumnId = targetId;
    },
    onDragLeave() {
      this.dragOverColumnId = null;
    },
    getTokensForCounter(counter) {
      const toks = this.visibleTokens || this.todayTokens || [];
      if (!counter || counter === "unassigned") {
        return toks.filter((t) => !t.counter_id && (!t.counter || !t.counter.id));
      }
      const cId = String(counter.id || counter);
      return toks.filter((t) => {
        if (t.counter_id && String(t.counter_id) === cId) return true;
        if (t.counter && String(t.counter.id) === cId) return true;
        if (t.counter_name && counter.name && lower(t.counter_name) === lower(counter.name)) return true;
        return false;
      });
    },
    async onDropToken(target) {
      if (!this.draggedToken) return;
      const token = this.draggedToken;
      this.dragOverColumnId = null;

      const isUnassign = target === "unassigned";
      const targetCounterId = isUnassign ? null : target.id;
      const targetName = isUnassign ? "Unassigned Queue" : (target.name || "Counter");

      if (String(token.counter_id) === String(targetCounterId)) {
        this.draggedToken = null;
        return;
      }

      const result = await Swal.fire({
        title: "Reassign Patient Token?",
        html: `Move token <strong>${token.token_number}</strong> (${token.token_name || 'Walk-in Patient'}) to <strong>${targetName}</strong>?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: '<i class="fas fa-check me-1"></i> Yes, Move',
        cancelButtonText: "Cancel",
        confirmButtonColor: "#0d9488",
        cancelButtonColor: "#6c757d",
      });

      if (!result.isConfirmed) {
        this.draggedToken = null;
        return;
      }

      this.draggedToken = null;
      const newStatus = isUnassign ? "pending" : "calling";

      try {
        const authToken = getAuthToken();
        const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
        const base = getApiBase();
        const apiUrl = base.endsWith("/api") ? base : `${base}/api`;

        let updatedAttendant = "No";
        let updatedUserId = null;
        if (!isUnassign && target) {
          updatedUserId = target.user_id || (target.user ? target.user.id : null);
          updatedAttendant = target.user ? target.user.name : (target.attendant_name || target.name);
        }

        Object.assign(token, {
          counter_id: targetCounterId,
          counter: isUnassign ? null : { id: targetCounterId, name: target.name },
          counter_name: isUnassign ? null : target.name,
          user_id: updatedUserId,
          token_attended: updatedAttendant,
          status: newStatus,
        });

        const payload = {
          tokenId: token.id,
          counterId: targetCounterId,
          unassign: isUnassign,
          status: newStatus,
        };
        await axios.put(`${apiUrl}/v1/admin/tokens/transfer-counter`, payload, { headers }).catch(() => {});

        await axios
          .put(
            `${apiUrl}/v1/admin/tokens/update/${token.id}`,
            {
              counter_id: targetCounterId,
              user_id: updatedUserId,
              token_attended: updatedAttendant,
              status: newStatus,
            },
            { headers }
          )
          .catch(() => {});

        localStorage.setItem("tokens_last_updated", String(Date.now()));
        window.dispatchEvent(new CustomEvent("tokens-updated"));

        Swal.fire({
          title: "Token Reassigned!",
          text: `Token ${token.token_number} moved to ${targetName}.`,
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });

        await this.loadTodayTokens();
      } catch (err) {
        console.error("Error moving token:", err);
        Swal.fire("Error", "Failed to reassign token", "error");
      }
    },
    scrollToEmergency() {
      const el = this.$refs.emergencyPanel;
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    },
    roomLabel(item) {
      const room = item.room || item.counter_name || item.name || "01";
      return lower(room).startsWith("room") ? room : `Room ${room}`;
    },
    getDrilldownStatusBadgeClass(status) {
      const s = lower(status);
      if (s === "completed") return "bg-success text-white";
      if (["calling", "serving", "processing"].includes(s)) return "bg-primary text-white";
      if (s === "skipped") return "bg-secondary text-white";
      if (s === "rejected") return "bg-danger text-white";
      return "bg-warning-soft text-warning border-warning";
    },
    getEmergencyRoom(em) {
      if (!em) return "";
      return em.counter?.name || em.counter_name || em.room || "";
    },
    getStatusBadgeClass(status) {
      const s = lower(status).trim();
      if (s.includes("busy") || s.includes("consultation")) return "bg-danger-soft text-danger border-danger";
      if (s.includes("break")) return "bg-warning-soft text-warning border-warning";
      if (s.includes("off")) return "bg-secondary-soft text-secondary border-secondary";
      return "bg-success-soft text-success border-success";
    },
    getStatusDotClass(status) {
      const s = lower(status).trim();
      if (s.includes("busy") || s.includes("consultation")) return "dot-busy";
      if (s.includes("break")) return "dot-break";
      if (s.includes("off")) return "dot-offline";
      return "dot-available";
    },
    isStatusMatch(item, targetStatus) {
      const s = lower(item.availability_status || item.status || "Available").trim();
      const t = lower(targetStatus).trim();
      if (t === "busy") return s.includes("busy") || s.includes("consultation");
      if (t.includes("break")) return s.includes("break");
      if (t.includes("off")) return s.includes("off");
      if (t === "available") return s === "available" || (!s.includes("busy") && !s.includes("break") && !s.includes("off"));
      return s === t;
    },
    navigateToAddDoctor() {
      this.$router.push({ name: "Users" });
    },
    async openCreateTokenModal() {
      let serviceOptions = `<option value="">Select a Service</option>`;
      (this.services || []).forEach((s) => {
        serviceOptions += `<option value="${s.id}">${s.name} ${s.prefix ? `(${s.prefix})` : ''}</option>`;
      });

      let counterOptions = `<option value="">Auto-assign Counter</option>`;
      (this.counters || []).forEach((c) => {
        if (this.isOtherTenant(c)) return;
        counterOptions += `<option value="${c.id}">${c.name}</option>`;
      });

      const { value: formValues } = await Swal.fire({
        title: "Add New Patient (Token)",
        html: `
          <div class="text-start">
            <label class="form-label text-xs fw-bold text-secondary mb-1">Service *</label>
            <select id="swal-select-service" class="form-select mb-2">${serviceOptions}</select>
            
            <label class="form-label text-xs fw-bold text-secondary mb-1">Patient Name *</label>
            <input id="swal-input-name" class="form-control mb-2" placeholder="Full Name" />
            
            <div class="row g-2 mb-2">
              <div class="col-4">
                <label class="form-label text-xs fw-bold text-secondary mb-1">Age</label>
                <input id="swal-input-age" type="number" class="form-control" placeholder="Age" />
              </div>
              <div class="col-4">
                <label class="form-label text-xs fw-bold text-secondary mb-1">Gender *</label>
                <select id="swal-select-gender" class="form-select">
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div class="col-4">
                <label class="form-label text-xs fw-bold text-secondary mb-1">Phone</label>
                <input id="swal-input-phone" class="form-control" placeholder="Phone Number" />
              </div>
            </div>

            <div class="row g-2 mb-2">
              <div class="col-6">
                <label class="form-label text-xs fw-bold text-secondary mb-1">Assign Counter</label>
                <select id="swal-select-counter" class="form-select">${counterOptions}</select>
              </div>
              <div class="col-6">
                <label class="form-label text-xs fw-bold text-secondary mb-1">Booking Type</label>
                <select id="swal-select-booking-type" class="form-select">
                  <option value="Walk-in">Walk-in</option>
                  <option value="Appointment">Appointment</option>
                  <option value="Emergency">Emergency</option>
                  <option value="VIP">VIP</option>
                </select>
              </div>
            </div>

            <label class="form-label text-xs fw-bold text-secondary mb-1">Chief Complaint / Description</label>
            <textarea id="swal-input-notes" class="form-control" rows="2" placeholder="Symptoms, reason for visit..."></textarea>
          </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonColor: "#0d9488",
        confirmButtonText: '<i class="fas fa-plus me-1"></i> Generate Token',
        preConfirm: () => {
          const serviceEl = document.getElementById("swal-select-service");
          const nameEl = document.getElementById("swal-input-name");
          const genderEl = document.getElementById("swal-select-gender");
          if (!serviceEl || !serviceEl.value) {
            Swal.showValidationMessage("Please select a service.");
            return false;
          }
          if (!nameEl || !nameEl.value.trim()) {
            Swal.showValidationMessage("Please enter patient name.");
            return false;
          }
          return {
            service_id: serviceEl.value,
            name: nameEl.value.trim(),
            age: document.getElementById("swal-input-age")?.value || '',
            gender: genderEl?.value || '',
            phone: document.getElementById("swal-input-phone")?.value || '',
            counter_id: document.getElementById("swal-select-counter")?.value || '',
            booking_type: document.getElementById("swal-select-booking-type")?.value || 'Walk-in',
            notes: document.getElementById("swal-input-notes")?.value || '',
          };
        },
      });

      if (formValues) {
        try {
          const token = getAuthToken();
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          const base = getApiBase();
          const apiUrl = base.endsWith("/api") ? base : `${base}/api`;

          const u = readStoredUser();
          const payload = {
            token_name: formValues.name,
            phone: formValues.phone,
            phone_number: formValues.phone,
            age: formValues.age ? Number(formValues.age) : null,
            gender: formValues.gender || null,
            notes: formValues.notes,
            description: formValues.notes,
            service_id: formValues.service_id ? Number(formValues.service_id) : null,
            counter_id: formValues.counter_id ? Number(formValues.counter_id) : null,
            booking_type: formValues.booking_type || "Walk-in",
            is_emergency: formValues.booking_type === "Emergency",
            status: "pending",
            user_id: u.id || localStorage.getItem("id") || null,
            tenant_id: this.currentTenantId,
          };

          let createdTok = null;
          try {
            const res = await axios.post(`${apiUrl}/v1/admin/tokens/add`, payload, { headers });
            createdTok = res.data && (res.data.token || res.data.data);
          } catch (e) {
            const res = await axios.post(`${apiUrl}/v1/admin/tokens`, payload, { headers });
            createdTok = res.data && (res.data.token || res.data.data);
          }

          localStorage.setItem("tokens_last_updated", String(Date.now()));
          window.dispatchEvent(new CustomEvent("token-created", { detail: payload }));
          window.dispatchEvent(new CustomEvent("tokens-updated"));

          Swal.fire({
            title: "Token Created!",
            text: createdTok && createdTok.token_number ? `Patient token ${createdTok.token_number} generated successfully.` : "Token created successfully.",
            icon: "success",
            timer: 2500,
            showConfirmButton: false,
          });

          this.loadDoctorSession(true);
        } catch (err) {
          console.error("Error creating token:", err);
          Swal.fire("Error", err.response?.data?.message || err.response?.data?.msg || "Failed to create token", "error");
        }
      }
    },
    triggerEmergency(title, description) {
      this.activeEmergency = { title, description };
    },
    broadcastEmergency(em) {
      const room = this.getEmergencyRoom(em);
      const attended = em.token_attended && em.token_attended !== "No" ? `Attended by ${em.token_attended}` : "Doctor pending";
      this.triggerEmergency(
        `${em.token_number} - ${em.token_name || "Emergency patient"}`,
        `${em.service?.name || em.service_name || "Emergency Department"} • ${attended}${room ? ` (Room ${room})` : ""}`
      );
    },
    dismissEmergency() {
      this.activeEmergency = null;
    },
    handleTabChange(tab) {
      this.activeTab = tab;
      if (tab === "dashboard" || !tab) this.loadDoctorSession(true);
    },
    isDoctorUser(item) {
      if (!item) return false;
      if (this.isOtherTenant(item)) return false;
      if (item.is_doctor) return true;
      const u = item.user || {};
      const r = cleanRoleKey(item.role || item.role_name || item.role_slug || roleNameOfUser(u));

      const currentAdminEmail = lower(localStorage.getItem("adminEmail")).trim();
      const currentAdminName = lower(localStorage.getItem("userName")).trim();
      if (currentAdminEmail && item.email && lower(item.email) === currentAdminEmail) return false;

      const rawName = String(item.name || item.attendant_name || u.name || "").trim();
      if (currentAdminName && lower(rawName) === currentAdminName) return false;

      if (hasAny(r, NON_DOCTOR_ROLE_KEYWORDS.filter((k) => k !== "reception" && k !== "nurse")) || (r.includes("admin") && !r.includes("doctor"))) return false;
      if (hasAny(r, DOCTOR_KEYWORDS)) return true;

      const lowerName = lower(rawName);
      return lowerName.startsWith("dr.") || lowerName.startsWith("dr ") || lowerName.startsWith("doctor ");
    },

    /* ----------------------------- shifts ------------------------------- */
    findSavedShiftForDoctor(id, email, name) {
      const strId = String(id || "").trim();
      const cleanEmail = lower(email).trim();
      const cleanName = stripDr(name);
      const all = readAllShifts();

      try {
        for (const k of [strId, cleanEmail, cleanName]) {
          if (!k) continue;
          const cached = localStorage.getItem(`doctorShift_${k}`);
          if (cached) return JSON.parse(cached);
          if (all[k]) return all[k];
        }

        const found = Object.values(all).find((item) => {
          if (!item) return false;
          if (strId && [item.doctor_id, item.id, item.user_id].some((x) => String(x) === strId)) return true;
          if (cleanEmail && item.doctor_email && lower(item.doctor_email) === cleanEmail) return true;
          if (cleanName && item.doctor_name) {
            const c = stripDr(item.doctor_name);
            if (c.includes(cleanName) || cleanName.includes(c)) return true;
          }
          return false;
        });
        if (found) return found;

        const sessionStr = localStorage.getItem("doctorShiftSession");
        if (sessionStr) {
          const s = JSON.parse(sessionStr);
          if (
            (s.doctor_email && cleanEmail && lower(s.doctor_email) === cleanEmail) ||
            (s.doctor_id && strId && String(s.doctor_id) === strId) ||
            (s.doctor_name && cleanName && stripDr(s.doctor_name).includes(cleanName)) ||
            (this.isDoctor && (strId === "1" || strId === String(this.doctors[0]?.id)))
          ) {
            return s;
          }
        }
      } catch (e) {
        console.warn("Could not find saved shift:", e.message);
      }
      return null;
    },
    saveShiftRecord(keys, data) {
      const all = readAllShifts();
      keys.filter(Boolean).forEach((k) => {
        localStorage.setItem(`doctorShift_${k}`, JSON.stringify(data));
        all[k] = data;
      });
      localStorage.setItem("hospitalDoctorShifts", JSON.stringify(all));
    },
    getDoctorShift(doc) {
      if (!doc) return "09:00 - 17:00";
      if (doc.start_time && doc.end_time) return `${doc.start_time} - ${doc.end_time}`;
      if (doc.shift && doc.shift !== "09:00 - 17:00") return doc.shift;
      const saved = this.findSavedShiftForDoctor(doc.user_id || doc.id, doc.email, doc.doctor_name || doc.name);
      if (saved && (saved.start_time || saved.shift)) return shiftLabel(saved);
      if (this.isDoctor && (doc.is_current || doc.id === this.doctors[0]?.id) && this.activeDoctorSession) {
        return shiftLabel(this.activeDoctorSession);
      }
      return doc.shift || "09:00 - 17:00";
    },
    getDoctorSlot(doc) {
      if (!doc) return 30;
      if (doc.slot_duration_minutes) return Number(doc.slot_duration_minutes);
      const saved = this.findSavedShiftForDoctor(doc.user_id || doc.id, doc.email, doc.doctor_name || doc.name);
      if (saved?.slot_duration_minutes) return Number(saved.slot_duration_minutes);
      if (this.isDoctor && (doc.is_current || doc.id === this.doctors[0]?.id) && this.activeDoctorSession?.slot_duration_minutes) {
        return Number(this.activeDoctorSession.slot_duration_minutes);
      }
      return 30;
    },
    onSessionStarted() {
      this.loadDoctorSession(true);
    },

    /* -------------------------- status syncing -------------------------- */
    doctorMatches(doc, { dId, dEmail, dName }) {
      const docId = String(doc.id || doc.user_id || "").trim();
      const docEmail = lower(doc.email).trim();
      const docCleanName = stripDr(doc.doctor_name || doc.name);
      return (
        (dId && docId && dId === docId) ||
        (dEmail && docEmail && dEmail === docEmail) ||
        (dName && docCleanName && (docCleanName === dName || docCleanName.startsWith(dName) || dName.startsWith(docCleanName)))
      );
    },
    /** Single place that applies an incoming status change to the table and session banner. */
    syncDoctorStatus(detail, persistSelf = false) {
      const newStatus = detail.status || detail.availability_status;
      if (!newStatus) return;

      const ids = {
        dId: String(detail.doctor_id || detail.user_id || "").trim(),
        dEmail: lower(detail.email || detail.doctor_email).trim(),
        dName: stripDr(detail.doctor_name || detail.name),
      };
      const tokenNum = detail.current_token || detail.currentToken;
      const effectiveToken = lower(newStatus) === "busy" ? tokenNum || "--" : "--";

      if (this.doctors.length > 0) {
        if (ids.dId || ids.dEmail || ids.dName) {
          this.doctors.forEach((doc) => {
            if (!this.doctorMatches(doc, ids)) return;
            this.setDocStatus(doc, newStatus);
            if (effectiveToken !== "--") doc.currentToken = effectiveToken;
          });
        } else if (this.isDoctor && this.doctors.length === 1) {
          this.setDocStatus(this.doctors[0], newStatus);
        }

        if (persistSelf && this.isDoctor) {
          const currentUserId = String(localStorage.getItem("id") || "").trim();
          const currentUserName = stripDr(getStoredName());
          const isMe =
            (currentUserId && ids.dId === currentUserId) ||
            (ids.dName && (currentUserName === ids.dName || currentUserName.includes(ids.dName)));
          if (isMe) {
            this.setDocStatus(this.doctors[0], newStatus);
            if (effectiveToken !== "--") {
              this.doctors[0].currentToken = effectiveToken;
              localStorage.setItem("current_token", effectiveToken);
            }
            writeLocalStatus(newStatus);
          }
        }
      }

      if (this.activeDoctorSession) {
        const sessDocId = String(this.activeDoctorSession.doctor_id || this.activeDoctorSession.id || "").trim();
        if (!ids.dId || sessDocId === ids.dId) {
          this.activeDoctorSession.status = newStatus;
          this.activeDoctorSession.availability_status = newStatus;
        }
      }
    },
    handleDoctorStatusSynced(e) {
      if (e && e.detail) this.syncDoctorStatus(e.detail, true);
      this.fetchEmergencyTokens();
    },
    handleDoctorAvailabilityEvent(e) {
      if (e && e.detail) {
        this.syncDoctorStatus(e.detail, false);
        if (e.detail.is_shift_update || e.detail.from_shift_modal) this.onSessionStarted(e.detail);
      }
      this.fetchEmergencyTokens();
    },
    handleStorageEvent(event) {
      if (!event) return;
      if (event.key === "tokens_last_updated" || event.key === "tokensUpdated") {
        this.loadDoctorSession(true);
        this.fetchEmergencyTokens();
      }
      if (event.key === "hospitalDoctorShifts" || event.key === "doctorAvailabilityLastUpdated") {
        this.loadDoctorSession(true);
      }
      if (event.key === "doctor_status_synced") {
        try {
          this.handleDoctorStatusSynced({ detail: JSON.parse(event.newValue) });
        } catch (e) {
          console.debug("Failed to parse doctor_status_synced storage event:", e);
        }
      }
    },
    setStatus(item, newStatus) {
      if (item.status_saving || this.isStatusMatch(item, newStatus)) return;
      this.updateStatus(item, newStatus);
    },
    async updateStatus(item, newStatus) {
      this.setDocStatus(item, newStatus);
      if (lower(newStatus) !== "busy") item.currentToken = "--";
      this.markSaving(item, true);

      const userId = item.user_id || item.id || (item.user ? item.user.id : null);
      const counterId = item.counter_id || null;
      const userEmail = item.email || "";
      const doctorName = item.doctor_name || item.name || item.attendant_name;
      const effectiveToken = lower(newStatus) === "busy" ? item.currentToken || "--" : "--";

      if (this.activeDoctorSession) {
        const sessDocId = String(this.activeDoctorSession.doctor_id || this.activeDoctorSession.id || "").trim();
        if (userId && sessDocId === String(userId)) {
          this.activeDoctorSession.status = newStatus;
          this.activeDoctorSession.availability_status = newStatus;
        }
      }

      const syncDetail = {
        doctor_id: userId,
        user_id: userId,
        email: userEmail,
        doctor_email: userEmail,
        doctor_name: doctorName,
        status: newStatus,
        availability_status: newStatus,
        current_token: effectiveToken,
        currentToken: effectiveToken,
        counter_id: counterId,
      };
      this.broadcastStatus(syncDetail);

      try {
        const token = getAuthToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const base = (process.env.VUE_APP_API_BASE_URL || process.env.VUE_APP_API_URL || "/api").replace(/\/+$/, "");
        const apiUrl = base.endsWith("/api") ? base : `${base}/api`;

        const startTime = item.start_time || "20:00";
        const endTime = item.end_time || "21:00";
        const breakStart = item.break_start || "13:00";
        const breakEnd = item.break_end || "14:00";
        const payload = {
          start_time: startTime,
          end_time: endTime,
          break_start: breakStart,
          break_end: breakEnd,
          slot_duration_minutes: Number(item.slot_duration_minutes) || 30,
          shift: item.shift || `${startTime} - ${endTime}`,
          availability_status: newStatus,
          status: newStatus,
          current_token: effectiveToken,
          currentToken: effectiveToken,
          counter_id: counterId,
          doctor_id: userId,
          user_id: userId,
          email: userEmail,
          doctor_email: userEmail,
          doctor_name: doctorName,
        };

        // Each endpoint is best-effort; a failure in one should not block the others.
        const attempt = async (label, request) => {
          try { await request(); } catch (err) { console.warn(`${label} skipped:`, err.message); }
        };

        await attempt("set-timing sync", () => axios.post(`${apiUrl}/v1/admin/doctor/set-timing`, payload, { headers }));

        if (userId) {
          await attempt("User update sync", () =>
            axios.post(`${apiUrl}/v1/admin/user/update/${userId}`, {
              start_time: startTime,
              end_time: endTime,
              break_start: breakStart,
              break_end: breakEnd,
              availability_status: newStatus,
              doctor_status: newStatus,
              current_token: effectiveToken,
              status: 1,
            }, { headers })
          );
        }

        if (counterId && !this.isDoctor && item.user_id && String(item.user_id) === String(userId)) {
          await attempt("Counter update", () =>
            axios.put(`${apiUrl}/v1/admin/counter/update/${counterId}`, {
              start_time: startTime,
              end_time: endTime,
              break_start: breakStart,
              break_end: breakEnd,
              availability_status: newStatus,
              current_token: effectiveToken,
              status: newStatus === "Available" ? 1 : 0,
            }, { headers })
          );
        }

        // Doctor availability endpoint (Item 2)
        if (counterId) {
          await attempt("Doctor availability endpoint", () =>
            axios.post(`${apiUrl}/v1/admin/counter/doctor-availability`, {
              counter_id: counterId,
              availability_status: newStatus,
              doctor_status: newStatus,
              break_start: breakStart,
              break_end: breakEnd,
            }, { headers })
          );
        }

        await attempt("Doctor status endpoint", () => axios.post(`${apiUrl}/v1/admin/doctor/status`, payload, { headers }));

        this.setDocStatus(item, newStatus);

        if (this.socket && this.socket.connected) this.socket.emit("doctor_status_updated", payload);
        window.dispatchEvent(new CustomEvent("doctor-availability-changed", { detail: payload }));

        try {
          const docId = String(userId || "");
          const docEmail = lower(userEmail).trim();
          const docCleanName = stripDr(item.doctor_name || item.name);
          const all = readAllShifts();
          const previous = (docId && all[docId]) || (docEmail && all[docEmail]) || (docCleanName && all[docCleanName]) || {};
          this.saveShiftRecord([docId, docEmail, docCleanName], {
            ...previous,
            doctor_id: userId,
            doctor_name: item.doctor_name || item.name,
            doctor_email: userEmail,
            break_start: breakStart,
            break_end: breakEnd,
            status: newStatus,
            availability_status: newStatus,
            current_token: effectiveToken,
            currentToken: effectiveToken,
          });
          localStorage.setItem("doctorAvailabilityLastUpdated", String(Date.now()));
          localStorage.setItem("doctor_status_synced", JSON.stringify(syncDetail));
        } catch (storageErr) {
          console.warn("Could not sync local shift storage:", storageErr);
        }

        if (this.$toast && typeof this.$toast.success === "function") {
          this.$toast.success(`${item.doctor_name || item.name}: status updated to ${newStatus}`);
        }
      } catch (err) {
        console.error("Failed to update status:", err);
        if (this.$toast && typeof this.$toast.error === "function") {
          this.$toast.error("Failed to update status in database");
        }
      } finally {
        this.markSaving(item, false);
      }
    },

    /* ---------------------------- state setters --------------------------- */
    // Kept synchronous on purpose: writing component state straight after an
    // `await` trips ESLint's require-atomic-updates rule.
    setFetching(value) {
      this.isFetchingSession = value;
    },
    markSaving(item, value) {
      item.status_saving = value;
    },
    setCounters(counters) {
      this.counters = Array.isArray(counters) ? counters.filter((c) => !this.isOtherTenant(c)) : [];
    },
    setDoctors(doctors) {
      this.doctors = doctors;
    },
    storeRolesCache(roles) {
      if (roles.length > 0) this.cachedRoles = roles;
    },
    storeBranchesCache(branches) {
      if (branches.length > 0) this.cachedBranches = branches;
    },

    /* ---------------------------- data loading --------------------------- */
    applyTokenList(rawList) {
      const filtered = Array.isArray(rawList) ? rawList.filter((t) => !this.isOtherTenant(t)) : [];
      this.todayTokens = filtered;
      this.emergencyTokens = filtered.filter(isEmergencyToken);
    },
    /** One shared in-flight request, so socket bursts do not fire a request per event. */
    loadTodayTokens() {
      if (this.tokensRequest) return this.tokensRequest;
      const token = getAuthToken();
      if (!token) return Promise.resolve(this.todayTokens);

      const base = getApiBase();
      const apiUrl = base.endsWith("/api") ? base : `${base}/api`;
      const url = `${apiUrl}/v1/admin/tokens/list?status=today&all=true`;

      this.tokensRequest = axios
        .get(url, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          const rawList = extractItems(res);
          this.applyTokenList(rawList);
          return this.todayTokens;
        })
        .catch((e) => {
          console.warn("Could not fetch tokens:", e);
          return this.todayTokens;
        })
        .finally(() => {
          this.tokensRequest = null;
        });
      return this.tokensRequest;
    },
    fetchEmergencyTokens() {
      return this.loadTodayTokens();
    },
    async fetchDisplays() {
      try {
        const token = getAuthToken();
        if (!token) return;
        const apiUrl = getApiBase();
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const rawRole = lower(localStorage.getItem("userRole") || localStorage.getItem("role")).trim();
        const userId = localStorage.getItem("id");
        let url = `${apiUrl}/v1/admin/user-device-list`;
        if (!rawRole.includes("superadmin") && !rawRole.includes("reception") && userId) {
          url += `?user_id=${userId}`;
        }

        const [deviceRes, playlistRes] = await Promise.allSettled([
          axios.get(url, config),
          axios.get(`${apiUrl}/v1/admin/playlists`, config),
        ]);

        const rawDevices = deviceRes.status === "fulfilled" ? extractItems(deviceRes.value) : [];
        const playlistMap = {};
        if (playlistRes.status === "fulfilled") {
          extractItems(playlistRes.value).forEach((p) => {
            if (p && p.id) playlistMap[p.id] = p.playlist_name || p.name;
          });
        }

        const filteredDevices = rawDevices.filter((d) => !this.isOtherTenant(d));

        this.displays = filteredDevices.map((d, idx) => {
          const isOnline = lower(d.status) === "online";
          const playlist = d.playlist_master_id && playlistMap[d.playlist_master_id]
            ? playlistMap[d.playlist_master_id]
            : d.playlist_master_id ? `Playlist #${d.playlist_master_id}` : "General OPD Media";

          return {
            id: d.id || idx + 1,
            name: d.device_name || `Display TV #${d.id || idx + 1}`,
            location: d.device_position || (d.branch?.branch_name ? `${d.branch.branch_name}` : "Main OPD Lobby"),
            playlist,
            online: isOnline,
            status: d.status || (isOnline ? "Online" : "Offline"),
            tenant_id: d.tenant_id || d.tenantId,
          };
        });
      } catch (err) {
        console.warn("Could not fetch displays:", err);
      }
    },
    applyDynamicTenant(dynamicName) {
      const realHosp = this.resolvedHospitalName;
      if (realHosp && !lower(realHosp).includes("metrotrust")) {
        this.tenantName = realHosp;
      } else if (dynamicName && dynamicName !== "Master" && !lower(dynamicName).includes("metrotrust")) {
        this.tenantName = dynamicName;
        localStorage.setItem("tenant_name", dynamicName);
        localStorage.setItem("company_name", dynamicName);
      }
    },
    getUserContext() {
      const token = getAuthToken();
      const userName = getStoredName().trim();
      return {
        token,
        apiUrl: getApiBase(),
        config: { headers: { Authorization: `Bearer ${token}` } },
        userId: localStorage.getItem("id"),
        userName,
        userEmail: lower(getStoredEmail()).trim(),
        cleanDocName: stripDr(userName),
      };
    },
    async loadDoctorSession(silent = false) {
      if (this.isFetchingSession) return;
      this.setFetching(true);
      try {
        this.fetchDisplays();
        this.fetchEmergencyTokens();
        const ctx = this.getUserContext();
        this.activeDoctorSession = null;
        if (ctx.token) await this.loadAdminView(silent, ctx);
      } finally {
        this.setFetching(false);
      }
    },

    /* ------------------------------ admin view -------------------------- */
    async loadAdminView(silent, { apiUrl, config, userId: currentUserId, userName: currentUserName, userEmail: currentUserEmail }) {
      try {
        const safeGet = (path) =>
          axios.get(`${apiUrl}/v1/admin/${path}`, config).catch((e) => {
            console.warn(`[MedicalDashboard] Endpoint ${path} not available:`, e.message);
            return { data: [] };
          });
        const userPath = () => `user?_t=${Date.now()}`;
        const rejected = { status: "rejected" };
        let rolesRes = rejected, candidatesRes = rejected, usersRes = rejected, attendantsRes = rejected;
        let countersRes = rejected, branchesRes = rejected, rolesListRes = rejected, servicesRes = rejected;

        const canUseCache = silent && this.cachedRoles?.length > 0 && this.cachedBranches?.length > 0;
        if (canUseCache) {
          [countersRes, usersRes, servicesRes] = await Promise.allSettled([safeGet("counter"), safeGet(userPath()), safeGet("services/list")]);
          if (this.branches.length === 0 && this.cachedBranches?.length > 0) {
            this.branches = this.cachedBranches;
          }
        } else {
          [rolesRes, candidatesRes, usersRes, attendantsRes, countersRes, branchesRes, rolesListRes, servicesRes] = await Promise.allSettled([
            safeGet("roles"), safeGet("candidate"), safeGet(userPath()), safeGet("user/attendants"), safeGet("counter"), safeGet("branches"), safeGet("roles/list"), safeGet("services/list"),
          ]);
        }

        const todayList = await this.loadTodayTokens();
        const activeServingTokens = (todayList || []).filter((t) => SERVING_STATUSES.includes(lower(t.status)));

        // ---- roles (merge API, cache, localStorage; de-duplicate by id)
        let rawRoles = [...extractItems(rolesRes), ...extractItems(rolesListRes)];
        if (rawRoles.length === 0 && this.cachedRoles?.length > 0) {
          rawRoles = [...this.cachedRoles];
        } else {
          try {
            const stored = JSON.parse(localStorage.getItem("roles") || "[]");
            if (Array.isArray(stored)) rawRoles.push(...stored);
          } catch (e) {
            console.warn("Could not read cached roles:", e);
          }
        }
        const roles = [];
        const seenRoleIds = new Set();
        rawRoles.forEach((r) => {
          if (r && (r.id !== undefined || r.role_id !== undefined)) {
            const id = String(r.id !== undefined ? r.id : r.role_id);
            if (!seenRoleIds.has(id)) {
              seenRoleIds.add(id);
              roles.push(r);
            }
          }
        });
        this.storeRolesCache(roles);

        const otherTenant = (rec) => this.isOtherTenant(rec);

        const roleKeyOf = (r) => cleanRoleKey(r.name || r.role_name || r.slug || r.title);
        const doctorRoleIds = new Set();
        roles.forEach((r) => {
          const key = roleKeyOf(r);
          if (hasAny(key, ["token", "admin", "reception", "operator", "teller", "supervisor"])) return;
          if (hasAny(key, DOCTOR_KEYWORDS)) doctorRoleIds.add(String(r.id !== undefined ? r.id : r.role_id));
        });
        const findRole = (roleId) => roles.find((r) => String(r.id) === String(roleId) || String(r.role_id) === String(roleId));

        // ---- raw collections
        const rawCounters = countersRes.status === "fulfilled" ? extractItems(countersRes.value) : [];
        const counters = rawCounters.filter((c) => !this.isOtherTenant(c));
        this.setCounters(counters);

        let branches = branchesRes.status === "fulfilled" ? extractItems(branchesRes.value) : [];
        if (branches.length === 0 && this.cachedBranches?.length > 0) branches = this.cachedBranches;
        else this.storeBranchesCache(branches);
        this.branches = branches.filter((br) => !this.isOtherTenant(br));

        let services = servicesRes && servicesRes.status === "fulfilled" ? extractItems(servicesRes.value) : [];
        this.services = services.filter((s) => !this.isOtherTenant(s));

        const branchNameById = (id) => {
          const b = this.branches.find((br) => String(br.id) === String(id));
          return b ? b.branch_name || b.name || "" : "";
        };

        const rawCandidates = candidatesRes.status === "fulfilled" ? extractItems(candidatesRes.value) : [];
        const rawUsers = usersRes.status === "fulfilled" ? extractItems(usersRes.value) : [];
        const rawAttendants = attendantsRes.status === "fulfilled" ? extractItems(attendantsRes.value) : [];

        // ---- merge candidates / users / attendants into one list
        const allUsersMap = new Map();
        [...rawCandidates, ...rawUsers, ...rawAttendants].forEach((u) => {
          if (!u || !(u.id || u.email || u.name || u.f_name)) return;
          if (otherTenant(u)) return;
          const key = u.id ? `id_${u.id}` : u.email ? `email_${lower(u.email)}` : `name_${lower(u.name || u.f_name)}`;
          const prev = allUsersMap.get(key);
          if (!prev) {
            allUsersMap.set(key, { ...u });
          } else {
            allUsersMap.set(key, {
              ...u,
              ...prev,
              role_name: prev.role_name || u.role_name,
              role_id: prev.role_id || u.role_id,
              role: prev.role || u.role,
              department: prev.department || u.department || u.branch_name,
              speciality: prev.speciality || u.speciality,
            });
          }
        });
        const allUsers = Array.from(allUsersMap.values());

        const isDoctorCandidate = (u) => {
          if (!u) return false;
          const uId = String(u.id || "");
          const uEmail = lower(u.email).trim();
          const uName = lower(u.name || `${u.f_name || ""} ${u.l_name || ""}`).trim();

          if (otherTenant(u)) return false;
          if (!this.isDoctor) {
            if (currentUserId && uId === String(currentUserId)) return false;
            if (currentUserEmail && uEmail === currentUserEmail) return false;
            if (currentUserName && uName === lower(currentUserName)) return false;
          }

          let rName = "";
          if (u.role_id && roles.length > 0) {
            const matchedRole = findRole(u.role_id);
            if (matchedRole) rName = lower(matchedRole.name || matchedRole.role_name || matchedRole.title);
          }
          if (!rName) {
            rName = lower(u.role_name || u.role_slug || u.roleName || u.user_role || (u.role && (u.role.name || u.role.slug || u.role.role_name)) || u.role);
          }
          const cleanR = cleanRoleKey(rName);

          if (cleanR.includes("admin") && !cleanR.includes("doctor")) return false;
          if (u.role_id && doctorRoleIds.has(String(u.role_id))) return true;
          if (hasAny(cleanR, DOCTOR_KEYWORDS)) return true;
          if (uName.startsWith("dr.") || uName.startsWith("dr ") || uName.startsWith("doctor ")) return true;
          if (Number(u.type) === 2 || String(u.installation_type_name || "").toLowerCase().includes("medical")) return true;

          return counters.some((c) => (c.user_id && String(c.user_id) === uId) || (u.counter_id && String(c.id) === String(u.counter_id)));
        };

        const withDefaults = (start, end, slot, shift) => {
          const s = start || "09:00";
          const e = end || "17:00";
          return { start: s, end: e, slot: slot || 30, shift: shift || `${s} - ${e}` };
        };
        const toRecord = (fields) => ({
          ...fields,
          doctor_name: fields.name,
          statusBadgeClass: this.getStatusBadgeClass(fields.status),
          availability_status: fields.status,
          is_current: false,
          role: "Doctor",
          role_name: "Doctor",
          is_doctor: true,
          tenant_id: fields.tenant_id || this.currentTenantId,
        });

        const buildRecord = (cand, effectiveCounter, idx, isAttendantOnly = false) => {
          const rawName = String(cand.name || cand.attendant_name || `${cand.f_name || ""} ${cand.l_name || ""}`.trim() || `Doctor ${idx + 1}`);
          const cleanName = stripDr(rawName);
          const lowerClean = cleanName.toLowerCase();

          const servingToken = findServingToken(activeServingTokens, cand.id || cand.user_id, effectiveCounter?.id, cleanName);

          let deptName = cand.speciality || cand.department || cand.department_name || "";
          if (!deptName && cand.branch_id) deptName = branchNameById(cand.branch_id);
          if (!deptName && effectiveCounter?.branch?.branch_name) deptName = effectiveCounter.branch.branch_name;
          if (!deptName && effectiveCounter?.branch_id) deptName = branchNameById(effectiveCounter.branch_id);
          if (!deptName && effectiveCounter?.speciality) deptName = effectiveCounter.speciality;

          const candIdStr = String(cand.id || cand.user_id || "");
          if (!deptName && candIdStr) {
            const assignedBranch = (branches || []).find((br) => {
              const bDocIds = this.getBranchDoctorIds ? this.getBranchDoctorIds(br) : [];
              return bDocIds.includes(candIdStr);
            });
            if (assignedBranch) {
              deptName = assignedBranch.branch_name || assignedBranch.department_name || assignedBranch.name || "";
              if (!cand.branch_id && assignedBranch.id) cand.branch_id = assignedBranch.id;
            }
          }
          if (!deptName) {
            const b = branches.find((br) => {
              if (br.user_id && String(br.user_id) === String(cand.id)) return true;
              if (br.contact_person && lower(br.contact_person).includes(lowerClean)) return true;
              if (br.doctor_names && lower(br.doctor_names).includes(lowerClean)) return true;
              return false;
            });
            if (b) {
              deptName = b.branch_name || b.name || "";
              if (!cand.branch_id && b.id) cand.branch_id = b.id;
            }
          }
          if (!deptName) deptName = isAttendantOnly ? "General Medicine" : "General OPD";

          let roomNo = cand.room || cand.counter_name || "";
          if (!roomNo && effectiveCounter?.name) {
            const digits = effectiveCounter.name.replace(/[^0-9]/g, "");
            roomNo = digits ? digits.padStart(2, "0") : effectiveCounter.name;
          }
          if (!roomNo) roomNo = "01";

          const isOnline = (effectiveCounter && (effectiveCounter.status === "Active" || effectiveCounter.status === 1)) || cand.status == 1;
          const isDedicated = effectiveCounter && effectiveCounter.user_id && String(effectiveCounter.user_id) === String(cand.id);

          const docId = cand.id || cand.user_id || (effectiveCounter ? effectiveCounter.id : 100 + idx);
          const p = this.findSavedShiftForDoctor(docId, cand.email || cand.user?.email, rawName);

          let docStart = p?.start_time || cand.start_time || (isDedicated ? effectiveCounter.start_time : null);
          let docEnd = p?.end_time || cand.end_time || (isDedicated ? effectiveCounter.end_time : null);
          let docSlot = Number(p?.slot_duration_minutes || cand.slot_duration_minutes || (isDedicated ? effectiveCounter.slot_duration_minutes : null)) || null;
          let docShift = docStart && docEnd ? `${docStart} - ${docEnd}` : p?.shift || cand.shift || null;

          if (!docStart && docShift && docShift.includes("-")) {
            const parts = docShift.split("-").map((s) => s.trim());
            docStart = parts[0];
            docEnd = parts[1];
          }
          const sh = withDefaults(docStart, docEnd, docSlot, docShift);

          let docToken = "--";
          let docStatus = "Available";
          if (servingToken) {
            docToken = servingToken.token_number;
            docStatus = "Busy";
          } else {
            let candidateToken = "--";
            if (effectiveCounter?.current_token && effectiveCounter.current_token !== "--" && (!effectiveCounter.user_id || String(effectiveCounter.user_id) === String(cand.id))) {
              candidateToken = effectiveCounter.current_token;
            }
            if (candidateToken !== "--" && Array.isArray(todayList)) {
              const matchingInToday = todayList.find((t) => String(t.token_number || t.token) === String(candidateToken));
              if (matchingInToday && ["completed", "done", "expired", "cancelled"].includes(String(matchingInToday.status || "").toLowerCase())) {
                candidateToken = "--";
              }
            }
            docToken = candidateToken;
            const backendStatus = cand.availability_status || cand.doctor_status || p?.status || p?.availability_status || (isDedicated ? effectiveCounter.availability_status : null);
            docStatus = backendStatus || (isOnline ? "Available" : "On Break");
            if (docToken === "--" && docStatus === "Busy") {
              docStatus = "Available";
            }
          }

          if (p?.room && (!cand.room || isAttendantOnly)) roomNo = p.room;
          if (p?.department && (!cand.department || isAttendantOnly)) deptName = p.department;

          const formattedName = formatDoctorName(rawName);
          return toRecord({
            id: docId,
            counter_id: effectiveCounter ? effectiveCounter.id : cand.counter_id || null,
            counter_name: effectiveCounter ? effectiveCounter.name : cand.counter_name || null,
            user_id: cand.user_id || cand.id || (effectiveCounter ? effectiveCounter.user_id : null),
            name: formattedName,
            doctor_name: formattedName,
            attendant_name: cand.attendant_name || rawName,
            initials: getInitials(cleanName),
            email: cand.email || cand.user?.email || `doctor${docId}@hospital.com`,
            speciality: deptName,
            department: deptName,
            room: roomNo,
            currentToken: docToken,
            shift: sh.shift,
            start_time: sh.start,
            end_time: sh.end,
            slot_duration_minutes: sh.slot,
            status: docStatus,
            tenant_id: cand.tenant_id || cand.tenantId || effectiveCounter?.tenant_id || this.currentTenantId || null,
            branch_id: cand.branch_id || cand.dept_id || effectiveCounter?.branch_id || null,
            dept_id: cand.dept_id || cand.branch_id || effectiveCounter?.branch_id || null,
            raw: cand,
          });
        };

        // ---- doctors that come from user records
        const doctorList = allUsers.filter(isDoctorCandidate).map((cand, idx) => {
          const cleanName = stripDr(cand.name || `${cand.f_name || ""} ${cand.l_name || ""}`);
          const lowerClean = cleanName.toLowerCase();
          const effectiveCounter =
            counters.find((c) =>
              (c.user_id && String(c.user_id) === String(cand.id)) ||
              (cand.counter_id && String(c.id) === String(cand.counter_id)) ||
              (c.user && String(c.user.id) === String(cand.id)) ||
              (c.email && cand.email && lower(c.email) === lower(cand.email)) ||
              (c.attendant_name && cleanName && (stripDr(c.attendant_name).includes(lowerClean) || lowerClean.includes(stripDr(c.attendant_name))))
            ) || null;

          return buildRecord(cand, effectiveCounter, idx, false);
        });

        // ---- doctors that exist as counter attendants
        counters.forEach((c, cIdx) => {
          if (otherTenant(c)) return;
          const attName = String(c.attendant_name || c.user?.name || "").trim();
          if (!attName) return;

          const lowerAtt = stripDr(attName).toLowerCase();
          const alreadyIncluded = doctorList.some(
            (d) =>
              (c.user_id && String(d.id) === String(c.user_id)) ||
              (c.user_id && String(d.user_id) === String(c.user_id)) ||
              (d.counter_id && String(d.counter_id) === String(c.id)) ||
              lower(d.name).includes(lowerAtt) ||
              lowerAtt.includes(stripDr(d.name))
          );
          if (alreadyIncluded) return;

          let rName = lower(c.user?.role_name || c.user?.role_slug || (c.user?.role && (c.user.role.name || c.user.role.slug)) || c.user?.role || c.role);
          if (!rName && c.user?.role_id) {
            const matchedRole = findRole(c.user.role_id);
            if (matchedRole) rName = lower(matchedRole.name);
          }
          const cleanR = cleanRoleKey(rName);

          const linkedUser = allUsers.find((u) => c.user_id && String(u.id) === String(c.user_id));
          const lowerAttFull = lower(attName);
          const isDoc =
            (linkedUser && isDoctorCandidate(linkedUser)) ||
            hasAny(cleanR, ["doctor", "physician", "consultant", "specialist"]) ||
            (c.user?.role_id && doctorRoleIds.has(String(c.user.role_id))) ||
            lowerAttFull.startsWith("dr.") || lowerAttFull.startsWith("dr ") || lowerAttFull.startsWith("doctor ") ||
            Boolean(attName);
          if (!isDoc) return;

          if (!this.isDoctor) {
            if (currentUserEmail && c.user?.email && lower(c.user.email) === currentUserEmail) return;
            if (currentUserName && lowerAttFull === lower(currentUserName)) return;
          }

          doctorList.push(buildRecord(c.user || { ...c, name: attName }, c, cIdx, true));
        });

        this.setDoctors(doctorList);
      } catch (e) {
        console.error("Error loading doctors for tenant admin:", e);
      }
    },
  },
};
</script>

<style scoped>
/* ---------- Tokens ---------- */
.medical-dashboard {
  --md-bg: #f4f7fb;
  --md-surface: #ffffff;
  --md-line: #e8eef5;
  --md-teal: #0d9488;
  --md-text: #1e293b;
  --md-muted: #64748b;
  background: var(--md-bg);
  border-radius: 20px;
  min-height: 100%;
}

/* ---------- Colour helpers ---------- */
.text-teal { color: #0d9488 !important; }
.bg-teal { background-color: #0d9488 !important; }
.btn-teal { background-color: #0d9488 !important; border-color: #0d9488 !important; }
.bg-teal-soft { background-color: rgba(13, 148, 136, 0.12); }
.btn-outline-teal { border: 1px solid #0d9488 !important; color: #0d9488 !important; background: transparent; }
.btn-outline-teal:hover { background-color: #0d9488 !important; color: #ffffff !important; }
.bg-primary-soft { background-color: rgba(59, 130, 246, 0.12); }
.bg-warning-soft { background-color: rgba(245, 158, 11, 0.12); }
.bg-danger-soft { background-color: rgba(239, 68, 68, 0.12); }
.bg-success-soft { background-color: rgba(34, 197, 94, 0.12); }
.bg-secondary-soft { background-color: rgba(100, 116, 139, 0.15); }
.cursor-pointer { cursor: pointer; }
.min-w-0 { min-width: 0; }

/* ---------- Cards ---------- */
.md-card {
  background: var(--md-surface);
  border-radius: 16px;
  border: 1px solid var(--md-line);
  box-shadow: 0 2px 10px rgba(30, 41, 59, 0.04);
}

/* ---------- KPI ---------- */
.kpi-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tone-teal { background: #d9f5f1; color: #0d9488; }
.tone-blue { background: #dbeafe; color: #2563eb; }
.tone-amber { background: #fff1d6; color: #d97706; }
.tone-pink { background: #fde2ec; color: #db2777; }
.kpi-label { font-size: 0.8rem; color: var(--md-muted); }
.kpi-value { font-weight: 800; color: var(--md-text); line-height: 1.1; }
.kpi-total { font-size: 0.9rem; font-weight: 500; color: var(--md-muted); }
.kpi-sub { font-size: 0.75rem; font-weight: 600; }
.text-pink { color: #db2777 !important; }

/* ---------- Tables ---------- */
.md-table thead th {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--md-muted);
  background: #f8fafc;
  border-bottom: 1px solid var(--md-line);
  padding: 0.65rem 0.75rem;
  white-space: nowrap;
}
.md-table tbody td {
  padding: 0.7rem 0.75rem;
  border-bottom: 1px solid #f1f5f9;
}
.md-table tbody tr:last-child td { border-bottom: none; }
.doctor-filter { max-width: 260px; font-size: 0.85rem; }

.room-chip {
  display: inline-block;
  padding: 0.3rem 0.8rem;
  border-radius: 999px;
  background: #f1f5f9;
  color: var(--md-text);
  font-size: 0.8rem;
  font-weight: 700;
}
.token-chip {
  display: inline-block;
  min-width: 52px;
  padding: 0.3rem 0.7rem;
  border-radius: 10px;
  background: #d9f5f1;
  color: #0f766e;
  font-weight: 700;
  font-size: 0.85rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.token-chip-empty { background: #f1f5f9; color: #94a3b8; }

/* ---------- Status control ---------- */
.status-cell { min-width: 230px; }
.status-pills {
  display: inline-flex;
  gap: 4px;
  padding: 3px;
  border-radius: 999px;
  background: #f1f5f9;
}
.status-pill {
  border: none;
  background: transparent;
  color: var(--md-muted);
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.status-pill:hover:not(:disabled):not(.active) { background: #e2e8f0; }
.status-pill:focus-visible { outline: 2px solid #0d9488; outline-offset: 1px; }
.status-pill:disabled { opacity: 0.55; cursor: wait; }
.status-pill.active { color: #ffffff; }
.status-pill.pill-available.active { background: #10b981; }
.status-pill.pill-busy.active { background: #ef4444; }
.status-pill.pill-break.active { background: #f59e0b; }
.status-pill.pill-offline.active { background: #64748b; }

.status-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
.status-dot.dot-available { background-color: #10b981; box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.25); }
.status-dot.dot-busy { background-color: #ef4444; box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.25); }
.status-dot.dot-break { background-color: #f59e0b; box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.25); }
.status-dot.dot-offline { background-color: #94a3b8; box-shadow: 0 0 0 2px rgba(148, 163, 184, 0.25); }

/* ---------- Departments Section (Image 2) ---------- */
.department-card {
  position: relative;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1rem 1.25rem;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
  overflow: hidden;
  cursor: pointer;
}

.department-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
}

.department-card.department-card-active {
  border: 2px solid #0d9488 !important;
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.15), 0 4px 14px rgba(13, 148, 136, 0.1) !important;
  background-color: #fbfdfd !important;
}

.department-accent {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  border-top-left-radius: 14px;
  border-bottom-left-radius: 14px;
}

.accent-teal { background-color: #0d9488; }
.accent-amber { background-color: #d97706; }
.accent-red { background-color: #dc2626; }

.dept-progress-track {
  width: 100%;
  height: 6px;
  background-color: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
}

.dept-progress-bar {
  height: 100%;
  border-radius: 999px;
  transition: width 0.3s ease;
}

.bar-teal { background-color: #0d9488; }
.bar-amber { background-color: #d97706; }
.bar-red { background-color: #dc2626; }

.btn-outline-teal {
  color: #0d9488;
  border-color: #0d9488;
  background-color: transparent;
}

.btn-outline-teal:hover {
  color: #ffffff;
  background-color: #0d9488;
  border-color: #0d9488;
}

.emergency-list { max-height: 290px; overflow-y: auto; }
.emergency-btn { border-radius: 10px; }

.screen-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  border-radius: 12px;
  border: 1px solid var(--md-line);
}
.screen-status-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.hover-bg-light:hover { background-color: #f1f5f9; }

/* Pulse only when the person has not asked for reduced motion */
@media (prefers-reduced-motion: no-preference) {
  .animate-pulse { animation: pulse-border 2s infinite; }
}
@keyframes pulse-border {
  0% { opacity: 1; }
  50% { opacity: 0.85; }
  100% { opacity: 1; }
}

/* ---------- Counter drilldown ---------- */
.border-start-lg { border-left: 1px solid #f1f5f9; }
@media (max-width: 991.98px) {
  .border-start-lg { border-left: none; border-top: 1px solid #f1f5f9; padding-top: 1.5rem; }
}
.donut-chart-container { width: 210px; height: 210px; }
.donut-svg { width: 100%; height: 100%; transform: rotate(-90deg); border-radius: 50%; }
.donut-segment { transition: stroke-width 0.2s ease, opacity 0.2s ease; }
.donut-segment:hover { stroke-width: 18; opacity: 0.9; }
.donut-center-label {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
}
.legend-color-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; flex-shrink: 0; }

/* ========================================================
   ADMIN DASHBOARD STYLES (MATCHING USER MOCKUPS)
   ======================================================== */
.dept-accent-amber {
  border-left: 4px solid #d97706 !important;
}
.dept-accent-teal {
  border-left: 4px solid #0d9488 !important;
}
.dept-accent-red {
  border-left: 4px solid #dc2626 !important;
}

.dept-title {
  font-size: 0.96rem;
  font-weight: 700;
  color: #1e293b;
}

.dept-subtitle {
  font-size: 0.8rem;
  color: #64748b;
}

.bg-amber {
  background-color: #d97706 !important;
}
.bg-red {
  background-color: #dc2626 !important;
}

/* Doctors Filter Pills */
.doctor-filter-pills {
  background: #f1f5f9;
  border-radius: 9999px;
  padding: 3px;
  gap: 2px;
}

.btn-doctor-pill {
  border: none;
  background: transparent;
  color: #475569;
  font-size: 0.82rem;
  font-weight: 500;
  padding: 5px 14px;
  border-radius: 9999px;
  transition: all 0.2s ease;
  cursor: pointer;
}
.btn-doctor-pill:hover {
  color: #0f172a;
}
.btn-doctor-pill.active {
  background-color: #0d9488;
  color: #ffffff;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(13, 148, 136, 0.25);
}

/* Doctor Status Badges */
.badge-status-pill {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 0.78rem;
  font-weight: 600;
  text-align: center;
}
.badge-status-available {
  background: #dcfce7;
  color: #16a34a;
}
.badge-status-busy {
  background: #fef3c7;
  color: #d97706;
}
.badge-status-break {
  background: #e0e7ff;
  color: #4338ca;
}
.badge-status-offline {
  background: #f1f5f9;
  color: #64748b;
}

.badge-no-room {
  display: inline-block;
  background: #fee2e2;
  color: #dc2626;
  padding: 3px 10px;
  border-radius: 9999px;
  font-size: 0.76rem;
  font-weight: 600;
}

/* Devices Section */
.badge-device-online-count {
  background: #dcfce7;
  color: #16a34a;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 0.8rem;
  font-weight: 600;
}

.badge-device-status {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}
.badge-device-offline {
  background: #fee2e2;
  color: #dc2626;
}
.badge-device-unassigned {
  background: #fef3c7;
  color: #d97706;
}
.badge-device-online {
  background: #dcfce7;
  color: #16a34a;
}

.border-subtle {
  border: 1px solid #f1f5f9;
}
.border-bottom-subtle {
  border-bottom: 1px solid #f8fafc;
}

/* Booked and Walk-in Card Styles matching mockup */
.booked-walkin-card {
  border-radius: 16px !important;
}

.booked-walkin-progress {
  display: flex;
  width: 100%;
  height: 10px;
  background-color: #f1f5f9;
  border-radius: 9999px;
  overflow: hidden;
}

.progress-seg-booked {
  background-color: #2563eb;
  height: 100%;
  transition: width 0.3s ease;
}

.progress-seg-walkin {
  background-color: #0d9488;
  height: 100%;
  transition: width 0.3s ease;
}

.booked-walkin-row {
  border-bottom: 1px solid #f1f5f9;
}

.booking-type-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

.booking-type-progress-seg {
  height: 100%;
  transition: width 0.3s ease;
}

/* ========================================================================== */
/* Token Flow Today Card & Hourly Bar Chart (matching screenshot)             */
/* ========================================================================== */
.token-flow-card {
  border-radius: 16px !important;
}

.token-flow-timing-select {
  max-width: 110px;
  font-size: 0.75rem;
  padding: 3px 20px 3px 8px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  color: #334155;
  background-color: #f8fafc;
  font-weight: 600;
  cursor: pointer;
}
.token-flow-timing-select:focus {
  border-color: #0d9488;
  box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.15);
}

.token-flow-date-picker {
  max-width: 130px;
  font-size: 0.75rem;
  padding: 3px 8px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  color: #334155;
  background-color: #f8fafc;
}
.token-flow-date-picker:focus {
  border-color: #0d9488;
  box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.15);
}

.token-flow-select {
  font-size: 0.75rem;
  padding: 4px 8px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  color: #1e293b;
  background-color: #f8fafc;
  font-weight: 500;
}
.token-flow-select:focus {
  border-color: #0d9488;
  box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.15);
}

/* 4 Status summary pills */
.token-flow-pills-row {
  display: flex;
  gap: 8px;
}

.token-flow-pill {
  background-color: #f1f5f9;
  border-radius: 12px;
  padding: 10px 6px;
  text-align: center;
  border: 1px solid rgba(226, 232, 240, 0.8);
  transition: all 0.2s ease;
}
.token-flow-pill:hover {
  background-color: #e2e8f0;
  transform: translateY(-1px);
}

.token-flow-pill-val {
  font-size: 1.28rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
  margin-bottom: 2px;
}

.token-flow-pill-lbl {
  font-size: 0.72rem;
  font-weight: 500;
  color: #64748b;
  text-transform: capitalize;
  letter-spacing: -0.01em;
}

/* Hourly bar chart */
.token-flow-chart-wrap {
  padding: 8px 4px 4px 4px;
}

.token-flow-bars {
  height: 125px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
}

.token-flow-bar-col {
  flex: 1;
  max-width: 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  cursor: pointer;
  border-radius: 6px;
  padding: 2px;
  transition: all 0.2s ease;
}
.token-flow-bar-col:hover {
  background-color: rgba(13, 148, 136, 0.08);
}
.token-flow-bar-col:hover .token-flow-bar {
  background-color: #3b82f6 !important;
}

.token-flow-bar-col.bar-col-selected {
  background-color: rgba(37, 99, 235, 0.12) !important;
}
.token-flow-bar-col.bar-col-selected .token-flow-bar {
  background-color: #2563eb !important;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.4);
}
.token-flow-bar-col.bar-col-selected .token-flow-bar-val,
.token-flow-bar-col.bar-col-selected .token-flow-bar-hour {
  color: #1d4ed8 !important;
  font-weight: 800;
}

.token-flow-bar-val {
  font-size: 0.7rem;
  font-weight: 600;
  color: #64748b;
  line-height: 1;
  min-height: 14px;
  display: inline-block;
  transition: color 0.2s ease;
}

.token-flow-bar-track {
  height: 85px;
  width: 100%;
  max-width: 22px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background-color: transparent;
}

.token-flow-bar {
  width: 100%;
  background-color: #84aee4;
  border-radius: 5px 5px 2px 2px;
  transition: height 0.35s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s ease;
  min-height: 3px;
}
.token-flow-bar:hover {
  background-color: #5c93d9;
}

.token-flow-bar.bar-peak {
  background-color: #72a2df;
}

.token-flow-bar.bar-active {
  background-color: #2563eb !important;
}

.token-flow-bar-hour {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  line-height: 1;
  min-height: 14px;
  transition: color 0.2s ease;
}

.token-flow-pill-active {
  border-color: #0d9488 !important;
  box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.25);
  background-color: #f0fdfa !important;
}

.token-flow-footer {
  border-top: 1px solid #f1f5f9;
  padding-top: 8px;
}

.border-start-3 {
  border-left-width: 3px !important;
}

.hourly-patient-drawer {
  animation: slideDownFade 0.2s ease-out;
}
@keyframes slideDownFade {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Department doctors table interaction */
.doctor-row-hover {
  transition: background-color 0.15s ease;
}
.doctor-row-hover:hover {
  background-color: #f8fafc;
}

.table-active-row {
  background-color: #f0fdfa !important;
  border-left: 3px solid #0d9488 !important;
}

/* Drag & Drop Token Styles for Receptionist */
.cursor-grab {
  cursor: grab;
}

.draggable-row {
  cursor: grab;
  transition: all 0.2s ease;
}

.draggable-row:active {
  cursor: grabbing;
  opacity: 0.7;
}

.counter-drop-chip {
  transition: all 0.2s ease;
  user-select: none;
}

.counter-drop-chip:hover {
  background-color: #f1f5f9 !important;
  border-color: #94a3b8 !important;
  transform: translateY(-1px);
}

.counter-drop-chip.drop-active {
  background-color: #ccfbf1 !important;
  border-color: #0d9488 !important;
  color: #0f766e !important;
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(13, 148, 136, 0.25) !important;
}

.pulse-border {
  animation: pulseBorder 1s infinite alternate;
}

@keyframes pulseBorder {
  0% {
    box-shadow: 0 0 0 0 rgba(13, 148, 136, 0.4);
  }
  100% {
    box-shadow: 0 0 0 6px rgba(13, 148, 136, 0);
  }
}

.counter-board-wrapper {
  max-width: 100% !important;
  width: 100% !important;
  overflow-x: auto !important;
  overflow-y: hidden;
  box-sizing: border-box;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f8fafc;
}

.counter-board-wrapper::-webkit-scrollbar {
  height: 8px;
}

.counter-board-wrapper::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.counter-board-wrapper::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.counter-board-wrapper::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.counter-column {
  width: 280px;
  min-width: 280px;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.counter-column.drop-active {
  background-color: #f0fdfa !important;
  border-color: #0d9488 !important;
  border-style: solid !important;
  box-shadow: 0 6px 20px rgba(13, 148, 136, 0.2) !important;
  transform: translateY(-2px);
}

.token-drag-card {
  cursor: grab;
  transition: all 0.2s ease;
  user-select: none;
}

.token-drag-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08) !important;
  border-color: #cbd5e1 !important;
}

.token-drag-card:active {
  cursor: grabbing;
  opacity: 0.6;
}

/* Department Bar Chart Styles */
.dept-bar-chart-card {
  background: #ffffff;
  border-radius: 16px;
  position: relative;
}
.dept-bars-wrapper {
  height: 165px;
  position: relative;
  overflow: hidden;
  padding: 10px 20px 0 50px;
}
.dept-bar-gridline {
  position: absolute;
  left: 50px;
  right: 20px;
  border-top: 1px dashed #e2e8f0;
  pointer-events: none;
  z-index: 0;
}
.dept-bar-gridline.top-line {
  top: 24px;
  border-top: 1px solid #e2e8f0;
}
.dept-bar-gridline.mid-line {
  top: 68px;
  border-top: 1px dashed #e2e8f0;
}
.dept-bar-gridline.base-line {
  bottom: 32px;
  border-top: 1px solid #cbd5e1;
}
.gridline-label {
  position: absolute;
  left: -42px;
  top: -8px;
  width: 34px;
  text-align: right;
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
}
.dept-bar-col {
  position: relative;
  z-index: 1;
  min-width: 80px;
  max-width: 140px;
  margin: 0 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  cursor: pointer;
  transition: transform 0.2s ease;
}
.dept-bar-col:hover {
  transform: translateY(-2px);
}
.dept-bar-col-active .dept-bar-track {
  outline: 3px solid #0d9488 !important;
  outline-offset: 2px !important;
  box-shadow: 0 0 12px rgba(13, 148, 136, 0.45) !important;
}
.dept-bar-value {
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  margin-bottom: 6px;
}
.dept-bar-track {
  width: 52px;
  height: 80px;
  background-color: #f1f5f9;
  border-radius: 8px 8px 3px 3px;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
}
.dept-bar-fill {
  width: 100%;
  border-radius: 8px 8px 0 0;
  transition: height 0.4s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s ease;
}
.dept-bar-name {
  font-size: 12px;
  max-width: 120px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
  color: #0d9488;
  font-weight: 600;
  margin-top: 6px;
}
.dept-bar-col-active .dept-bar-name {
  color: #0f766e;
  font-weight: 700;
}
.bg-warning-soft {
  background-color: rgba(245, 158, 11, 0.15) !important;
}
.text-warning {
  color: #d97706 !important;
}
.font-size-11 {
  font-size: 11px !important;
}
</style>